/* What The Puzzles — digital puzzle player.
 * Identity comes from the logged-in Shopify customer (email rendered by the
 * theme into #wtp-play[data-customer-email]). No second sign-in.
 * Flow: (logged in) -> "My Puzzles" -> play view (headbreaker + timer) -> leaderboard.
 * Requires window.supabase, plus Konva + headbreaker (loaded by the section). */
(function () {
  "use strict";

  // Version marker — logged to the console and kept in the page (hidden since v87, at the user's
  // request), so a stale cached build is still identifiable: DevTools, or the tests' ".wtp-buildtag".
  var WTP_PLAY_VERSION = "v88";
  try { console.log("[wtp-play] build", WTP_PLAY_VERSION); } catch (e) {}
  function versionTag() {
    return '<span class="wtp-ver" hidden>' + WTP_PLAY_VERSION + "</span>";
  }

  var root = document.getElementById("wtp-play");
  if (!root) return;

  // ---- sound effects (self-contained; independent of gameplay) ----------------
  // Loads the 4 SFX from the theme (data-snd-* on #wtp-play), preloads them, and plays
  // on demand. Debounced to avoid accidental double/burst plays; fails silently if a
  // file is missing/blocked so audio can never break the puzzle. Autoplay unlocks on
  // the first user gesture (the first grab), which is all a puzzle needs.
  var wtpAudio = (function () {
    var els = {}, last = {};
    var gap = { pick: 40, drop: 40, correct: 120, complete: 0 };
    function add(name, url) {
      if (!url) return;
      try {
        var a = new Audio(url);
        a.preload = "auto";
        a.volume = name === "complete" ? 0.55 : 0.4;
        els[name] = a;
      } catch (e) {}
    }
    add("pick", root.dataset.sndPick);
    add("drop", root.dataset.sndDrop);
    add("correct", root.dataset.sndCorrect);
    add("complete", root.dataset.sndComplete);
    // v78: mute ("the existing mute setting still applies" — Sep 30 Scope & Plan).
    // Remembered on this device; silences every game sound, Snap Pack included.
    var muted = false;
    try { muted = localStorage.getItem("wtp_muted") === "1"; } catch (e) {}
    function setMuted(m) {
      muted = !!m;
      try { localStorage.setItem("wtp_muted", muted ? "1" : "0"); } catch (e) {}
    }
    function play(name) {
      if (muted) return;
      var a = els[name];
      if (!a) return;
      var now = Date.now();
      if (last[name] && now - last[name] < (gap[name] || 0)) return; // debounce
      last[name] = now;
      try { a.currentTime = 0; var p = a.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
    }
    // M5b Snap Pack: the equipped sound replaces the correct-snap sound (null = the
    // built-in one). A file that fails to load falls back to the built-in sound.
    var builtInCorrect = root.dataset.sndCorrect || null;
    function useSnap(url) {
      var want = url || builtInCorrect;
      if (!want || (els.correct && els.correct._src === want)) return;
      add("correct", want);
      var a = els.correct;
      if (!a) return;
      a._src = want;
      if (url && builtInCorrect) {
        a.addEventListener("error", function () {
          if (els.correct === a) { add("correct", builtInCorrect); if (els.correct) els.correct._src = builtInCorrect; }
        });
      }
    }
    // One-off playback for the catalogue's preview buttons (never the game channel).
    var pv = null;
    function preview(url) {
      if (!url) return;
      try {
        if (pv) pv.pause();
        pv = new Audio(url);
        pv.volume = 0.4;
        var p = pv.play();
        if (p && p.catch) p.catch(function () {});
      } catch (e) {}
    }
    return { play: play, useSnap: useSnap, preview: preview, setMuted: setMuted,
             isMuted: function () { return muted; } };
  })();

  var sb = null;
  // Free to play, but LOGIN REQUIRED: the player must be a logged-in Shopify
  // customer to view/play any puzzle. Identity (leaderboard + save/resume) comes
  // from the customer's email/name. On localhost the theme can't read the Shopify
  // session, so ?as=email simulates a logged-in customer for LOCAL TESTING only.
  var devHost = /^(127\.0\.0\.1|localhost)$/.test(location.hostname);
  var devAs = "";
  if (devHost) {
    var asParam = (new URLSearchParams(location.search).get("as") || "").trim().toLowerCase();
    try {
      if (asParam) localStorage.setItem("wtp_dev_as", asParam);
      devAs = asParam || localStorage.getItem("wtp_dev_as") || "";
    } catch (e) { devAs = asParam; }
  }
  var customerEmail = (root.dataset.customerEmail || "").trim().toLowerCase() || devAs || null;
  var customerName = (root.dataset.customerName || "").trim();
  if (!customerName && devAs) customerName = devAs.split("@")[0]; // dev simulation only
  var loginBase = root.dataset.loginBase || "/account/login"; // raw login URL (no return_url)

  // DB key (the `email` column) + display name.
  function getPlayerKey() { return customerEmail; }
  // Player-chosen username (from Supabase) wins; then the Shopify name; then a masked email.
  var playerUsername = null; // null = not fetched / not set yet
  function playerName() {
    return playerUsername || customerName || (customerEmail ? maskEmail(customerEmail) : "Player");
  }
  // Read the saved display name for this player (cached). Requires Supabase.
  async function fetchUsername() {
    if (!customerEmail) return null;
    if (!sb) await sbReady;
    try {
      var res = await sb.rpc("get_username", { p_email: getPlayerKey() });
      if (res && res.error) {
        if (await healStaleAuth(res.error)) {
          res = await sb.rpc("get_username", { p_email: getPlayerKey() });
        }
      }
      var name = res && res.data ? String(res.data).trim() : "";
      playerUsername = name || null;
      return playerUsername;
    } catch (e) { return playerUsername; }
  }
  // Save the chosen display name (also backfills past scores, server-side).
  // Returns { ok:true, username } on success, or { ok:false, error:'taken'|'empty' }.
  async function saveUsername(name) {
    if (!customerEmail) return { ok: false, error: "no-login" };
    if (!sb) await sbReady;
    var res = await sb.rpc("set_username", { p_email: getPlayerKey(), p_username: name });
    if (res && res.error) {
      if (await healStaleAuth(res.error)) {
        res = await sb.rpc("set_username", { p_email: getPlayerKey(), p_username: name });
      }
      if (res && res.error) throw res.error;
    }
    var out = res && res.data ? res.data : { ok: false, error: "unknown" };
    if (out.ok) playerUsername = out.username || null;
    return out;
  }

  var productsCache = null;
  var activeCanvas = null;
  var physicalUrl = null; // resolved product URL of the PHYSICAL twin for "Buy this puzzle" (null = none)
  var current = null; // { canvas, grid, timerEl, winEl, boardEl, productId, recorded }
  var playZoom = 1;   // board zoom factor (toolbar −/+); dragBoundFunc reads it, reset per build
  // While true-ish (a timestamp), the stage pointerup pass must NOT run canvas.autoconnect():
  // detachPiece sets it so the double-click unlock is never silently re-snapped by the
  // release handler (root cause of the "double-click snaps the piece back" bug).
  var suppressAutoconnectUntil = 0;
  var timer = { start: 0, id: null, paused: false, elapsedMs: 0, el: null };

  // ---- helpers --------------------------------------------------------------
  function el(html) {
    var t = document.createElement("template");
    t.innerHTML = html.trim();
    return t.content.firstChild;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  // v80: the mockup's Stash ring (wtp-full-interactive-experience.html): a 30px gold ring
  // filled to the box's level, with the box icon in the middle. Shared by the header chip,
  // the tray's Stash row and the "Your Credits" panel so all three always agree.
  var WTP_BOX_ICON = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="10" width="18" height="10" rx="2"></rect>' +
    '<path d="M3 10l2.5-5h13L21 10"></path><path d="M12 14v2"></path></svg>';
  var WTP_COIN_ICON = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v10M9.5 9.5h3.2a1.8 1.8 0 0 1 0 3.6H9.8a1.8 1.8 0 0 0 0 3.6h3.4" ' +
    'stroke-linecap="round"></path></svg>';
  var WTP_BOLT_ICON = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 2 4 14h6l-1 8 9-12h-6l1-8Z"></path></svg>';
  var WTP_SPARK_ICON = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4L12 2Z"></path></svg>';
  function stashRingHtml(stash, cap) {
    var pct = cap > 0 ? Math.max(0, Math.min(1, Number(stash) / cap)) : 0;
    var C = 2 * Math.PI * 12.5;
    return '<span class="wtp-ring" aria-hidden="true">' +
      '<svg width="30" height="30" viewBox="0 0 30 30">' +
        '<circle cx="15" cy="15" r="12.5" fill="none" stroke="#F0DDB0" stroke-width="3"></circle>' +
        // An empty box draws no arc: a zero-length round-capped stroke still paints a dot.
        (pct > 0 ? '<circle class="wtp-ring__fg" cx="15" cy="15" r="12.5" fill="none" stroke="#D9A441" stroke-width="3" ' +
        'stroke-linecap="round" stroke-dasharray="' + C.toFixed(1) + '" stroke-dashoffset="' + (C * (1 - pct)).toFixed(1) + '" ' +
        'transform="rotate(-90 15 15)"></circle>' : "") + "</svg>" +
      '<span class="wtp-ring__icon">' + WTP_BOX_ICON + "</span></span>";
  }

  function maskEmail(e) {
    e = String(e || "");
    var at = e.indexOf("@");
    if (at < 1) return "player";
    return e.slice(0, Math.min(3, at)) + "***" + e.slice(at);
  }
  function fmtTime(s) {
    s = Math.max(0, Math.floor(s));
    var m = Math.floor(s / 60), r = s % 60;
    return (m < 10 ? "0" : "") + m + ":" + (r < 10 ? "0" : "") + r;
  }
  function qparam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }
  function whenReady(cb) {
    if (window.supabase) return cb();
    document.addEventListener("supabase:ready", cb);
    var tries = 0;
    var iv = setInterval(function () {
      if (window.supabase) { clearInterval(iv); cb(); }
      else if (++tries > 50) { clearInterval(iv); }
    }, 100);
  }
  function ensureHeadbreaker() {
    return new Promise(function (resolve, reject) {
      if (window.headbreaker) return resolve(window.headbreaker);
      var done = false;
      document.addEventListener("headbreaker:ready", function () {
        if (!done && window.headbreaker) { done = true; resolve(window.headbreaker); }
      });
      var n = 0;
      var iv = setInterval(function () {
        if (window.headbreaker) { clearInterval(iv); if (!done) { done = true; resolve(window.headbreaker); } }
        else if (++n > 150) { clearInterval(iv); reject(new Error("game engine failed to load")); }
      }, 100);
    });
  }

  // ---- data -----------------------------------------------------------------
  // M13: Shopify's storefront sometimes answers 429 (throttled) or a 5xx for a
  // moment. Retry with backoff (honouring Retry-After) instead of treating a
  // blip as "no puzzles"; a persistent failure THROWS so the page can say so and
  // offer "Try again" — it must never claim the store has no puzzles.
  async function fetchJsonRetry(url, tries) {
    tries = tries || 4;
    for (var i = 0; ; i++) {
      var r = null;
      try { r = await fetch(url, { headers: { Accept: "application/json" } }); } catch (e) { r = null; }
      if (r && r.ok) return r.json();
      var retryable = !r || r.status === 429 || r.status >= 500;
      if (!retryable || i >= tries - 1) throw new Error("feed_" + (r ? r.status : "network"));
      var wait = 800 * Math.pow(2, i);
      var ra = r ? Number(r.headers.get("Retry-After")) : 0;
      if (ra > 0) wait = Math.min(ra * 1000, 5000);
      await new Promise(function (res) { setTimeout(res, wait); });
    }
  }

  function fetchAllProducts() {
    // Promise-cached so concurrent callers share ONE request run.
    if (productsCache) return productsCache;
    productsCache = (async function () {
      var all = [];
      // Fast: fetch ONLY the digital-puzzles collection (~1 request) instead of
      // scanning the entire storefront catalogue across up to 20 requests.
      for (var page = 1; page <= 5; page++) {
        var data = await fetchJsonRetry("/collections/digital-puzzles/products.json?limit=250&page=" + page);
        var list = data.products || [];
        all = all.concat(list);
        if (list.length < 250) break;
      }
      return all;
    })().catch(function (e) { productsCache = null; throw e; });
    return productsCache;
  }
  // PREFETCH immediately: the product list doesn't need Supabase, so load it in
  // parallel with the Supabase CDN script instead of waiting behind it.
  try { fetchAllProducts().catch(function () {}); } catch (e) {}
  function isDigital(p) { return (p.handle || "").indexOf("digital-") === 0; }
  // Resolve a DIGITAL puzzle to its PHYSICAL product URL for the "Buy this puzzle"
  // button. The handles follow no single rule (physical of `digital-horsing-around-
  // jigsaw-puzzle` is `horsing-around`, but of `digital-the-clam-jam-jigsaw-puzzle`
  // is `the-clam-jam-jigsaw-puzzle`), so we match on the puzzle NAME instead: the
  // digital product title is "Digital | The Clam Jam" and its physical twin's title is
  // "The Clam Jam | 1000 Piece Jigsaw Puzzle for Adults" — stripping the platform
  // prefix/suffix from both yields the same key. The physical catalogue is fetched ONCE
  // and matched exactly, because Shopify's predictive search is FUZZY (it returned the
  // unrelated `fuck-off-flowers` for "Colonoscopy Crayons", which is why the button used
  // to lead somewhere wrong). No exact twin → the puzzle has no physical version → the
  // button is omitted (never a wrong product, a 404, or the free digital page).
  var physicalUrlCache = {};
  var physicalMapPromise = null;
  function normPuzzleName(title) {
    return String(title || "")
      .replace(/^\s*digital\s*[|:\-]\s*/i, "") // "Digital | The Clam Jam" → "The Clam Jam"
      .split("|")[0]                           // drop "| 1000 Piece Jigsaw Puzzle for Adults"
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }
  // Physical twins that were RENAMED after their digital version shipped, so the names
  // no longer match. Keyed by the digital puzzle's normalized name (pairing verified:
  // the physical product's artwork file is literally "TarotBitchArtOnly.jpg").
  var PHYSICAL_NAME_ALIASES = { "tarot bitch": "tarot-ibly-sassy" };
  function fetchPhysicalMap() {
    if (physicalMapPromise) return physicalMapPromise;
    physicalMapPromise = (async function () {
      var map = {};
      try {
        for (var page = 1; page <= 5; page++) {
          var list = ((await fetchJsonRetry("/products.json?limit=250&page=" + page)).products) || [];
          list.forEach(function (p) {
            var h = p.handle || "";
            if (!h || h.indexOf("digital-") === 0) return;
            var key = normPuzzleName(p.title);
            if (key && !(key in map)) map[key] = h;
          });
          if (list.length < 250) break;
        }
      } catch (e) {
        // Still failing after retries: no Buy button this time, but DON'T cache
        // the failure — the next puzzle opened tries again.
        physicalMapPromise = null;
        throw e;
      }
      return map;
    })();
    return physicalMapPromise;
  }
  async function resolvePhysicalUrl(digitalHandle, digitalTitle) {
    if (!/^digital-/.test(digitalHandle)) return null;
    if (digitalHandle in physicalUrlCache) return physicalUrlCache[digitalHandle];
    var name = normPuzzleName(digitalTitle) ||
      normPuzzleName(digitalHandle.replace(/^digital-/, "").replace(/-jigsaw-puzzle$/, ""));
    var url = null;
    try {
      var handle = (await fetchPhysicalMap())[name] || PHYSICAL_NAME_ALIASES[name];
      if (handle) url = "/products/" + handle;
    } catch (e) { return null; }           // feed down: omit the button, don't remember that
    physicalUrlCache[digitalHandle] = url; // null = no physical twin → button omitted
    return url;
  }
  function productImage(p) { return p.images && p.images.length ? p.images[0].src : null; }
  // Ask Shopify's CDN for a smaller image (faster card grid). URLs already have ?v=.
  function sizedImg(url, w) {
    if (!url) return url;
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }
  // Admin-set puzzle data (custom image + difficulty), from the public `puzzles` table
  // (RLS allows anon read of is_active rows). Promise-cached; keyed by BOTH handle and
  // shopify_product_id so the play side can look up by either. Never throws.
  var puzzleMapPromise = null;
  function fetchPuzzleMap() {
    if (puzzleMapPromise) return puzzleMapPromise;
    puzzleMapPromise = (async function () {
      var map = {};
      try {
        if (!sb) await sbReady;
        var res = await sb.from("puzzles")
          .select("shopify_product_id, handle, custom_image_url, difficulty, piece_count");
        (res && res.data ? res.data : []).forEach(function (row) {
          if (row.handle) map["h:" + row.handle] = row;
          if (row.shopify_product_id) map["p:" + String(row.shopify_product_id)] = row;
        });
      } catch (e) { /* fall back to Shopify images/difficulty */ }
      return map;
    })().catch(function () { return {}; });
    return puzzleMapPromise;
  }
  // Look up a product's admin row by id or handle.
  function puzzleRowFor(map, p) {
    if (!map || !p) return null;
    return map["p:" + String(p.id)] || map["h:" + (p.handle || "")] || null;
  }
  // The image a player should see: the admin's custom upload wins over the Shopify image.
  function playImage(map, p) {
    var row = puzzleRowFor(map, p);
    return (row && row.custom_image_url) || productImage(p);
  }

  // Puzzles the admin has switched OFF (`puzzles.is_active = false`). The list itself comes
  // from Shopify, and the table's public read is gated on `is_active = true`, so an inactive
  // row and a product with no row yet are BOTH just absent to the anon key. A product with no
  // row must stay VISIBLE (new puzzles default Active), so the hidden ids are stated
  // explicitly by the `inactive_puzzle_ids` RPC. FAILS OPEN: any error returns {} so a
  // Supabase outage shows the full list rather than emptying the page.
  var inactiveIdsPromise = null;
  function fetchInactiveIds() {
    if (inactiveIdsPromise) return inactiveIdsPromise;
    inactiveIdsPromise = (async function () {
      var hidden = {};
      if (!sb) await sbReady;
      var res = await sb.rpc("inactive_puzzle_ids");
      if (res && res.error) throw res.error;
      (res && res.data ? res.data : []).forEach(function (r) {
        var id = r && (r.shopify_product_id != null ? r.shopify_product_id : r);
        if (id != null) hidden[String(id)] = true;
      });
      return hidden;
    })().catch(function () { return {}; });
    return inactiveIdsPromise;
  }

  // Leaderboard RECORD per puzzle (fastest time anyone has set, at any difficulty) plus
  // THIS player's own best. The card badge used to show ONLY the personal best, so a puzzle
  // with a busy leaderboard still read "Not played yet" to anyone who had not played it
  // themselves (client report). Two queries rather than one: deriving both from a single
  // capped result set could miss a puzzle's record once another puzzle accumulates
  // thousands of faster times. Promise-cached and never throws, like fetchPuzzleMap above.
  var bestTimesPromise = null;
  function fetchBestTimes() {
    if (bestTimesPromise) return bestTimesPromise;
    bestTimesPromise = (async function () {
      var out = { global: {}, mine: {} };
      // Must NOT gate on `sb` being set. The dashboard renders before the Supabase CDN
      // module arrives, so the old `if (customerEmail && sb)` guard silently skipped the
      // whole query on a cold load and every card fell back to "Not played yet".
      if (!sb) await sbReady;
      var mineQ = customerEmail
        ? sb.from("puzzle_scores").select("shopify_product_id, time_seconds").eq("email", getPlayerKey())
        : Promise.resolve({ data: [] });
      var res = await Promise.all([
        sb.from("puzzle_scores").select("shopify_product_id, time_seconds, difficulty")
          .order("time_seconds", { ascending: true }).limit(2000),
        mineQ,
      ]);
      // Rows arrive fastest-first, so the FIRST row seen for a product is its record.
      // Keep the DIFFICULTY it was set at: the in-game leaderboard is per-difficulty, so
      // the card must name the level or it appears to contradict the board the player opens
      // (measured on live data: 9 of 16 puzzles' records are NOT at the default level, and
      // 3 open on a tab with no times at all).
      (res[0] && res[0].data ? res[0].data : []).forEach(function (r) {
        var id = String(r.shopify_product_id);
        if (!(id in out.global)) out.global[id] = { t: r.time_seconds, d: r.difficulty };
      });
      (res[1] && res[1].data ? res[1].data : []).forEach(function (r) {
        var id = String(r.shopify_product_id);
        if (!(id in out.mine) || r.time_seconds < out.mine[id]) out.mine[id] = r.time_seconds;
      });
      return out;
    })().catch(function () { return { global: {}, mine: {} }; });
    return bestTimesPromise;
  }
  // Card badge: the leaderboard record first, then the player's own time when they have one.
  // The record is public (scores are public-read), so it shows to logged-out visitors too;
  // the "You" line does not. Empty wording matches loadLeaderboard's exactly.
  function badgeHtml(pid, best) {
    var g = best && best.global[pid];
    var m = best && best.mine[pid];
    if (g == null) {
      return '<div class="wtp-pcard__best wtp-pcard__best--none">No times yet — be the first! 🏁</div>';
    }
    // Name the level the record was set at (DIFF_NAMES is assigned before this ever runs —
    // badgeHtml is only called from fetchBestTimes' async callback). Omitted if unknown.
    var lvl = DIFF_NAMES && DIFF_NAMES[g.d];
    var html = '<div class="wtp-pcard__best">🏆 Best ' + fmtTime(g.t) +
      (lvl ? ' <span class="wtp-pcard__diff">· ' + esc(lvl) + "</span>" : "") + "</div>";
    if (m != null) {
      html += '<div class="wtp-pcard__best wtp-pcard__best--you">⏱ You ' + fmtTime(m) + "</div>";
    }
    return html;
  }

  // ---- credit wallet (v2 scope, M0) ----------------------------------------
  // Credits are real money, so the wallet does NOT use this file's usual identity
  // model. Every other player RPC here passes `p_email` and is anon-callable —
  // forgeable by design, fine for a leaderboard (§7). The wallet RPCs take NO email
  // argument: the server derives the caller from the verified Supabase Auth JWT
  // (current_email()) and they are revoked from `anon` entirely.
  //
  // A wallet action therefore needs a real Supabase session whose email MATCHES the
  // logged-in Shopify customer. Verification is LAZY — nothing here runs until the
  // player actually reaches for credits, so the free game never waits on it.
  var wtpWallet = (function () {
    var cached = null;    // last wallet_state() payload
    var inflight = null;  // de-dupes concurrent refreshes

    // The session must belong to THIS Shopify customer. /pages/admin signs in on
    // the SAME origin, so without this check an admin who then plays would be
    // handed the admin account's wallet. Also covers a leftover session from a
    // previous customer on a shared device.
    async function boundSession() {
      if (!customerEmail) return null;
      if (!sb) await sbReady;
      try {
        var r = await sb.auth.getSession();
        var sess = r && r.data && r.data.session;
        if (!sess || !sess.user || !sess.user.email) return null;
        if (String(sess.user.email).toLowerCase() !== customerEmail) return null;
        return sess;
      } catch (e) { return null; }
    }

    // Wallet snapshot (balance, stash, cosmetics, equipped). Returns null when the
    // player has not verified on this device yet. Never throws.
    async function state(force) {
      if (cached && !force) return cached;
      if (inflight && !force) return inflight;
      inflight = (async function () {
        if (!(await boundSession())) return null;
        try {
          var res = await sb.rpc("wallet_state");
          if (res && res.error) return null;
          cached = res && res.data ? res.data : null;
          return cached;
        } catch (e) { return null; }
      })();
      try { return await inflight; } finally { inflight = null; }
    }

    // Call before ANY wallet action. Resolves to a session, or null if the player
    // dismissed verification. Opens the prompt only when genuinely needed.
    async function ensure() {
      var sess = await boundSession();
      if (sess) return sess;
      if (!customerEmail) return null;   // not logged into Shopify at all
      return await verifyModal();
    }

    // Run a wallet RPC, verifying first. Always returns a shape callers can branch
    // on, never throws.
    async function call(fn, args) {
      if (!(await ensure())) return { ok: false, error: "not_verified" };
      try {
        var res = await sb.rpc(fn, args || {});
        if (res && res.error) return { ok: false, error: res.error.message || "rpc_failed" };
        await state(true); // balances moved — refresh the snapshot
        repaintAll();       // … and push it to every chip on screen, live
        return res && res.data ? res.data : { ok: false, error: "empty" };
      } catch (e) { return { ok: false, error: (e && e.message) || "threw" }; }
    }

    // One-time-per-device email verification. Shopify already proved this address at
    // login, but that proof cannot be handed to Supabase without an App Proxy (§2),
    // so the player confirms it once here and the session then persists.
    // Accepts EITHER the emailed code or the emailed link — a code only appears if
    // the Supabase email template includes the token, whereas the link works on the
    // default template. See the setup note in §10.
    function verifyModal() {
      return new Promise(function (resolve) {
        var existing = document.querySelector(".wtp-modal-overlay");
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
        var overlay = el(
          '<div class="wtp-modal-overlay">' +
            '<div class="wtp-modal" role="dialog" aria-modal="true" aria-label="Verify your email">' +
              '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
              '<div class="wtp-modal__emoji">🔐</div>' +
              "<h2>Verify your email</h2>" +
              '<p class="wtp-modal__time">Credits are tied to your account, so we need to confirm ' +
              "<strong>" + esc(customerEmail) + "</strong> once on this device.</p>" +
              '<div class="wtp-verify">' +
                '<button type="button" class="wtp-btn wtp-btn--primary wtp-verify-send">Send me a code</button>' +
                '<div class="wtp-verify-step2" style="display:none">' +
                  '<input class="wtp-verify-code" type="text" inputmode="numeric" autocomplete="one-time-code" ' +
                  'maxlength="8" placeholder="Code from the email">' +
                  '<button type="button" class="wtp-btn wtp-btn--primary wtp-verify-check">Verify</button>' +
                "</div>" +
                '<p class="wtp-verify-note" style="display:none">Check your inbox — enter the code, or ' +
                "just click the link in the email and you will come straight back here.</p>" +
                '<p class="wtp-verify-err" style="display:none"></p>' +
              "</div>" +
            "</div>" +
          "</div>");
        document.body.appendChild(overlay);
        var sendBtn = overlay.querySelector(".wtp-verify-send");
        var step2 = overlay.querySelector(".wtp-verify-step2");
        var note = overlay.querySelector(".wtp-verify-note");
        var errEl = overlay.querySelector(".wtp-verify-err");
        var codeEl = overlay.querySelector(".wtp-verify-code");
        var checkBtn = overlay.querySelector(".wtp-verify-check");
        function fail(m) { errEl.textContent = m; errEl.style.display = ""; }
        function done(v) { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); resolve(v); }
        overlay.querySelector(".wtp-modal__close").addEventListener("click", function () { done(null); });
        overlay.addEventListener("click", function (e) { if (e.target === overlay) done(null); });

        sendBtn.addEventListener("click", async function () {
          errEl.style.display = "none";
          sendBtn.disabled = true; sendBtn.textContent = "Sending…";
          try {
            if (!sb) await sbReady;
            var r = await sb.auth.signInWithOtp({
              email: customerEmail,
              options: { shouldCreateUser: true, emailRedirectTo: location.href },
            });
            if (r && r.error) throw r.error;
            sendBtn.style.display = "none";
            step2.style.display = ""; note.style.display = "";
            try { codeEl.focus(); } catch (e) {}
          } catch (e) {
            sendBtn.disabled = false; sendBtn.textContent = "Send me a code";
            fail("Could not send the email: " + ((e && e.message) || e));
          }
        });

        checkBtn.addEventListener("click", async function () {
          var token = (codeEl.value || "").replace(/\s/g, "");
          if (token.length < 6) return fail("Enter the code from the email.");
          errEl.style.display = "none";
          checkBtn.disabled = true; checkBtn.textContent = "Verifying…";
          try {
            var r = await sb.auth.verifyOtp({ email: customerEmail, token: token, type: "email" });
            if (r && r.error) throw r.error;
            cached = null;
            done((r && r.data && r.data.session) || null);
          } catch (e) {
            checkBtn.disabled = false; checkBtn.textContent = "Verify";
            fail("That code did not work — check it and try again.");
          }
        });
        codeEl.addEventListener("keydown", function (e) { if (e.key === "Enter") checkBtn.click(); });
      });
    }

    // v78: the Stash chip carries a fill ring ("Stash Box ring in the header") and reads
    // "Box full" once the box reaches its cap ("once full it shows 'Box full'").
    // v80: drawn as the mockup has it — the 30px ring with the box icon, then "12/20" over
    // "LOCKED CREDITS" (or "BOX FULL").
    function stashChipHtml(stash, cap) {
      var full = cap > 0 && stash >= cap;
      return '<button type="button" class="wtp-stash-chip' + (full ? " wtp-stash-chip--full" : "") + '" title="' +
        (full ? "Your Stash Box is full — cash out to keep earning" : "Locked Credits earned by finishing puzzles") + '">' +
        stashRingHtml(stash, cap) +
        '<span class="wtp-stash-chip__txt"><span class="wtp-stash-chip__n">' + stashNum(stash) + "/" + cap + "</span>" +
        '<span class="wtp-stash-chip__l">' + (full ? "Box full" : "Locked Credits") + "</span></span></button>";
    }
    // Locked Credits can be fractional (Very Easy earns 0.25): never print float noise.
    function stashNum(v) { return String(Math.round(Number(v || 0) * 100) / 100); }

    // Header chips. Rendered only for a logged-in Shopify customer — credits are
    // meaningless otherwise.
    function chipHtml(st) {
      if (!customerEmail) return "";
      if (!st || !st.signed_in) {
        return '<button type="button" class="wtp-credit-chip wtp-credit-chip--locked">🔐 Credits</button>';
      }
      var n = Number(st.wallet || 0);
      var stash = Number(st.stash || 0);
      var cap = Number(st.stash_cap || 20);
      // v80: the mockup's order — Stash chip, credits chip, then "+ Buy".
      if (st.unlimited) {   // M10: subscribers have no balance to watch
        return stashChipHtml(stash, cap) +
          '<button type="button" class="wtp-credit-chip wtp-credit-chip--unl" ' +
          'title="Unlimited — every add-on is included">∞ Unlimited</button>';
      }
      return stashChipHtml(stash, cap) +
        '<button type="button" class="wtp-credit-chip">' + WTP_COIN_ICON + "<span>" + n +
        (n === 1 ? " credit" : " credits") + "</span></button>" +
        '<button type="button" class="wtp-buy-chip" title="Buy credits">+ Buy</button>';
    }

    // Every mounted chip container. Tracked so a balance change repaints ALL of
    // them — that is what makes the Stash tick up the instant a puzzle is solved,
    // instead of only after a page refresh.
    var hosts = [];

    function paintInto(hostEl) {
      if (!hostEl) return;
      hostEl.innerHTML = chipHtml(cached);
      var c = hostEl.querySelector(".wtp-credit-chip");
      if (c) c.addEventListener("click", async function () {
        if (cached && cached.unlimited) return openBuyCredits();   // plan status + manage link
        await ensure();
        await state(true);
        repaintAll();
        if (cached && cached.signed_in) wtpHistory.open();         // M11: where it came from
      });
      var sChip = hostEl.querySelector(".wtp-stash-chip");
      if (sChip) sChip.addEventListener("click", function () { stashInfo(cached); });
      var bChip = hostEl.querySelector(".wtp-buy-chip");
      if (bChip) bChip.addEventListener("click", function () { openBuyCredits(); });
    }

    // Drop hosts that have been removed from the DOM (every re-render of the meta
    // line creates a fresh slot), then repaint the rest.
    function repaintAll() {
      hosts = hosts.filter(function (h) { return h && h.isConnected; });
      hosts.forEach(paintInto);
    }

    // Paint the chips into a header, then keep them live in the background.
    function mount(hostEl) {
      if (!hostEl || !customerEmail) return;
      if (hosts.indexOf(hostEl) === -1) hosts.push(hostEl);
      paintInto(hostEl);
      // Background refresh — never blocks the header paint.
      state(false).then(function (st) { if (st) repaintAll(); }).catch(function () {});
    }

    // ---- Buy Credits (M1) ---------------------------------------------------
    // The pack catalogue lives in Supabase (`credit_packs`), not in this file, so
    // the client can reprice or add a pack without a theme deploy. Only rows that
    // are is_active AND have a Shopify variant id are sellable — a half-configured
    // pack stays invisible instead of rendering a dead Buy button.
    var packsPromise = null;
    function fetchPacks() {
      if (packsPromise) return packsPromise;
      packsPromise = (async function () {
        if (!sb) await sbReady;
        var res = await sb.from("credit_packs")
          .select("code, kind, label, badge, price_text, credits, rate_text, featured, first_purchase_only, " +
            "shopify_variant_id, shopify_product_id, shopify_handle")
          .order("sort_order", { ascending: true });
        if (res && res.error) throw res.error;
        return (res && res.data ? res.data : []).filter(function (p) {
          return p.shopify_variant_id;
        });
      })().catch(function () { return []; });
      return packsPromise;
    }

    // Shopify AJAX cart, then straight to checkout. Any physical items already in
    // the cart come along, which is what a shopper would expect.
    // M10: the $14.99/mo Unlimited tile is a Seal Subscriptions product. It must
    // go in the cart WITH its selling plan — added without one, Shopify sells it
    // as a one-off. The plan is read from the product's own storefront JSON, so
    // nothing is hardcoded; the handle comes from credit_packs or, failing that,
    // from the storefront catalogue by product id.
    async function subscriptionPlanId(p) {
      var handle = p.shopify_handle || null;
      for (var page = 1; !handle && p.shopify_product_id && page <= 5; page++) {
        var list = [];
        try { list = ((await fetchJsonRetry("/products.json?limit=250&page=" + page)).products) || []; } catch (e) { break; }
        var hit = list.filter(function (x) { return String(x.id) === String(p.shopify_product_id); })[0];
        if (hit) handle = hit.handle;
        if (list.length < 250) break;
      }
      if (!handle) return null;
      var prod = null;
      try { prod = await fetchJsonRetry("/products/" + encodeURIComponent(handle) + ".js"); } catch (e) { return null; }
      var plans = [];
      ((prod && prod.selling_plan_groups) || []).forEach(function (g) {
        (g.selling_plans || []).forEach(function (s) { plans.push(s); });
      });
      if (!plans.length) return null;
      var monthly = plans.filter(function (s) { return /month/i.test((s.name || "") + " " + (s.description || "")); })[0];
      return (monthly || plans[0]).id;
    }

    // Shopify AJAX cart, then straight to checkout. Any physical items already in
    // the cart come along, which is what a shopper would expect.
    async function buyPack(p, btn) {
      var original = btn ? btn.textContent : "";
      if (btn) { btn.disabled = true; btn.textContent = "Adding…"; }
      try {
        var item = { id: Number(p.shopify_variant_id), quantity: 1 };
        if (p.kind === "subscription") {
          var plan = await subscriptionPlanId(p);
          if (!plan) throw new Error("no_plan");     // never sell Unlimited as a one-off
          item.selling_plan = Number(plan);
        }
        var r = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [item] }),
        });
        if (!r.ok) throw new Error("cart add failed (" + r.status + ")");
        location.href = "/checkout";
      } catch (e) {
        if (btn) { btn.disabled = false; btn.textContent = original; }
        alert(e && e.message === "no_plan"
          ? "Sorry, the Unlimited subscription isn't available right now. Please try again later."
          : "Sorry, that could not be added to your cart. Please try again.");
      }
    }

    function packCardHtml(p, st) {
      var solid = p.featured ? " wtp-pack__buy--solid" : "";
      var active = p.kind === "subscription" && st && st.unlimited;
      return '<div class="wtp-pack' + (p.featured ? " wtp-pack--featured" : "") + '">' +
        (p.badge ? '<span class="wtp-pack__badge">' + esc(p.badge) + "</span>" : "") +
        '<div class="wtp-pack__name">' + esc(p.label) + "</div>" +
        '<div class="wtp-pack__price">' + esc(p.price_text) + "</div>" +
        '<div class="wtp-pack__credits">' +
          (p.kind === "subscription"
            ? "unlimited credits"
            : (Number(p.credits) + " credits")) + "</div>" +
        '<div class="wtp-pack__rate">' + esc(p.rate_text || "") + "</div>" +
        (active
          ? '<button type="button" class="wtp-pack__buy wtp-pack__buy--active" disabled>Active ✓</button>'
          : '<button type="button" class="wtp-pack__buy' + solid + '" data-code="' + esc(p.code) + '">' +
            (p.kind === "subscription" ? "Subscribe" : "Buy") + "</button>") +
      "</div>";
    }

    async function openBuyCredits() {
      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal wtp-modal--wide" role="dialog" aria-modal="true" aria-label="Buy Credits">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
            "<h2>Buy Credits</h2>" +
            '<p class="wtp-modal__time">Spend credits on in-puzzle upsells — a completed border, ' +
            "hints, sounds, and more. Bigger packs get a bigger bonus.</p>" +
            '<div class="wtp-pack-grid"><p class="wtp-play__sub">Loading…</p></div>' +
            '<p class="wtp-hist-link"><button type="button" class="wtp-link-btn" data-open-history>' +
              "View your credit history →</button></p>" +
          "</div>" +
        "</div>");
      document.body.appendChild(overlay);
      function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });

      var grid = overlay.querySelector(".wtp-pack-grid");
      // v85: Buy Credits sells credit packs and Unlimited only. The Stash Box unlock has its own
      // window (stashInfo) and would otherwise show here as a "0 credits" tile once its product exists.
      var packs = (await fetchPacks()).filter(function (p) { return p.kind === "pack" || p.kind === "subscription"; });

      // Hide the first-purchase-only tile once this player has bought before.
      // The real rule is enforced server-side: the webhook grants the offer once
      // per account, one unit (wallet_grant_first_offer); a repeat grants nothing.
      var purchased = false;
      try {
        if (await boundSession()) {
          var hp = await sb.rpc("wallet_has_purchased");
          purchased = !!(hp && hp.data);
        }
      } catch (e) { /* treat as not purchased */ }
      if (purchased) packs = packs.filter(function (p) { return !p.first_purchase_only; });

      // M10: a subscriber always sees their plan status — even when no packs are
      // on sale — so it goes in before the empty-state early return below.
      var stNow = cached;
      if (stNow && stNow.unlimited) {
        var until = stNow.unlimited_until ? new Date(stNow.unlimited_until) : null;
        grid.insertAdjacentHTML("beforebegin",
          '<div class="wtp-unl-banner">✓ You’re on <b>Unlimited</b> — every add-on is included' +
          (until && !isNaN(until) ? " until <b>" + esc(until.toLocaleDateString(undefined,
            { month: "short", day: "numeric", year: "numeric" })) + "</b>" : "") +
          '. <a href="/account">Manage subscription →</a></div>');
      }

      if (!packs.length) {
        // Narrow the modal: 860px is sized for the card grid and looks like an
        // empty box with a single line of text in it.
        var box = overlay.querySelector(".wtp-modal");
        if (box) box.classList.remove("wtp-modal--wide");
        grid.outerHTML = '<p class="wtp-play__sub wtp-packs-empty">' +
          "Credit packs aren't on sale just yet — check back soon.</p>";
        return;
      }
      grid.innerHTML = packs.map(function (p) { return packCardHtml(p, stNow); }).join("");
      // v85: with only one or two tiles on sale (today: Unlimited alone), centre them in a narrower
      // window instead of stretching one card across the 860px grid.
      if (packs.length <= 2) {
        var few = overlay.querySelector(".wtp-modal");
        if (few) few.classList.remove("wtp-modal--wide");
        grid.classList.add("wtp-pack-grid--few");
        grid.style.gridTemplateColumns = "repeat(" + packs.length + ", minmax(170px, 220px))";
      }
      // v86: the full shop is 6 tiles (5 packs + Unlimited). The auto-fit grid put 5 in a row and the 6th
      // alone underneath; lay them out 3 x 2 on desktop instead (phones keep their 2 columns).
      if (packs.length === 6) grid.classList.add("wtp-pack-grid--six");
      if (!packs.some(function (p) { return p.kind === "pack"; })) {
        grid.insertAdjacentHTML("afterend", '<p class="wtp-packs-soon">Credit packs are coming soon.</p>');
      }
      grid.addEventListener("click", function (e) {
        var b = e.target.closest(".wtp-pack__buy[data-code]");
        if (!b) return;
        var p = packs.filter(function (x) { return x.code === b.getAttribute("data-code"); })[0];
        if (p) buyPack(p, b);
      });
    }

    // ---- Stash Box (M2) -----------------------------------------------------
    // Locked Credits accrue free as puzzles are finished. Unlocking converts the
    // WHOLE box for one flat price, per the client's rule: "Claim everything in
    // the box right now for one flat price — no matter how full it is."
    // The economy numbers come from `credit_settings` and the unlock price from
    // the `stash_unlock` row of `credit_packs`, so nothing here is hardcoded.
    var economyPromise = null;
    function fetchEconomy() {
      if (economyPromise) return economyPromise;
      economyPromise = (async function () {
        if (!sb) await sbReady;
        var res = await sb.rpc("credit_settings_public");
        if (res && res.error) throw res.error;
        return (res && res.data) || {};
      })().catch(function () { return {}; });
      return economyPromise;
    }

    // The difficulty ladder shown in the mockup. DIFF_NAMES is module scope.
    var STASH_LADDER = [[2, 0.25], [3, 0.5], [4, 1], [5, 2], [6, 3.5], [7, 5.5], [8, 8.5]];

    async function stashInfo(st) {
      st = st || cached;
      var stash = Number((st && st.stash) || 0);
      var cap = Number((st && st.stash_cap) || 20);
      var pct = cap > 0 ? Math.max(0, Math.min(1, stash / cap)) : 0;
      var canUnlock = stash > 0;   // client removed the half-full minimum
      var full = cap > 0 && stash >= cap;   // v78: "once full it shows 'Box full' and stops filling"

      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal wtp-modal--stash" role="dialog" aria-modal="true" aria-label="Your Stash Box">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
            "<h2>Your Stash Box</h2>" +
            '<p class="wtp-modal__time">Locked Credits pile up here for free as you finish puzzles ' +
            "— harder puzzles earn more. Cash out any time, whatever is in there.</p>" +
            '<div class="wtp-stash-body">' +
              '<div class="wtp-stash-visual">' +
                '<div class="wtp-stash-ring">' +
                  '<svg width="168" height="168" viewBox="0 0 168 168" aria-hidden="true">' +
                    '<circle cx="84" cy="84" r="72" fill="none" stroke="#F0DDB0" stroke-width="10"></circle>' +
                    '<circle class="wtp-stash-ring__fg" cx="84" cy="84" r="72" fill="none" stroke="#D9A441" ' +
                    'stroke-width="10" stroke-linecap="round" stroke-dasharray="452.4" ' +
                    'stroke-dashoffset="' + (452.4 * (1 - pct)).toFixed(1) + '"></circle>' +
                  "</svg>" +
                  '<div class="wtp-stash-ring__num"><span class="n">' + stash + "</span>" +
                  (full ? '<span class="of wtp-stash-full">Box full</span>'
                        : '<span class="of">of ' + cap + " Locked Credits</span>") + "</div>" +
                "</div>" +
                '<div class="wtp-stash-value"></div>' +
                (full ? '<p class="wtp-stash-fullnote">It’s stopped filling — cash out to keep earning.</p>' : "") +
              "</div>" +
              '<div class="wtp-stash-right">' +
                '<div class="wtp-stash-offer"></div>' +
                '<div class="wtp-stash-ladder">' +
                  '<div class="wtp-stash-ladder__title">How you earn Locked Credits</div>' +
                  STASH_LADDER.map(function (row) {
                    return '<div class="wtp-stash-ladder__row"><span>' +
                      esc(DIFF_NAMES[row[0]] || ("Level " + row[0])) +
                      '</span><span class="v">' + row[1] + "</span></div>";
                  }).join("") +
                "</div>" +
                '<p class="wtp-stash-foot">Awarded the first time you finish a puzzle at each ' +
                "difficulty — replays don’t earn more.</p>" +
              "</div>" +
            "</div>" +
          "</div>" +
        "</div>");
      document.body.appendChild(overlay);
      function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });

      // Value + unlock offer need the economy settings and the unlock product,
      // so they fill in after the modal is already on screen.
      var econ = await fetchEconomy();
      var perCredit = Number(econ.credit_value_usd || 0.5);
      var value = stash * perCredit;
      var valueEl = overlay.querySelector(".wtp-stash-value");
      if (valueEl) {
        valueEl.innerHTML = "= <strong>$" + value.toFixed(2) + "</strong> of value inside";
      }

      var packs = await fetchPacks();
      var unlock = packs.filter(function (p) { return p.kind === "stash_unlock"; })[0];
      var offer = overlay.querySelector(".wtp-stash-offer");
      if (!offer) return;

      if (!unlock) {
        offer.innerHTML = '<h4>Keep playing</h4><p class="wtp-play__sub">Unlocking isn’t ' +
          "available just yet — your Locked Credits are safe in the box.</p>";
        return;
      }

      var price = parseFloat(String(unlock.price_text).replace(/[^0-9.]/g, "")) || 0;
      var saving = value > 0 && price > 0 && price < value
        ? Math.round(((value - price) / value) * 100) : 0;

      offer.innerHTML =
        "<h4>" + (full ? "Box full — ready to cash out" : canUnlock ? "Ready to cash out" : "Nothing in the box yet") + "</h4>" +
        '<p class="wtp-play__sub">Claim everything in the box right now for one flat price ' +
        "— no matter how full it is.</p>" +
        '<div class="wtp-stash-price-row"><span class="wtp-stash-price">' +
          esc(unlock.price_text) + "</span>" +
          (saving > 0 ? '<span class="wtp-stash-save">' + saving + "% off value</span>" : "") +
        "</div>" +
        (canUnlock
          ? '<button type="button" class="wtp-btn wtp-btn--primary wtp-stash-unlock" data-variant="' +
            esc(String(unlock.shopify_variant_id)) + '">Cash Out Stash Box</button>'
          : '<p class="wtp-stash-locked-note">Finish a puzzle to start filling the box — ' +
            "then you can cash out any time.</p>");

      var btn = offer.querySelector(".wtp-stash-unlock");
      // v86: buyPack takes the pack OBJECT since M10 (it reads .shopify_variant_id and .kind). This call
      // still passed the bare variant id, so the cart add sent id NaN and Cash Out could never succeed;
      // unnoticed until 2026-10-18, when the client created the Stash Box Unlock product.
      if (btn) btn.addEventListener("click", function () {
        buyPack(unlock, btn);
      });
    }

    return { state: state, ensure: ensure, call: call, mount: mount,
             boundSession: boundSession, repaint: repaintAll,
             buyCredits: openBuyCredits,
             // Painting the tray must not await; the tray refreshes separately.
             stateSync: function () { return cached; },
             stash: stashInfo };
  })();

  // ---- Bells & Whistles tray (M3) -------------------------------------------
  // The shared plumbing for every paid in-puzzle extra. M4 (consumables) and M5
  // (cosmetics) do not re-implement spending — they register an `apply` function
  // here and inherit one correct path: verify -> check balance -> spend on the
  // SERVER -> apply -> repaint -> confirm.
  //
  // SAFETY RULE: a row whose `apply` is still null renders disabled as "Soon" and
  // cannot be bought. A player must never be able to spend a real credit on a
  // feature that has not been built yet.
  var wtpBW = (function () {
    // registry — M4/M5 fill in `apply`. cost/label/copy come from the client mockup.
    // v80: each row's icon is the mockup's own (wtp-full-interactive-experience.html).
    var ICO = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
    var ITEMS = {
      consumable: [
        { key: "edge_lord", label: "Edge Lord", cost: 1,
          alt: "Auto-places the border — starts you with the frame finished", once: true, apply: null,
          icon: ICO + '<rect x="3" y="3" width="18" height="18" rx="3" stroke-dasharray="3 3"></rect></svg>' },
        { key: "cheat_peek", label: "Cheat Peek", cost: 1,
          alt: "5-second look at the full picture, over the board", once: false, apply: null,
          icon: ICO + '<path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>' },
        { key: "auto_sort", label: "Auto-Sort Edge Pieces", cost: 1,
          alt: "Gathers every edge piece beside the board", once: true, apply: null,
          icon: ICO + '<path d="M8 3v14M8 17l-4-4M8 17l4-4M16 21V7M16 7l4 4M16 7l-4 4"></path></svg>' },
      ],
      cosmetic: [
        { key: "sound",    label: "Snap Pack",         alt: "10 sounds · tap to browse",   browse: null,
          icon: ICO + '<path d="M11 5 6 9H2v6h4l5 4V5Z"></path><path d="M15.5 8.5a5 5 0 0 1 0 7"></path><path d="M18.5 6a9 9 0 0 1 0 12"></path></svg>' },
        { key: "foil",     label: "Gold Foil Finish",  alt: "10 finishes · tap to browse", browse: null,
          icon: ICO + '<path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4L12 2Z"></path></svg>' },
        { key: "confetti", label: "Confetti Cannon",   alt: "10 styles · tap to browse",   browse: null,
          icon: ICO + '<path d="M4 20l4-1 9-9-3-3-9 9-1 4Z"></path><path d="M14 5l1 1M18 9l1 1M17 4l.5 1.5M20 7.5l1.5.5"></path></svg>' },
        { key: "cursor",   label: "Custom Cursor",     alt: "10 skins · tap to browse",    browse: null,
          icon: ICO + '<path d="M4 4l7 17 2-7 7-2L4 4Z"></path></svg>' },
      ],
    };

    // M4/M5 call this to light a row up. v80: `check` (optional) runs BEFORE any charge
    // and returns a reason string when there is nothing for the helper to do, so a player
    // is never charged for a no-op (e.g. Auto-Sort with every edge piece already placed).
    function register(group, key, fn, check) {
      (ITEMS[group] || []).forEach(function (it) {
        if (it.key === key) { if (group === "cosmetic") it.browse = fn; else { it.apply = fn; it.check = check || null; } }
      });
    }

    // v80: one-per-puzzle helpers already used on THIS puzzle show "✓ Applied" (as in the
    // mockup) instead of a price. The server is the authority (puzzle_consumables, which a
    // player may read for their own email); the set is refreshed whenever the tray opens.
    var used = { product: null, keys: {} };
    function usedSet() {
      var pid = current && current.productId;
      if (used.product !== pid) used = { product: pid, keys: {} };
      return used.keys;
    }
    async function loadUsed() {
      var pid = current && current.productId;
      if (!pid || !sb) return;
      try {
        if (!(await wtpWallet.boundSession())) return;     // never prompts; signed-in players only
        var res = await sb.from("puzzle_consumables").select("item").eq("shopify_product_id", String(pid));
        if (res && !res.error && res.data && current && current.productId === pid) {
          var keys = usedSet();
          res.data.forEach(function (r) { keys[r.item] = true; });
        }
      } catch (e) { /* the server still refuses a second use */ }
    }

    var trayEl = null;

    function close() {
      if (trayEl && trayEl.parentNode) trayEl.parentNode.removeChild(trayEl);
      trayEl = null;
    }

    // A small confirmation, per the client's "displays a confirmation pop-up".
    function toast(msg, isErr) {
      var t = el('<div class="wtp-toast' + (isErr ? " wtp-toast--err" : "") + '">' + esc(msg) + "</div>");
      document.body.appendChild(t);
      setTimeout(function () { t.classList.add("wtp-toast--in"); }, 10);
      setTimeout(function () {
        t.classList.remove("wtp-toast--in");
        setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 300);
      }, 2600);
    }

    // v78: "Every spend asks for confirmation first" (Sep 30 Scope & Plan). Its own layer
    // on <body>, above the tray and the cosmetics catalogue, so it never replaces either.
    // Resolves true ONLY on the confirm button; Cancel, the backdrop and Esc resolve false.
    function confirm(opts) {
      return new Promise(function (resolve) {
        var layer = el(
          '<div class="wtp-confirm" role="dialog" aria-modal="true" aria-label="' + esc(opts.title) + '">' +
            '<div class="wtp-confirm__box">' +
              "<h3>" + esc(opts.title) + "</h3>" +
              (opts.text ? "<p>" + esc(opts.text) + "</p>" : "") +
              (opts.note ? '<p class="wtp-confirm__cost">' + esc(opts.note) + "</p>" : "") +
              '<div class="wtp-confirm__actions">' +
                '<button type="button" class="wtp-confirm__no">Cancel</button>' +
                '<button type="button" class="wtp-confirm__yes">' + esc(opts.ok || "Confirm") + "</button>" +
              "</div>" +
            "</div>" +
          "</div>");
        function onKey(e) { if (e.key === "Escape") { e.stopPropagation(); done(false); } }
        function done(v) {
          document.removeEventListener("keydown", onKey, true);
          if (layer.parentNode) layer.parentNode.removeChild(layer);
          resolve(v);
        }
        layer.addEventListener("click", function (e) {
          if (e.target === layer || e.target.closest(".wtp-confirm__no")) return done(false);
          if (e.target.closest(".wtp-confirm__yes")) return done(true);
        });
        document.addEventListener("keydown", onKey, true);
        document.body.appendChild(layer);
        try { layer.querySelector(".wtp-confirm__yes").focus(); } catch (e) {}
      });
    }
    function plural(n, word) { return n + " " + word + (Number(n) === 1 ? "" : "s"); }

    // THE spend pipeline. Everything paid goes through here.
    async function spendOn(item, btn) {
      if (!item.apply) return;                     // guarded in render too
      if (!current || !current.productId) {
        return toast("Start the puzzle first.", true);
      }
      if (!(await wtpWallet.ensure())) return;     // not verified -> prompt shown
      if (item.once && usedSet()[item.key]) {
        return toast("You've already used " + item.label + " on this puzzle.", true);
      }
      if (item.check) {                            // v80: nothing to do -> nothing charged
        var why0 = null;
        try { why0 = item.check(); } catch (e) {}
        if (why0) return toast(why0, true);
      }

      var st = await wtpWallet.state(true);
      var bal = Number((st && st.wallet) || 0);
      var unlimited = !!(st && st.unlimited);
      if (!unlimited && bal < item.cost) {
        close();
        toast("You need " + item.cost + " credit" + (item.cost === 1 ? "" : "s") + " for that.", true);
        return wtpWallet.buyCredits();             // don't dead-end: offer to top up
      }

      // Ask first — nothing is charged unless the player confirms.
      var go = await confirm({
        title: "Use " + item.label + "?",
        text: item.alt + ".",
        note: unlimited ? "Included with Unlimited" : plural(item.cost, "credit") + " · you have " + plural(bal, "credit"),
        ok: unlimited ? "Use it" : "Use " + plural(item.cost, "credit"),
      });
      if (!go) return;

      var original = btn ? btn.textContent : "";
      if (btn) { btn.disabled = true; btn.textContent = "…"; }

      var res = await wtpWallet.call("wallet_use_consumable", {
        p_product: current.productId,
        p_item: item.key,
        p_cost: item.cost,
        p_once_per_puzzle: !!item.once,
      });

      if (!res || !res.ok) {
        if (btn) { btn.disabled = false; btn.textContent = original; }
        if (res && res.error === "already_used") { usedSet()[item.key] = true; renderRows(); }
        var why = res && res.error === "already_used"
          ? "You've already used that on this puzzle."
          : res && res.error === "insufficient"
            ? "Not enough credits."
            : res && res.error === "price_changed"
              ? "That price just changed — please try again."
            : "That didn't go through — please try again.";
        return toast(why, true);
      }

      // Charged — so it counts as a hint used NOW, before applying: a hint can
      // finish the puzzle itself (Edge Lord on a 2-row board), and the
      // completion screen is drawn inside apply(). (M6: "n hints used")
      hintsAdd();
      if (item.once) usedSet()[item.key] = true;
      // Apply the effect; a throw here must not silently swallow the spend.
      // v80: the tray closes FIRST — it covers the board, and Edge Lord / Auto-Sort now
      // animate the pieces into place, which the player should see. apply() resolves when
      // the animation has finished; the confirmation follows it.
      try {
        close();
        await item.apply();
        toast(item.label + ((st && st.unlimited) ? " applied — included with Unlimited." : " applied — 1 credit spent."));
      } catch (e) {
        toast(item.label + " was charged but could not be applied. Please tell support.", true);
      }
      renderRows();
    }

    function rowHtml(it, kind, st) {
      var owned = false;
      if (kind === "cosmetic" && st && st.cosmetics) {
        owned = st.cosmetics.some(function (c) { return c.category === it.key; });
      }
      var ready = kind === "cosmetic" ? !!it.browse : !!it.apply;
      var applied = kind === "consumable" && it.once && ready && usedSet()[it.key];
      var action = !ready
        ? '<span class="wtp-bw__soon">Soon</span>'
        : applied
          ? '<span class="wtp-bw__applied">✓ Applied</span>'
        : kind === "cosmetic"
          ? '<button type="button" class="wtp-bw__btn wtp-bw__btn--outline" data-kind="cosmetic" data-key="' +
            esc(it.key) + '">Choose ›</button>'
          : '<button type="button" class="wtp-bw__btn" data-kind="consumable" data-key="' +
            esc(it.key) + '">' + it.cost + " cr</button>";
      // v80: a cosmetic row opens its shop from anywhere on the row, as in the mockup.
      var rowAttr = kind === "cosmetic" && ready ? ' wtp-bw__row--link" data-row-key="' + esc(it.key) : "";
      return '<div class="wtp-bw__row' + rowAttr + '">' +
        '<span class="wtp-bw__ico wtp-bw__ico--' + (kind === "cosmetic" ? "k" : "c") + '">' + (it.icon || "") + "</span>" +
        '<div class="wtp-bw__info"><div class="wtp-bw__name">' + esc(it.label) +
        (owned ? ' <span class="wtp-bw__owned">✓ owned</span>' : "") + "</div>" +
        '<div class="wtp-bw__alt">' + esc(it.alt) + "</div></div>" + action + "</div>";
    }

    function renderRows() {
      if (!trayEl) return;
      var st = null;
      try { st = wtpWallet.stateSync(); } catch (e) {}
      var bal = Number((st && st.wallet) || 0);
      var bodyEl = trayEl.querySelector(".wtp-bw__body");
      var balEl = trayEl.querySelector(".wtp-bw__balance");
      if (balEl) {
        balEl.innerHTML = WTP_COIN_ICON + "<span>" + ((st && st.unlimited)
          ? "∞ Unlimited — every add-on included"
          : bal + (bal === 1 ? " credit available" : " credits available")) + "</span>";
      }
      if (!bodyEl) return;
      var stash = Number((st && st.stash) || 0), cap = Number((st && st.stash_cap) || 20);
      bodyEl.innerHTML =
        '<button type="button" class="wtp-bw__stash">' + stashRingHtml(stash, cap) +
          '<div class="wtp-bw__stash-txt"><div class="t">Stash Box — ' +
          (stash >= cap ? "Box full (" + stash + "/" + cap + ")" : stash + "/" + cap + " Locked Credits") +
          "</div>" +
          '<div class="s">Earned free by finishing puzzles. Tap to view.</div></div>' +
          '<span class="wtp-bw__chev" aria-hidden="true">›</span></button>' +
        '<div class="wtp-bw__section"><div class="wtp-bw__label wtp-bw__label--c">' + WTP_BOLT_ICON +
          "Consumables — this puzzle only</div>" +
          ITEMS.consumable.map(function (i) { return rowHtml(i, "consumable", st); }).join("") +
        "</div>" +
        '<div class="wtp-bw__section"><div class="wtp-bw__label wtp-bw__label--k">' + WTP_SPARK_ICON +
          "Cosmetics — owned forever</div>" +
          ITEMS.cosmetic.map(function (i) { return rowHtml(i, "cosmetic", st); }).join("") +
        "</div>";
    }

    async function open() {
      if (trayEl) return close();
      var stage = document.querySelector(".wtp-game__stage");
      if (!stage) return;

      trayEl = el(
        '<div class="wtp-bw">' +
          '<div class="wtp-bw__scrim"></div>' +
          '<div class="wtp-bw__panel" role="dialog" aria-label="Bells and Whistles">' +
            '<div class="wtp-bw__head"><h3>Bells &amp; Whistles</h3>' +
            '<button type="button" class="wtp-bw__close" aria-label="Close">×</button></div>' +
            '<div class="wtp-bw__balrow"><div class="wtp-bw__balance">…</div>' +
            '<button type="button" class="wtp-bw__history" data-open-history>History ›</button></div>' +
            '<div class="wtp-bw__body"></div>' +
            '<button type="button" class="wtp-bw__more">Buy More Credits</button>' +
          "</div>" +
        "</div>");
      stage.appendChild(trayEl);
      // v84 (client report 2026-10-12: "See what Bells & Whistles cost ... doesn't go anywhere"):
      // the tray lives inside the board, and on a laptop the store's banners push the board partly or
      // wholly below the fold, so the tray opened out of sight. Bring the board into view when the
      // tray is not fully visible. On phones the tray is a fixed bottom sheet, which is always in view.
      try {
        var pr = trayEl.querySelector(".wtp-bw__panel").getBoundingClientRect();
        if (pr.top < 0 || pr.bottom > window.innerHeight) {
          stage.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } catch (e) {}
      trayEl.querySelector(".wtp-bw__scrim").addEventListener("click", close);
      trayEl.querySelector(".wtp-bw__close").addEventListener("click", close);
      trayEl.querySelector(".wtp-bw__more").addEventListener("click", function () {
        close(); wtpWallet.buyCredits();
      });
      trayEl.addEventListener("click", function (e) {
        var s = e.target.closest(".wtp-bw__stash");
        if (s) { close(); return wtpWallet.stash(); }
        var b = e.target.closest(".wtp-bw__btn");
        var kind, key;
        if (b) { kind = b.getAttribute("data-kind"); key = b.getAttribute("data-key"); }
        else {
          var row = e.target.closest(".wtp-bw__row--link");     // v80: the whole cosmetic row
          if (!row) return;
          kind = "cosmetic"; key = row.getAttribute("data-row-key");
        }
        var it = (ITEMS[kind] || []).filter(function (x) { return x.key === key; })[0];
        if (!it) return;
        if (kind === "cosmetic") { if (it.browse) it.browse(); return; }
        spendOn(it, b);
      });

      renderRows();
      // Balance may be stale or absent, and "✓ Applied" comes from the server: refresh both,
      // then repaint.
      try { await Promise.all([wtpWallet.state(true), loadUsed()]); } catch (e) {}
      renderRows();
    }

    return { open: open, close: close, register: register, toast: toast, confirm: confirm };
  })();

  // ---- M4: consumable implementations ---------------------------------------
  // Registered into the tray, so they inherit the one spend pipeline (verify ->
  // balance -> server-side spend -> apply -> repaint -> confirm).
  //
  // Grid positions are derived from each piece's SOLVED coordinates, never from
  // piece ids or array order — §11 of the context file: headbreaker + Konva 6
  // order proved unreliable, which is why WtpClassicOutline does the same thing.
  (function registerConsumables() {

    // Paint the toolbar progress readout from module scope (the live updater
    // lives in renderPlay's closure and is not reachable from here).
    function paintProgress() {
      try {
        var p = progress();
        if (!p[1]) return;
        var pct = Math.round((p[0] / p[1]) * 100);
        var pe = document.querySelector(".wtp-progress");
        var pf = document.querySelector(".wtp-progress-fill");
        if (pe) pe.textContent = pct + "%";
        if (pf) pf.style.width = pct + "%";
      } catch (e) {}
    }

    // { index -> {row, col} } for every piece, plus the grid origin.
    function gridMap() {
      var pts = current && current.solved;
      if (!pts || !pts.length) return null;
      var ps = current.pieceSize || 1;
      var minX = Infinity, minY = Infinity;
      pts.forEach(function (p) {
        if (p[0] < minX) minX = p[0];
        if (p[1] < minY) minY = p[1];
      });
      return pts.map(function (p) {
        return {
          row: Math.round((p[1] - minY) / ps),
          col: Math.round((p[0] - minX) / ps),
        };
      });
    }

    function borderIndices() {
      var g = gridMap();
      if (!g) return [];
      var rows = current.rows, cols = current.cols;
      var out = [];
      g.forEach(function (rc, i) {
        if (rc.row === 0 || rc.row === rows - 1 || rc.col === 0 || rc.col === cols - 1) {
          out.push(i);
        }
      });
      return out;
    }

    // Is this piece already sitting where it belongs, relative to the cluster?
    function isPlaced(i) {
      try {
        var pts = readPoints(current.canvas);
        if (!pts || !pts[i] || !current.solved[i]) return false;
        var tol = current.tol || 30;
        // Compare against the MOST COMMON offset (the assembled cluster), so a
        // board that has drifted as a whole still counts as placed.
        var dx = [], dy = [];
        for (var k = 0; k < pts.length; k++) {
          dx.push(pts[k][0] - current.solved[k][0]);
          dy.push(pts[k][1] - current.solved[k][1]);
        }
        var best = 0, bx = 0, by = 0;
        for (var a = 0; a < dx.length; a++) {
          var c = 0;
          for (var b = 0; b < dx.length; b++) {
            if (Math.abs(dx[b] - dx[a]) <= tol && Math.abs(dy[b] - dy[a]) <= tol) c++;
          }
          if (c > best) { best = c; bx = dx[a]; by = dy[a]; }
        }
        // A cluster of ONE is not an assembled cluster - it is just whichever
        // piece happened to be scanned first. Without this guard, a board where
        // nothing is joined yet reports one arbitrary piece as already placed,
        // and Auto-Sort silently skips it. (Caught by the geometry unit test.)
        if (best < 2) return false;
        return Math.abs(dx[i] - bx) <= tol && Math.abs(dy[i] - by) <= tol;
      } catch (e) { return false; }
    }

    // ---- v80: moving pieces the way the mockup does ---------------------------
    // Pieces GLIDE to their targets instead of jumping (Sep 30 doc: Edge Lord "animates
    // all border pieces into place"; the mockup staggers its pieces ~35ms apart with a
    // fast-out ease). Each frame goes through relocateTo(), the engine's own absolute
    // placement, which only moves the piece: no connection is made mid-flight (only
    // autoconnect() does that, once everything has landed). Players who ask the system
    // for reduced motion get the instant move. Resolves when every piece has landed.
    function glide(moves, dur, gap, spread) {
      return new Promise(function (resolve) {
        var cv = current && current.canvas;
        var host = document.getElementById("wtp-canvas");
        var list = moves.filter(function (m) {
          return m.pc && m.to && typeof m.pc.relocateTo === "function";
        }).map(function (m) {
          var a = m.pc.centralAnchor || { x: m.to[0], y: m.to[1] };
          return { pc: m.pc, x0: a.x, y0: a.y, x1: m.to[0], y1: m.to[1] };
        });
        var finished = false;
        function land() {
          if (finished) return;
          finished = true;
          list.forEach(function (m) { try { m.pc.relocateTo(m.x1, m.y1); } catch (e) {} });
          try { cv.redraw(); } catch (e) {}
          if (host) host.style.pointerEvents = "";
          resolve();
        }
        var reduce = false;
        try { reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}
        if (reduce || !list.length || !window.requestAnimationFrame) return land();
        // The whole wave never takes longer than `spread` ms, however many pieces move.
        var step = list.length > 1 ? Math.min(gap, spread / (list.length - 1)) : 0;
        var total = dur + step * (list.length - 1);
        if (host) host.style.pointerEvents = "none";     // no grabbing a piece mid-flight
        function ease(t) { return 1 - Math.pow(1 - t, 3); }   // fast out, soft landing
        var t0 = null;
        function frame(now) {
          if (finished) return;
          if (t0 === null) t0 = now;
          var ms = now - t0;
          list.forEach(function (m, k) {
            var e = ease(Math.max(0, Math.min(1, (ms - k * step) / dur)));
            try { m.pc.relocateTo(m.x0 + (m.x1 - m.x0) * e, m.y0 + (m.y1 - m.y0) * e); } catch (err) {}
          });
          try { cv.redraw(); } catch (e) {}
          if (ms >= total) land(); else requestAnimationFrame(frame);
        }
        requestAnimationFrame(frame);
        // rAF pauses in a background tab: never leave pieces stranded mid-air.
        setTimeout(land, total + 1500);
      });
    }

    // Mockup: a black "⚡ Edge Lord — border auto-placed" badge in the board's corner.
    // Shown for a few seconds, then it fades (a permanent label would sit over the pieces).
    function stageBadge(text) {
      var stage = document.querySelector(".wtp-game__stage");
      if (!stage) return;
      var old = stage.querySelector(".wtp-stage-badge");
      if (old && old.parentNode) old.parentNode.removeChild(old);
      var b = el('<div class="wtp-stage-badge" role="status">' + WTP_BOLT_ICON + "<span>" + esc(text) + "</span></div>");
      stage.appendChild(b);
      requestAnimationFrame(function () { b.classList.add("wtp-stage-badge--in"); });
      setTimeout(function () {
        b.classList.remove("wtp-stage-badge--in");
        setTimeout(function () { if (b.parentNode) b.parentNode.removeChild(b); }, 400);
      }, 4000);
    }

    // Border pieces in walking order round the frame (top row left to right, down the
    // right side, bottom row right to left, up the left side), so the frame builds itself
    // piece by piece rather than in a random scatter.
    function perimeterOrder(idx) {
      var g = gridMap();
      if (!g) return idx;
      var R = current.rows - 1, C = current.cols - 1;
      function pos(i) {
        var r = g[i].row, c = g[i].col;
        if (r === 0) return c;
        if (c === C) return C + r;
        if (r === R) return C + R + (C - c);
        return 2 * C + R + (R - r);
      }
      return idx.slice().sort(function (a, b) { return pos(a) - pos(b); });
    }

    // ---- Edge Lord ----------------------------------------------------------
    // "places the border pieces into position, so the player starts with the
    //  frame finished." v80: they fly in round the frame, then the badge shows.
    wtpBW.register("consumable", "edge_lord", function () {
      if (!current || !current.canvas || !current.solved) throw new Error("no puzzle");
      var pcs = current.canvas.puzzle.pieces;
      var idx = borderIndices();
      if (!idx.length) throw new Error("no border");

      var moves = perimeterOrder(idx).map(function (i) { return { pc: pcs[i], to: current.solved[i] }; });
      return glide(moves, 500, 35, 900).then(function () {
        try { current.canvas.autoconnect(); } catch (e) {}
        try { current.canvas.redraw(); } catch (e) {}
        // The photo mapping must be re-asserted after any bulk move (§12).
        try { if (current.reapplyPhoto) current.reapplyPhoto(); } catch (e) {}
        paintProgress();
        stageBadge("Edge Lord — border auto-placed");
        scheduleSave();                                // a paid border survives a reload

        // On a 2x2 board every piece IS a border piece, so this can finish the
        // puzzle outright. Record it rather than leaving a solved board unscored.
        try { if (isArrangedCorrectly()) recordSolve(); } catch (e) {}
      });
    }, function () {
      // Nothing to place -> nothing charged.
      if (!current || !current.solved) return null;
      var idx = borderIndices();
      return idx.length && idx.every(isPlaced) ? "Your border is already finished." : null;
    });

    // ---- Cheat Peek ---------------------------------------------------------
    // Sep 30 doc: "The small reference picture stays visible at all times, as it does
    // today. Spending 1 credit enlarges it to exactly the size and position of the
    // board, slightly transparent so the pieces show through, with a 5-second
    // countdown before it shrinks back." The grow/shrink is the client's Reveal
    // example (the reference card scales out to the board and back, .45s
    // cubic-bezier(.2,.9,.3,1)); the ring is the mockup's.

    // The board's rectangle inside the stage: where the solved picture sits (the solved
    // centres span the grid and each piece is pieceSize square), times the zoom, measured
    // from the canvas element's corner. Null when it can't be measured.
    function boardRectIn(stage) {
      var pts = current && current.solved, ps = current && current.pieceSize;
      var cvs = document.querySelector("#wtp-canvas .konvajs-content") || document.querySelector("#wtp-canvas canvas");
      if (!pts || !pts.length || !ps || !cvs) return null;
      var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      pts.forEach(function (p) {
        if (p[0] < minX) minX = p[0];
        if (p[1] < minY) minY = p[1];
        if (p[0] > maxX) maxX = p[0];
        if (p[1] > maxY) maxY = p[1];
      });
      var z = playZoom || 1;
      var c = cvs.getBoundingClientRect(), s = stage.getBoundingClientRect();
      var r = {
        left: c.left - s.left - stage.clientLeft + (minX - ps / 2) * z,
        top: c.top - s.top - stage.clientTop + (minY - ps / 2) * z,
        width: (maxX - minX + ps) * z,
        height: (maxY - minY + ps) * z,
      };
      return r.width > 20 && r.height > 20 ? r : null;
    }

    wtpBW.register("consumable", "cheat_peek", function () {
      var stage = document.querySelector(".wtp-game__stage");
      var ref = document.querySelector(".wtp-preview-img");
      if (!stage || !ref || !ref.src) throw new Error("no reference image");

      var old = stage.querySelector(".wtp-peek");
      if (old && old.parentNode) old.parentNode.removeChild(old);

      var SECS = 5, CIRC = 113.1;
      var peek = el(
        '<div class="wtp-peek">' +
          '<img class="wtp-peek__img" alt="full picture">' +
          '<div class="wtp-peek__ring">' +
            '<svg width="44" height="44" viewBox="0 0 44 44">' +
              '<circle cx="22" cy="22" r="18" fill="none" stroke="#EFEFEF" stroke-width="4"></circle>' +
              '<circle class="wtp-peek__arc" cx="22" cy="22" r="18" fill="none" stroke="#E14F97" ' +
              'stroke-width="4" stroke-linecap="round" stroke-dasharray="' + CIRC + '" ' +
              'stroke-dashoffset="0"></circle>' +
            "</svg>" +
            '<span class="wtp-peek__num">' + SECS + "</span>" +
          "</div>" +
        "</div>");
      peek.querySelector(".wtp-peek__img").src = ref.src;

      // Exactly over the board, growing out of the reference picture. If either can't be
      // measured (hidden reference, no board yet) it falls back to the v79 full-stage fade.
      var box = boardRectIn(stage), rr = ref.getBoundingClientRect(), shrunk = null;
      if (box) {
        peek.classList.add("wtp-peek--board");
        peek.style.left = box.left + "px";
        peek.style.top = box.top + "px";
        peek.style.width = box.width + "px";
        peek.style.height = box.height + "px";
        if (rr.width > 4 && rr.height > 4) {
          var sr = stage.getBoundingClientRect();
          shrunk = "translate(" + (rr.left - sr.left - stage.clientLeft - box.left).toFixed(1) + "px," +
            (rr.top - sr.top - stage.clientTop - box.top).toFixed(1) + "px) scale(" +
            (rr.width / box.width).toFixed(4) + "," + (rr.height / box.height).toFixed(4) + ")";
          peek.style.transform = shrunk;
        }
      }
      stage.appendChild(peek);
      void peek.getBoundingClientRect();              // commit the start state before animating
      peek.classList.add("wtp-peek--in");
      if (shrunk) peek.style.transform = "none";

      var arc = peek.querySelector(".wtp-peek__arc");
      var num = peek.querySelector(".wtp-peek__num");
      arc.style.transition = "none";
      arc.style.strokeDashoffset = "0";
      void arc.getBoundingClientRect();           // force reflow before animating
      arc.style.transition = "stroke-dashoffset " + SECS + "s linear";
      arc.style.strokeDashoffset = String(CIRC);

      var left = SECS;
      var tick = setInterval(function () {
        left -= 1;
        if (left <= 0) clearInterval(tick);
        else num.textContent = left;
      }, 1000);

      setTimeout(function () {
        clearInterval(tick);
        peek.classList.remove("wtp-peek--in");
        if (shrunk) { peek.classList.add("wtp-peek--back"); peek.style.transform = shrunk; }
        setTimeout(function () {
          if (peek.parentNode) peek.parentNode.removeChild(peek);
        }, 480);
      }, SECS * 1000);
    });

    // ---- Auto-Sort Edge Pieces ---------------------------------------------
    // NOTE FOR REVIEWERS: the client's doc describes this as "snaps all edge
    // pieces into place", which is word-for-word what Edge Lord already does —
    // two 1-credit items doing the same thing. Built here as SORTING (gather the
    // still-unplaced edge pieces into a tidy band so they are easy to find),
    // which matches the item's name and the prototype's animation, and keeps the
    // two purchases meaningfully different. Flagged for client confirmation.
    // v80: the pieces glide into the band (the mockup's .7s, ~18ms apart).
    function looseEdges() { return borderIndices().filter(function (i) { return !isPlaced(i); }); }
    wtpBW.register("consumable", "auto_sort", function () {
      if (!current || !current.canvas || !current.solved) throw new Error("no puzzle");
      var pcs = current.canvas.puzzle.pieces;
      var loose = looseEdges();
      if (!loose.length) throw new Error("nothing to sort");

      var host = document.getElementById("wtp-canvas");
      var W = (host && host.clientWidth) || 900;
      var ps = current.pieceSize || 60;
      // Spacing must exceed the engine's connect proximity (max(18, 0.5*P)) or
      // neighbours would immediately snap to each other while being laid out.
      var cell = Math.round(ps * 1.5);
      var pad = Math.round(ps * 0.7);
      var perRow = Math.max(1, Math.floor((W - pad * 2) / cell));

      var moves = loose.map(function (i, n) {
        return { pc: pcs[i], to: [pad + (n % perRow) * cell + cell / 2, pad + Math.floor(n / perRow) * cell + cell / 2] };
      });
      return glide(moves, 700, 18, 500).then(function () {
        try { current.canvas.redraw(); } catch (e) {}
        try { if (current.reapplyPhoto) current.reapplyPhoto(); } catch (e) {}
        paintProgress();
        scheduleSave();
      });
    }, function () {
      // Nothing to sort -> nothing charged.
      if (!current || !current.solved) return null;
      return looseEdges().length ? null : "Every edge piece is already in place — nothing to sort.";
    });
  })();

  // ---- M5: cosmetics --------------------------------------------------------
  // One data-driven catalogue for all four categories (`cosmetic_items`), laid out
  // like the client's prototype and bought/equipped through the wallet RPCs. The
  // SERVER is the authority on availability and price: it refuses unavailable
  // items and never charges for a default or an already-owned item.

  // Confetti Cannon: every style is drawn from its `spec` as CSS particles — the
  // prototype's technique — so it needs no artwork and works today.
  var wtpConfetti = (function () {
    var FALLBACK = ["#E14F97", "#F5C518", "#3B82F6", "#22C55E"];

    // The particle recipe, kept separate from the DOM so it can be unit-tested.
    function particle(spec) {
      spec = spec || {};
      // M5b: the client's PNG particles (1-3 variations per style), drawn small.
      var images = spec.images && spec.images.length ? spec.images : null;
      var glyphs = spec.glyphs && spec.glyphs.length ? spec.glyphs : null;
      var colors = spec.colors && spec.colors.length ? spec.colors : FALLBACK;
      var p = {
        left: Math.random() * 100,                   // % across the viewport
        dx0: Math.random() * 40 - 20,                // px drift at mid-fall
        dx1: Math.random() * 220 - 110,              // px drift at landing
        spin: 360 + Math.random() * 540,             // deg
        dur: 1.8 + Math.random() * 1.4,              // s
        delay: Math.random() * 0.4,                  // s
      };
      if (images) {
        p.kind = "img";
        p.src = images[Math.floor(Math.random() * images.length)];
        p.size = 18 + Math.random() * 16;            // px; art is 256-512 px, scaled down
        return p;
      }
      if (glyphs) {
        p.kind = "glyph";
        p.glyph = glyphs[Math.floor(Math.random() * glyphs.length)];
        p.font = 14 + Math.random() * 12;
        return p;
      }
      var shape = spec.shape || "mixed";
      p.kind = shape === "mixed" ? (Math.random() < 0.4 ? "circle" : "rect") : shape;
      p.color = colors[Math.floor(Math.random() * colors.length)];
      var size = 6 + Math.random() * 8;
      if (p.kind === "circle") { p.w = size; p.h = size; p.r = "50%"; }
      else if (p.kind === "ribbon") { p.w = 3 + Math.random() * 3; p.h = 18 + Math.random() * 16; p.r = "2px"; }
      else { p.kind = "rect"; p.w = size; p.h = size * 1.5; p.r = "2px"; }
      return p;
    }

    // v78: the mockup's randomized congratulations banner, shown with the WIN burst only
    // (Victory Confetti example: `const lines = [...]`, ink pill, bannerPop 2.6s).
    var WIN_LINES = ["YOU FILTHY GENIUS", "NAILED IT", "PUZZLE: ANNIHILATED", "DAMN, LOOK AT YOU"];
    function winLine() { return WIN_LINES[Math.floor(Math.random() * WIN_LINES.length)]; }

    function burst(spec, bannerText) {
      spec = spec || {};
      var field = document.createElement("div");
      field.className = "wtp-confetti";
      field.setAttribute("aria-hidden", "true");
      if (bannerText) {
        var bn = document.createElement("div");
        bn.className = "wtp-confetti__banner";
        bn.textContent = bannerText;
        field.appendChild(bn);
      }
      var count = (spec.glyphs && spec.glyphs.length) || (spec.images && spec.images.length) ? 46 : 80;
      for (var i = 0; i < count; i++) {
        var d = particle(spec);
        var n = document.createElement(d.kind === "img" ? "img" : "span");
        n.className = "wtp-confetti__p" + (d.kind === "glyph" ? " wtp-confetti__p--glyph" :
          d.kind === "img" ? " wtp-confetti__p--img" : "");
        n.style.left = d.left + "%";
        n.style.setProperty("--dx0", d.dx0 + "px");
        n.style.setProperty("--dx1", d.dx1 + "px");
        n.style.setProperty("--spin", d.spin + "deg");
        n.style.animationDuration = d.dur + "s";
        n.style.animationDelay = d.delay + "s";
        if (d.kind === "img") {
          n.src = d.src;
          n.alt = "";
          n.style.width = d.size + "px";
        } else if (d.kind === "glyph") {
          n.textContent = d.glyph;
          n.style.fontSize = d.font + "px";
        } else {
          n.style.background = d.color;
          n.style.width = d.w + "px";
          n.style.height = d.h + "px";
          n.style.borderRadius = d.r;
        }
        field.appendChild(n);
      }
      document.body.appendChild(field);
      // Longest particle: 3.2s fall + 0.4s delay. Then the layer is removed.
      setTimeout(function () { if (field.parentNode) field.parentNode.removeChild(field); }, 4000);
    }
    return { burst: burst, particle: particle, winLine: winLine, lines: WIN_LINES };
  })();

  var wtpCosmetics = (function () {
    // Text and icons are the client's prototype CATEGORY_META, word for word, except Foil:
    // v88 follows the client's "Foil Finish Gallery" (a frame + light sweep + plaque on the
    // finished puzzle), which replaced the per-piece texture idea (client, 2026-10-20).
    var SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">';
    var META = {
      sound: { title: "Snap Pack", noun: "sounds", one: "sound", bg: "#EDE9FB", fg: "#7A5FD1",
        sub: "Pick the sound your pieces make when they lock into place. Tap ▶ to preview — each one’s yours forever once bought.",
        icon: SVG + '<path d="M11 5 6 9H2v6h4l5 4V5Z"></path><path d="M15.5 8.5a5 5 0 0 1 0 7"></path>' +
          '<path d="M18.5 6a9 9 0 0 1 0 12"></path></svg>' },
      foil: { title: "Gold Foil Finish", noun: "finishes", one: "finish", bg: "#F5EBCB", fg: "#B8860B",
        sub: "Pick the foil frame your finished puzzle gets: a metallic border, a light sweep and a “Completed in” plaque, every time you finish. Tap a swatch to preview. Every finish is yours forever once bought.",
        icon: SVG + '<path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4L12 2Z"></path></svg>' },
      confetti: { title: "Confetti Cannon", noun: "styles", one: "style", bg: "#FCEBD8", fg: "#B8730E",
        sub: "Pick what explodes across the screen when you finish a puzzle. Every style is yours forever once bought.",
        icon: SVG + '<path d="M4 20l4-1 9-9-3-3-9 9-1 4Z"></path><path d="M14 5l1 1M18 9l1 1M17 4l.5 1.5M20 7.5l1.5.5"></path></svg>' },
      cursor: { title: "Custom Cursor", noun: "skins", one: "skin", bg: "#E3F2FD", fg: "#1E88C7",
        sub: "Swap the little arrow you drag pieces with. Every skin is yours forever once bought.",
        icon: SVG + '<path d="M4 4l7 17 2-7 7-2L4 4Z"></path></svg>' },
    };
    var PACK_COST = 7;   // client-confirmed: 1 cr each, or 7 cr for the set of 10

    var catPromise = null;
    function catalogue() {
      if (catPromise) return catPromise;
      catPromise = (async function () {
        if (!sb) await sbReady;
        var res = await sb.from("cosmetic_items")
          .select("category, item_id, label, description, sort_order, is_default, is_available, asset_url, spec")
          .order("sort_order", { ascending: true });
        if (res && res.error) throw res.error;
        return res && res.data ? res.data : [];
      })().catch(function () { catPromise = null; return []; });
      return catPromise;
    }

    function itemsOf(all, cat) {
      return all.filter(function (x) { return x.category === cat; });
    }

    // What the player owns and has equipped. The free default is always owned,
    // and is what's equipped until they choose something else.
    function ownership(all, cat, st) {
      var owned = {};
      itemsOf(all, cat).forEach(function (x) { if (x.is_default) owned[x.item_id] = true; });
      ((st && st.cosmetics) || []).forEach(function (c) {
        if (c.category === cat) owned[c.item] = true;
      });
      var equipped = (st && st.equipped && st.equipped[cat]) || null;
      if (!equipped || !owned[equipped]) {
        var d = itemsOf(all, cat).filter(function (x) { return x.is_default; })[0];
        equipped = d ? d.item_id : null;
      }
      return { owned: owned, equipped: equipped };
    }

    // The bundle card: price and saving computed from what is actually left, so
    // it never promises a saving that isn't real. Matches the server's cap.
    function packOffer(items, own) {
      var buyable = items.filter(function (x) { return x.is_available && !own.owned[x.item_id]; });
      var ready = items.length > 0 && items.every(function (x) { return x.is_available; });
      var cost = Math.min(PACK_COST, buyable.length);
      return { ready: ready, buyable: buyable, cost: cost, old: buyable.length,
               save: buyable.length - cost };
    }

    // M5b: what an item looks/sounds like = its spec + its delivered file. A file is either
    // a full https URL or (v75) the NAME of a file shipped in this theme's assets, e.g.
    // "wtp-snd-burp.mp3", resolved against #wtp-play[data-asset-base]. Either way only
    // URLs without quotes/brackets/spaces are accepted (they go into CSS and media).
    function safeUrl(u) {
      if (typeof u !== "string") return null;
      if (/^[A-Za-z0-9][A-Za-z0-9_.-]*\.(mp3|png|svg|webp|gif)$/.test(u) && u.indexOf("..") < 0) {
        var base = (typeof root !== "undefined" && root && root.dataset.assetBase) || "";
        if (!/^(https:)?\/\/[^"'()\s<>]+\/$/.test(base)) return null;
        u = (base.indexOf("//") === 0 ? "https:" : "") + base + u;
      }
      return /^https:\/\/[^"'()\s<>]+$/.test(u) ? u : null;
    }
    function lookOf(it) {
      if (!it) return null;
      var sp = {}, src = it.spec || {};
      Object.keys(src).forEach(function (k) { sp[k] = src[k]; });
      var url = safeUrl(it.asset_url);
      if (url) sp.url = url;
      if (sp.url2x) sp.url2x = safeUrl(sp.url2x);
      if (sp.images) sp.images = [].concat(sp.images).map(safeUrl).filter(Boolean);
      if (it.category === "confetti" && url && !(sp.images && sp.images.length)) sp.images = [url];
      return sp;
    }

    // Spec of the equipped style in a category, or null. Never needs a sign-in:
    // an unverified player simply gets the free default.
    async function equippedSpec(cat) {
      var all = await catalogue();
      var st = null;
      try { st = await wtpWallet.state(false); } catch (e) {}
      var own = ownership(all, cat, st);
      var it = itemsOf(all, cat).filter(function (x) { return x.item_id === own.equipped; })[0];
      return it && it.is_available ? lookOf(it) : null;
    }

    function swatchHtml(cat, it) {
      var sp = it.spec || {};
      var look = lookOf(it);
      if (cat === "foil") {                    // v88: tap to preview the finish (free, never charges)
        var bg = sp.swatch && sp.swatch.length && sp.swatch.every(wtpFoil.ok)
          ? "background:linear-gradient(135deg," + sp.swatch.join(",") + ");" : "";
        var ring = wtpFoil.ok(sp.border) ? "--wtp-foil-ring:" + sp.border + ";" : "";
        return '<button type="button" class="wtp-cos__swatch wtp-cos__swatch--foil" data-act="preview" data-id="' +
          esc(it.item_id) + '" aria-label="Preview ' + esc(it.label) + '" style="' + esc(bg + ring) + '">' +
          (bg ? "" : '<span style="color:' + META.foil.fg + '">' + META.foil.icon + "</span>") + "</button>";
      }
      var art = cat === "cursor" ? look.url : cat === "confetti" && look.images && look.images.length ? look.images[0] : null;
      if (art) {
        return '<span class="wtp-cos__swatch wtp-cos__swatch--img"><img src="' + esc(art) + '" alt=""></span>';
      }
      if (sp.glyphs && sp.glyphs.length) {
        return '<span class="wtp-cos__swatch wtp-cos__swatch--glyph">' + esc(sp.glyphs[0]) + "</span>";
      }
      if (cat === "confetti" && sp.colors) {
        // Four dots, as in the prototype's swatches.
        var pos = [[4, 4], [18, 3], [6, 17], [19, 18]];
        return '<span class="wtp-cos__swatch">' + pos.map(function (xy, k) {
          return '<i style="left:' + xy[0] + "px;top:" + xy[1] + "px;background:" +
            esc(sp.colors[k % sp.colors.length]) + '"></i>';
        }).join("") + "</span>";
      }
      if (sp.swatch && sp.swatch.length) {
        return '<span class="wtp-cos__swatch" style="background:linear-gradient(135deg,' +
          sp.swatch.map(esc).join(",") + ')"></span>';
      }
      return '<span class="wtp-cos__swatch wtp-cos__swatch--icon" style="color:' + META[cat].fg + '">' +
        META[cat].icon + "</span>";
    }

    // v80: Snap Pack rows as in the PDF mockup (p.8) — a round black ▶ on the LEFT of each
    // row (in the swatch's place) and a small sound-wave under the name. A sound with no
    // file yet keeps the same spot as a dimmed, disabled button so the rows line up.
    function previewHtml(cat, it) {
      var url = cat === "sound" && it.is_available ? lookOf(it).url : null;
      return '<button type="button" class="wtp-cos__play"' + (url ? ' data-act="preview"' : " disabled") +
        ' data-id="' + esc(it.item_id) + '" aria-label="Preview ' + esc(it.label) + '">' +
        '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2.5 1.2v7.6L8.6 5z" fill="currentColor"></path></svg></button>';
    }
    // Seven bars, as in the mockup. Decorative, but each sound gets its own steady shape
    // (seeded from its id) so the list doesn't look copy-pasted.
    function waveHtml(it) {
      var id = String(it.item_id || ""), h = 0, bars = "";
      for (var i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
      for (var k = 0; k < 7; k++) {
        h = (h * 1103515245 + 12345) >>> 0;
        bars += '<i style="height:' + (4 + (h >>> 16) % 11) + 'px"></i>';
      }
      return '<span class="wtp-cos__wave" aria-hidden="true">' + bars + "</span>";
    }

    function ctrlHtml(it, own) {
      if (!it.is_available) return '<span class="wtp-cos__soon">Soon</span>';
      if (own.equipped === it.item_id) return '<span class="wtp-cos__on">✓ Equipped</span>';
      var id = ' data-id="' + esc(it.item_id) + '"';
      if (own.owned[it.item_id]) return '<button type="button" class="wtp-cos__equip" data-act="equip"' + id + ">Equip</button>";
      return '<button type="button" class="wtp-cos__price" data-act="buy"' + id + ">1 cr</button>";
    }

    function bundleHtml(meta, items, offer) {
      // v79: a "none" row (Custom Cursor's free "Normal pointer") isn't one of the set —
      // the cursor bundle reads "Get all 10 skins", as in the doc ("7 credits for the full set of 10").
      var count = items.filter(function (x) { return !(x.spec && x.spec.none); }).length;
      var title = '<div class="t">Get all ' + count + " " + meta.noun + "</div>";
      if (!offer.ready) {
        return '<div class="wtp-cos__bundle wtp-cos__bundle--off"><div class="wtp-cos__btext">' + title +
          '<div class="s">Coming soon — we’re finishing these now.</div></div>' +
          '<button type="button" class="wtp-cos__pack" disabled>Soon</button></div>';
      }
      if (!offer.buyable.length) {
        return '<div class="wtp-cos__bundle"><span class="wtp-cos__badge">Best Value</span>' +
          '<div class="wtp-cos__btext">' + title +
          '<div class="s">Switch between them any time, no extra cost.</div></div>' +
          '<button type="button" class="wtp-cos__pack" disabled>All Owned ✓</button></div>';
      }
      var sub = offer.save > 0
        ? '<s>' + offer.old + " cr</s>Buy the full pack and save " + offer.save +
          " credit" + (offer.save === 1 ? "" : "s")
        : "Unlock every remaining " + meta.one + " in one tap";
      return '<div class="wtp-cos__bundle"><span class="wtp-cos__badge">Best Value</span>' +
        '<div class="wtp-cos__btext">' + title + '<div class="s">' + sub + "</div></div>" +
        '<button type="button" class="wtp-cos__pack" data-act="pack">Buy Pack · ' +
        offer.cost + " cr</button></div>";
    }

    function open(cat) {
      var meta = META[cat];
      if (!meta) return;
      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal wtp-modal--cos" role="dialog" aria-modal="true" aria-label="' + esc(meta.title) + '">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
            '<div class="wtp-cos__head"><span class="wtp-cos__icon" style="background:' + meta.bg +
            ";color:" + meta.fg + '">' + meta.icon + "</span><h2>" + esc(meta.title) + "</h2></div>" +
            '<p class="wtp-cos__sub">' + esc(meta.sub) + "</p>" +
            '<div class="wtp-cos__body"><p class="wtp-cos__sub">Loading…</p></div>' +
          "</div>" +
        "</div>");
      document.body.appendChild(overlay);
      function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });

      var busy = false;
      var body = overlay.querySelector(".wtp-cos__body");
      var previewId = null;                    // v88: the finish shown in the foil preview frame

      // v88: the client's gallery in small. A board that wears the previewed finish, with
      // the plaque, and its name in the finish's colour.
      function foilDemo() {
        var box = body.querySelector(".wtp-foil-demo");
        if (!box) return;
        var items = body._items || [], own = body._own || { equipped: null };
        var paid = items.filter(function (x) { return !(x.spec && x.spec.none); });
        if (!previewId) previewId = own.equipped && paid.some(function (x) { return x.item_id === own.equipped; })
          ? own.equipped : (paid[0] && paid[0].item_id);
        var it = items.filter(function (x) { return x.item_id === previewId; })[0];
        var look = it ? lookOf(it) : null;
        var on = wtpFoil.mount(box.querySelector(".wtp-foil-demo__board"), look, "04:12");
        var name = box.querySelector(".wtp-foil-demo__name");
        name.textContent = it ? (on ? it.label : it.label + " (no foil)") : "";
        name.style.color = on ? look.border : "";
        [].forEach.call(body.querySelectorAll(".wtp-cos__swatch--foil"), function (b) {
          b.classList.toggle("is-on", b.getAttribute("data-id") === previewId);
        });
      }

      async function render(force) {
        var all = await catalogue();
        var st = null;
        try { st = await wtpWallet.state(!!force); } catch (e) {}
        var items = itemsOf(all, cat);
        if (!items.length) {
          body.innerHTML = '<p class="wtp-cos__sub">Couldn’t load these right now — please try again.</p>';
          return;
        }
        var own = ownership(all, cat, st);
        var offer = packOffer(items, own);
        body.innerHTML = (cat === "foil"
            ? '<div class="wtp-foil-demo"><div class="wtp-foil-demo__board"></div>' +
              '<div class="wtp-foil-demo__name"></div></div>' : "") +
          bundleHtml(meta, items, offer) +
          '<div class="wtp-cos__divider">or pick individually</div>' +
          '<div class="wtp-cos__list">' + items.map(function (it) {
            if (cat === "sound") {                     // v80: ▶ left, wave under the name
              return '<div class="wtp-cos__row wtp-cos__row--snd" title="' + esc(it.description || "") + '">' +
                previewHtml(cat, it) +
                '<div class="wtp-cos__info"><div class="n">' + esc(it.label) + "</div>" + waveHtml(it) + "</div>" +
                ctrlHtml(it, own) + "</div>";
            }
            return '<div class="wtp-cos__row">' + swatchHtml(cat, it) +
              '<div class="wtp-cos__info"><div class="n">' + esc(it.label) + "</div>" +
              '<div class="d">' + esc(it.description || "") + "</div></div>" +
              ctrlHtml(it, own) + "</div>";
          }).join("") + "</div>" +
          '<p class="wtp-cos__foot">One ' + meta.one + " equipped at a time · switch anytime, no extra cost</p>";
        body._offer = offer;
        body._items = items;
        body._own = own;
        if (cat === "foil") foilDemo();
      }

      body.addEventListener("click", async function (e) {
        var b = e.target.closest("[data-act]");
        if (!b || b.disabled || busy) return;
        var act = b.getAttribute("data-act"), id = b.getAttribute("data-id");
        if (act === "preview") {               // free, no sign-in, never charges
          var pit = (body._items || []).filter(function (x) { return x.item_id === id; })[0];
          if (pit && cat === "foil") { previewId = id; foilDemo(); return; }
          if (pit) wtpAudio.preview(lookOf(pit).url);
          return;
        }
        busy = true;
        var label = b.textContent;
        b.disabled = true; b.textContent = "…";
        try {
          // Verifying may swap in its own prompt; put the catalogue back after.
          var sess = await wtpWallet.ensure();
          if (!overlay.parentNode) document.body.appendChild(overlay);
          if (!sess) { b.disabled = false; b.textContent = label; return; }

          var res;
          if (act === "equip") {
            res = await wtpWallet.call("wallet_equip", { p_category: cat, p_item: id });
            if (res && res.ok) wtpBW.toast("Equipped.");
          } else {
            var offer = body._offer;
            var list = act === "pack" ? offer.buyable.map(function (x) { return x.item_id; }) : [id];
            var cost = act === "pack" ? offer.cost : 1;
            var st = await wtpWallet.state(true);
            if (!(st && st.unlimited) && Number((st && st.wallet) || 0) < cost) {
              close();
              wtpBW.toast("You need " + cost + " credit" + (cost === 1 ? "" : "s") + " for that.", true);
              return wtpWallet.buyCredits();       // don't dead-end: offer to top up
            }
            // v78: ask first — nothing is charged unless the player confirms.
            var unl = !!(st && st.unlimited), have = Number((st && st.wallet) || 0);
            var one = act === "pack" ? null : body._items.filter(function (x) { return x.item_id === id; })[0];
            var go = await wtpBW.confirm({
              title: act === "pack" ? "Unlock all " + list.length + " " + meta.noun + "?"
                : "Unlock “" + (one ? one.label : meta.one) + "”?",
              text: "Yours forever — switch between your " + meta.noun + " any time, free.",
              note: unl ? "Included with Unlimited"
                : cost + " credit" + (cost === 1 ? "" : "s") + " · you have " + have + " credit" + (have === 1 ? "" : "s"),
              ok: unl ? "Unlock it" : "Unlock for " + cost + " credit" + (cost === 1 ? "" : "s"),
            });
            if (!go) { b.disabled = false; b.textContent = label; return; }
            res = await wtpWallet.call("wallet_buy_cosmetics", { p_category: cat, p_items: list, p_cost: cost });
            if (res && res.ok) {
              var spent = Number(res.charged != null ? res.charged : cost);
              var paid = (st && st.unlimited) ? "" : " — " + spent + " credit" + (spent === 1 ? "" : "s") + " spent";
              if (act === "buy") {
                // Picking one equips it, exactly like the prototype's selectCosmetic.
                await wtpWallet.call("wallet_equip", { p_category: cat, p_item: id });
                wtpBW.toast("Unlocked and equipped" + paid + ".");
                var it = body._items.filter(function (x) { return x.item_id === id; })[0];
                if (it && cat === "confetti") wtpConfetti.burst(lookOf(it));   // show them what they bought
                if (it && cat === "sound") wtpAudio.preview(lookOf(it).url);  // ...or let them hear it
                if (it && cat === "foil") previewId = id;                     // ...or see it on the frame
              } else {
                // The pack keeps the current choice — buying all shouldn't change the look.
                wtpBW.toast("All " + meta.noun + " unlocked" + paid + ".");
              }
            }
          }
          if (!res || !res.ok) {
            var err = res && res.error;
            wtpBW.toast(err === "insufficient" ? "Not enough credits."
              : err === "unavailable" ? "That one isn’t available yet."
              : err === "already_owned" ? "You already own that."
              : err === "price_changed" ? "That price just changed — please check and try again."
              : "That didn’t go through — please try again.", true);
          }
          await render(false);        // call() already refreshed the wallet snapshot
          if (res && res.ok) wtpLooks.apply(cat);   // a new sound / cursor / foil takes effect at once
        } finally { busy = false; }
      });

      render(true);
    }

    return { open: open, equippedSpec: equippedSpec, catalogue: catalogue,
             ownership: ownership, packOffer: packOffer };
  })();

  // M5b: apply the equipped Snap Pack sound and Custom Cursor skin. Pure data: an
  // item takes effect once its file is delivered (asset_url) and it is switched on
  // (is_available); until then the built-in sound and normal pointer stay. Called at
  // puzzle start and after every equip/buy. Failures fall back silently.
  var wtpLooks = (function () {
    function cursorCss(look) {
      if (!look || !look.url) return null;
      var hs = look.hotspot && look.hotspot.length === 2
        ? " " + (Number(look.hotspot[0]) || 0) + " " + (Number(look.hotspot[1]) || 0) : " 0 0";
      return {
        plain: 'url("' + look.url + '")' + hs + ", auto",
        hidpi: look.url2x ? 'image-set(url("' + look.url + '") 1x, url("' + look.url2x + '") 2x)' + hs + ", auto" : null,
      };
    }
    async function apply(cat) {
      try {
        if (!cat || cat === "sound") {
          var s = await wtpCosmetics.equippedSpec("sound");
          wtpAudio.useSnap(s && s.url ? s.url : null);
        }
        if (!cat || cat === "cursor") {
          var c = cursorCss(await wtpCosmetics.equippedSpec("cursor"));
          if (c) {
            var v = c.plain;
            try { if (c.hidpi && window.CSS && CSS.supports("cursor", c.hidpi)) v = c.hidpi; } catch (e) {}
            root.style.setProperty("--wtp-cursor", v);
            root.classList.add("wtp-has-cursor");
          } else {
            root.style.removeProperty("--wtp-cursor");
            root.classList.remove("wtp-has-cursor");
          }
        }
        if (cat === "foil") await wtpFoil.refresh();   // v88
      } catch (e) {}
    }
    return { apply: apply, cursorCss: cursorCss };
  })();

  // v88: Gold Foil Finish, from the client's "Foil Finish Gallery" (Digital Puzzle/Goil Foil
  // Finish Examples.rtf): ten colour treatments of one completion frame + light sweep + a
  // "Completed in mm:ss" plaque. Overlays only, laid over their host: the board's layout and the
  // canvas are never touched (§11/§12). Holo finishes turn the hue of the overlays only, so the
  // photo keeps its true colours. Colours come from cosmetic_items.spec and are checked first:
  // a bad value means no foil, never broken markup.
  var wtpFoil = (function () {
    var VARS = { border: "--wtp-foil-border", glow: "--wtp-foil-glow", shimmer: "--wtp-foil-shimmer",
                 plaqueBg: "--wtp-foil-plaque-bg", plaqueText: "--wtp-foil-plaque-text" };
    var lastTime = "";
    function ok(v) { return typeof v === "string" && v.length > 0 && v.length <= 600 && /^[#a-z0-9(),.%\s-]+$/i.test(v); }
    function usable(look) {
      return !!(look && !look.none && Object.keys(VARS).every(function (k) { return ok(look[k]); }));
    }
    function clear(host) {
      if (!host) return;
      for (var i = host.children.length - 1; i >= 0; i--) {
        if (host.children[i].classList.contains("wtp-foil")) host.removeChild(host.children[i]);
      }
      host.classList.remove("wtp-has-foil");
      Object.keys(VARS).forEach(function (k) { host.style.removeProperty(VARS[k]); });
    }
    // Dress `host` in a finish. timeText adds the plaque. Returns the overlay, or null.
    function mount(host, look, timeText) {
      clear(host);
      if (!host || !usable(look)) return null;
      Object.keys(VARS).forEach(function (k) { host.style.setProperty(VARS[k], look[k]); });
      var box = document.createElement("div");
      box.className = "wtp-foil" + (look.holo ? " wtp-foil--holo" : "") + (look.glitter ? " wtp-foil--glitter" : "");
      box.setAttribute("aria-hidden", "true");
      box.innerHTML = '<span class="wtp-foil__frame"></span><span class="wtp-foil__sweep"></span>' +
        (timeText ? '<span class="wtp-foil__plaque"></span>' : "");
      if (timeText) box.querySelector(".wtp-foil__plaque").textContent = "Completed in " + timeText;
      host.appendChild(box);
      host.classList.add("wtp-has-foil");
      return box;
    }
    function stage() { return document.querySelector(".wtp-game__stage"); }
    // A genuine solve: the picture in the completion window, and the board under it.
    async function onSolve(winOverlay, timeText) {
      var look = await wtpCosmetics.equippedSpec("foil");
      if (!usable(look)) return false;
      lastTime = timeText || "";
      var pic = winOverlay && winOverlay.querySelector ? winOverlay.querySelector(".wtp-win__img") : null;
      if (pic) mount(pic, look, lastTime);
      mount(stage(), look, lastTime);
      return true;
    }
    // After a buy/equip: a board that is wearing a finish switches to the new one (or none).
    async function refresh() {
      var st = stage();
      if (!st || !st.classList.contains("wtp-has-foil")) return;
      var look = await wtpCosmetics.equippedSpec("foil");
      if (usable(look)) mount(st, look, lastTime); else clear(st);
    }
    return { ok: ok, usable: usable, mount: mount, clear: clear, onSolve: onSolve, refresh: refresh,
             clearBoard: function () { clear(stage()); } };
  })();

  // v88: frame the finished puzzle with the player's equipped Gold Foil finish, if any.
  function fireWinFoil(winOverlay, timeText) {
    try { wtpFoil.onSolve(winOverlay, timeText).catch(function () {}); } catch (e) {}
  }

  // Light up all four cosmetic rows in the tray.
  ["sound", "foil", "confetti", "cursor"].forEach(function (cat) {
    wtpBW.register("cosmetic", cat, function () { wtpBW.close(); wtpCosmetics.open(cat); });
  });

  // Fire the player's equipped Confetti Cannon style. Purely cosmetic: any
  // failure (offline, catalogue not loaded) falls back to the default mix.
  function fireWinConfetti() {
    try {
      var line = wtpConfetti.winLine();       // v78: random banner from the mockup
      wtpCosmetics.equippedSpec("confetti")
        .then(function (spec) { wtpConfetti.burst(spec, line); })
        .catch(function () { wtpConfetti.burst(null, line); });
    } catch (e) {}
  }

  // ---- M6: completion screen + share reward ---------------------------------
  // Built to the client's mockup (v2 scope PDF, p.10): 100% COMPLETE / Nailed it. /
  // time · difficulty · hints chips / the finished puzzle / Buy This Physical Puzzle
  // / Play More Puzzles / OR / "Show it off +1 credit" with Facebook, Instagram and
  // Snapchat. Client-confirmed: ONE credit per completed puzzle in total, however
  // many platforms are tapped — the server enforces it (wallet_claim_share_credit).

  // Hints used = consumables (Edge Lord, Cheat Peek, Auto-Sort) applied during this
  // game. Kept per puzzle + difficulty in localStorage so a resumed game still
  // counts the hints used before the reload.
  function hintsKey(pid, grid) { return "wtp_hints_" + pid + "_" + grid; }
  function hintsAdd() {
    if (!current) return;
    current.hints = (current.hints || 0) + 1;
    try { localStorage.setItem(hintsKey(current.productId, current.grid), String(current.hints)); } catch (e) {}
  }
  function hintsStart(pid, grid, resumed) {
    var n = 0;
    try {
      if (resumed) n = Number(localStorage.getItem(hintsKey(pid, grid))) || 0;
      else localStorage.removeItem(hintsKey(pid, grid));
    } catch (e) {}
    return n;
  }
  function hintsClear(pid, grid) {
    try { localStorage.removeItem(hintsKey(pid, grid)); } catch (e) {}
  }

  function isMobileDevice() {
    var ua = navigator.userAgent || "";
    return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) ||
      (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);   // iPadOS reports as a Mac
  }
  function puzzleName(title) {
    return String(title || "").replace(/^\s*digital\s*[|:\-]\s*/i, "").trim() || "a puzzle";
  }

  // What a share links to: the PHYSICAL puzzle's page when there is one (its
  // preview card is the artwork, and it is the thing the store sells), otherwise
  // this digital puzzle's play page. UTM-tagged so the client can see share traffic.
  function shareLink(info, platform) {
    var base = info.physicalUrl
      ? location.origin + info.physicalUrl
      : location.origin + "/pages/play?puzzle=" + encodeURIComponent(info.handle || "");
    return base + (base.indexOf("?") < 0 ? "?" : "&") +
      "utm_source=" + platform + "&utm_medium=social&utm_campaign=puzzle_share";
  }
  function shareUrlFor(platform, info) {
    var link = encodeURIComponent(shareLink(info, platform));
    if (platform === "facebook") return "https://www.facebook.com/sharer/sharer.php?u=" + link;
    // Exactly what Snap's own Creative Kit web SDK opens (no app id needed).
    if (platform === "snapchat") return "https://snapchat.com/scan?attachmentUrl=" + link;
    return null;
  }

  // Instagram can't take a link from the web, so it gets a branded 1080px image:
  // the finished artwork, the result, and the store name. Both image hosts
  // (Shopify CDN, Supabase Storage) send Access-Control-Allow-Origin: *, so the
  // canvas stays exportable. Resolves null on any failure — callers fall back.
  function fitText(g, text, max) {
    if (g.measureText(text).width <= max) return text;
    while (text.length > 1 && g.measureText(text + "…").width > max) text = text.slice(0, -1);
    return text + "…";
  }
  function roundedRect(g, x, y, w, h, r) {
    g.beginPath();
    g.moveTo(x + r, y);
    g.arcTo(x + w, y, x + w, y + h, r);
    g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r);
    g.arcTo(x, y, x + w, y, r);
    g.closePath();
  }
  function drawShareCard(img, info) {
    var W = 1080, PAD = 60, IW = W - PAD * 2, TOP = 210;
    var natural = Math.round(IW * img.naturalHeight / img.naturalWidth);
    var IH = Math.max(560, Math.min(1100, natural));        // keep the card postable
    var H = TOP + IH + 250;
    var c = document.createElement("canvas");
    c.width = W; c.height = H;
    var g = c.getContext("2d");
    g.fillStyle = "#FFFFFF"; g.fillRect(0, 0, W, H);
    g.textAlign = "center"; g.textBaseline = "alphabetic";

    g.fillStyle = "#E14F97"; g.font = '700 30px "Poppins", sans-serif';
    try { g.letterSpacing = "4px"; } catch (e) {}
    g.fillText(info.kicker, W / 2, 80);
    try { g.letterSpacing = "0px"; } catch (e) {}
    g.fillStyle = "#1a1a1a"; g.font = '700 78px "Fredoka", "Poppins", sans-serif';
    g.fillText(info.headline, W / 2, 166);

    // The artwork, cover-cropped into its frame with rounded corners.
    var sw = img.naturalWidth, sh = img.naturalHeight, sx = 0, sy = 0, want = IW / IH;
    if (sw / sh > want) { sx = (sw - sh * want) / 2; sw = sh * want; }
    else { sy = (sh - sw / want) / 2; sh = sw / want; }
    g.save(); roundedRect(g, PAD, TOP, IW, IH, 28); g.clip();
    g.drawImage(img, sx, sy, sw, sh, PAD, TOP, IW, IH);
    g.restore();

    g.fillStyle = "#1a1a1a"; g.font = '600 42px "Poppins", sans-serif';
    g.fillText(fitText(g, "I finished " + puzzleName(info.title), IW), W / 2, TOP + IH + 80);
    g.fillStyle = "#6b7280"; g.font = '500 32px "Poppins", sans-serif';
    g.fillText(fitText(g, "⏱ " + info.time + "   ·   " + info.difficulty +
      "   ·   " + info.hintsText, IW), W / 2, TOP + IH + 132);

    g.fillStyle = "#E14F97"; g.fillRect(0, H - 90, W, 90);
    g.fillStyle = "#FFFFFF"; g.font = '700 38px "Fredoka", "Poppins", sans-serif';
    g.fillText("whatthepuzzles.com", W / 2, H - 32);
    return c;
  }
  function buildShareImage(info) {
    return new Promise(function (resolve) {
      if (!info.imageUrl || !window.HTMLCanvasElement) return resolve(null);
      var img = new Image();
      img.crossOrigin = "anonymous";
      img.onerror = function () { resolve(null); };
      img.onload = function () {
        function draw() {
          try {
            drawShareCard(img, info).toBlob(function (b) { resolve(b || null); }, "image/png");
          } catch (e) { resolve(null); }   // e.g. a tainted canvas — fall back
        }
        // Use the page's brand fonts if they arrive quickly; never wait long.
        var fonts = document.fonts && document.fonts.load
          ? Promise.all(['700 78px "Fredoka"', '700 30px "Poppins"', '600 42px "Poppins"', '500 32px "Poppins"']
              .map(function (f) { return document.fonts.load(f); }))
          : Promise.resolve();
        Promise.race([fonts, new Promise(function (r) { setTimeout(r, 1500); })]).then(draw, draw);
      };
      img.src = info.imageUrl;
    });
  }
  function downloadBlob(blob, name) {
    var url = URL.createObjectURL(blob);
    var a = document.createElement("a");
    a.href = url; a.download = name; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.parentNode.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 30000);
  }

  var WIN_ICONS = {
    clock: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
      'stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"></circle><path d="M12 9v4l2 2M9 2h6"></path></svg>',
    bag: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
      'stroke-linecap="round" stroke-linejoin="round"><path d="M6 7h12l1 14H5L6 7Z"></path><path d="M9 7a3 3 0 0 1 6 0"></path></svg>',
    play: '<svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M6 4l14 8-14 8V4Z"></path></svg>',
    facebook: '<svg width="22" height="22" viewBox="0 0 24 24" fill="#fff"><path d="M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 ' +
      '4.6V11H7v4h2.5v9h4v-9h3l.5-4h-3.5V8.8c0-.5.3-.8.5-.8Z"></path></svg>',
    instagram: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2">' +
      '<rect x="3.5" y="3.5" width="17" height="17" rx="5"></rect><circle cx="12" cy="12" r="4"></circle>' +
      '<circle cx="17.2" cy="6.8" r="1" fill="#fff" stroke="none"></circle></svg>',
    snapchat: '<svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" stroke="#1a1a1a" stroke-width="1.4" ' +
      'stroke-linejoin="round"><path d="M12 3c3 0 5 2.2 5 5v2.2l1.6-.5c.6-.2 1 .6.4 1l-2 1.1c.5 1.8 1.8 3 ' +
      '3.6 3.6.5.2.4.9-.1 1-1 .3-1.6.4-1.8 1.2-.1.4-.5.5-1 .4-.8-.2-1.8-.2-2.6.4-.9.6-1.8 1.1-3.1 1.1s-2.2-.5-3.1-1.1c-.8-.6-1.8-.6-2.6-.4-.5.1-.9 ' +
      '0-1-.4-.2-.8-.8-.9-1.8-1.2-.5-.1-.6-.8-.1-1 1.8-.6 3.1-1.8 3.6-3.6l-2-1.1c-.6-.4-.2-1.2.4-1L7 10.2V8c0-2.8 2-5 5-5Z"></path></svg>',
  };

  function showCompletionPopup(secs, genuine, pct) {
    var existing = document.querySelector(".wtp-modal-overlay");
    if (existing) existing.parentNode.removeChild(existing);
    var c = current || {};
    var hints = c.hints || 0;
    var info = {
      productId: c.productId, grid: c.grid, title: c.title || "", handle: c.handle || "",
      imageUrl: c.imageUrl || "", physicalUrl: physicalUrl, genuine: !!genuine,
      time: fmtTime(secs), difficulty: DIFF_NAMES[c.grid] || "",
      hintsText: hints + " hint" + (hints === 1 ? "" : "s") + " used",
      kicker: genuine ? "100% COMPLETE" : Math.max(0, Math.min(99, pct | 0)) + "% COMPLETE",
      headline: genuine ? "Nailed it." : "Time submitted.",
    };
    var fileName = "what-the-puzzles-" +
      (puzzleName(info.title).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "puzzle") + ".png";

    function circle(p, label) {
      return '<button type="button" class="wtp-share__btn" data-platform="' + p + '">' +
        '<span class="wtp-share__dot wtp-share__dot--' + p + '">' + WIN_ICONS[p] + "</span>" +
        '<span class="wtp-share__lbl">' + label + "</span></button>";
    }
    var overlay = el(
      '<div class="wtp-modal-overlay">' +
        '<div class="wtp-modal wtp-modal--win" role="dialog" aria-modal="true" aria-label="Puzzle complete">' +
          '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
          (genuine ? '<div class="wtp-win__deco" aria-hidden="true"><span>🎉</span><span>✨</span>' +
            "<span>🎊</span><span>✨</span><span>🎉</span></div>" : "") +
          '<div class="wtp-win__kicker">' + esc(info.kicker) + "</div>" +
          '<h2 class="wtp-win__title">' + esc(info.headline) + "</h2>" +
          '<p class="wtp-win__sub">You ' + (genuine ? "finished" : "submitted") +
            " “" + esc(info.title) + "”</p>" +
          '<div class="wtp-win__chips">' +
            "<span>" + WIN_ICONS.clock + esc(info.time) + "</span>" +
            (info.difficulty ? "<span>Difficulty: " + esc(info.difficulty) + "</span>" : "") +
            "<span>" + esc(info.hintsText) + "</span>" +
          "</div>" +
          (info.imageUrl
            ? '<div class="wtp-win__img"><img src="' + esc(info.imageUrl) + '" alt="' + esc(info.title) + '">' +
              "<span>Your finished puzzle</span></div>"
            : "") +
          '<div class="wtp-win__actions">' +
            // Omitted only when the puzzle has no physical twin — never a wrong
            // product, a 404, or the free digital page (resolvePhysicalUrl).
            (physicalUrl
              ? '<a class="wtp-win__buy" href="' + esc(physicalUrl) + '">' + WIN_ICONS.bag + "Buy This Physical Puzzle</a>"
              : "") +
            '<a class="wtp-win__more" href="/pages/play">' + WIN_ICONS.play + "Play More Puzzles</a>" +
          "</div>" +
          '<div class="wtp-win__or">or</div>' +
          '<div class="wtp-share" data-state="offer">' +
            '<div class="wtp-share__head"><strong>Show it off</strong>' +
              '<span class="wtp-share__badge">+1 credit</span></div>' +
            '<p class="wtp-share__sub">Share your finished puzzle and we’ll toss you a free credit.</p>' +
            '<div class="wtp-share__row">' +
              circle("facebook", "Facebook") + circle("instagram", "Instagram") + circle("snapchat", "Snapchat") +
            "</div>" +
            '<p class="wtp-share__note">TikTok intentionally left out — no reliable way to push a share to it ' +
              "from a web page, so it can’t fairly reward a credit here.</p>" +
          "</div>" +
        "</div>" +
      "</div>");
    document.body.appendChild(overlay);
    function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }
    overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
    overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });

    var card = overlay.querySelector(".wtp-share");
    var badge = card.querySelector(".wtp-share__badge");
    var sub = card.querySelector(".wtp-share__sub");
    function setCard(state) {
      card.setAttribute("data-state", state);
      if (state === "earned") {
        badge.textContent = "✓ +1 credit added";
        sub.textContent = "Thanks for sharing — your free credit is in your wallet.";
      } else if (state === "already") {
        badge.textContent = "✓ Credit earned";
        sub.textContent = "You’ve already earned the share credit for this puzzle — share away!";
      } else if (state === "none") {
        badge.style.display = "none";
        sub.textContent = "Share your puzzle with friends. Finish every piece to earn a free credit.";
      }
    }

    var claimed = false, claiming = false;
    if (!info.genuine) setCard("none");
    else {
      // Replay of a puzzle whose credit was already earned? Say so up front rather
      // than promising "+1 credit". Silent: never prompts for verification here.
      wtpWallet.boundSession().then(function (sess) {
        return sess ? sb.rpc("wallet_share_status", { p_product: info.productId }) : null;
      }).then(function (r) {
        if (r && r.data && r.data.claimed && !claimed) { claimed = true; setCard("already"); }
      }).catch(function () {});
    }

    // Build the Instagram image now, so it is ready (in hand) at tap time: the
    // mobile share sheet must be opened straight from the tap.
    var shareBlob = null, shareFile = null;
    buildShareImage(info).then(function (b) {
      shareBlob = b;
      if (b) { try { shareFile = new File([b], fileName, { type: "image/png" }); } catch (e) { shareFile = null; } }
    });

    async function claim(platform) {
      if (!info.genuine || claimed || claiming) return;
      claiming = true;
      try {
        var sess = await wtpWallet.ensure();
        if (!overlay.parentNode) document.body.appendChild(overlay);   // verify swaps it out
        if (!sess) { wtpBW.toast("Verify your email to collect your free credit.", true); return; }
        // A player who verified only now had no completion recorded at solve time.
        // Idempotent: first completion only, and the Locked Credits they earned.
        await wtpWallet.call("wallet_record_completion", { p_product: info.productId, p_difficulty: info.grid });
        var res = await wtpWallet.call("wallet_claim_share_credit",
          { p_product: info.productId, p_platform: platform });
        if (res && res.ok) {
          claimed = true; setCard("earned");
          wtpBW.toast("+1 credit added — thanks for sharing!");
        } else if (res && res.error === "already_claimed") {
          claimed = true; setCard("already");
        } else {
          wtpBW.toast("Shared! The credit didn’t go through — tap a share button again to retry.", true);
        }
      } finally { claiming = false; }
    }

    // Instagram and Snapchat share the finished-puzzle IMAGE. Sep 30 Scope & Plan: "On
    // phones this opens the phone's own share sheet; on computers ... Instagram and
    // Snapchat download the image." (v78 added Snapchat to this path.)
    var APP = { instagram: "Instagram", snapchat: "Snapchat" };
    function saveImage(platform) {
      var app = APP[platform];
      if (shareBlob) {
        downloadBlob(shareBlob, fileName);
        wtpBW.toast(isMobileDevice() ? "Image saved — open " + app + " to post it."
          : platform === "snapchat" ? "Image saved — send it from Snapchat on your phone."
          : "Image saved — post it to Instagram from your phone.");
        return claim(platform);
      }
      // Image not ready (or couldn't be built): open the artwork itself to save.
      var w = info.imageUrl ? window.open(info.imageUrl, "_blank") : null;
      if (!w) return wtpBW.toast("Couldn’t prepare the image — please try again.", true);
      try { w.opener = null; } catch (e) {}
      wtpBW.toast("Save the image, then post it to " + app + ".");
      claim(platform);
    }
    function shareImage(platform) {
      // Phones: the system share sheet, where Instagram / Snapchat appear.
      if (isMobileDevice() && shareFile && navigator.share && navigator.canShare) {
        // v80: Sep 30 doc — "share an image of the finished puzzle with the time and a link".
        // The time and link ride in `text`, not `url`: with `url` set, iOS hands some apps
        // (Instagram among them) only the link and drops the image.
        var data = { files: [shareFile], title: "What The Puzzles",
          text: "I finished " + puzzleName(info.title) + " in " + info.time + " 🧩 " + shareLink(info, platform) };
        var can = false;
        try { can = navigator.canShare(data); } catch (e) {}
        if (!can) {                                   // a sheet that takes files but not text
          data = { files: [shareFile], title: "What The Puzzles" };
          try { can = navigator.canShare(data); } catch (e) {}
        }
        if (can) {
          navigator.share(data).then(function () { claim(platform); }, function (err) {
            if (err && err.name === "AbortError") return;     // backed out: no share, no credit
            saveImage(platform);
          });
          return;
        }
      }
      // A phone without a usable share sheet: Snapchat keeps its own web share link
      // (opened synchronously from the tap, or browsers block it). Computers download.
      if (platform === "snapchat" && isMobileDevice()) {
        var w = window.open(shareUrlFor("snapchat", info), "_blank");
        if (!w) return wtpBW.toast("Your browser blocked the share window — allow pop-ups and tap again.", true);
        try { w.opener = null; } catch (err) {}
        return claim("snapchat");
      }
      saveImage(platform);
    }

    card.addEventListener("click", function (e) {
      var b = e.target.closest("[data-platform]");
      if (!b) return;
      var p = b.getAttribute("data-platform");
      if (p === "instagram" || p === "snapchat") return shareImage(p);
      // Facebook: its share window, opened synchronously from the tap or the browser
      // blocks it as a pop-up.
      var w = window.open(shareUrlFor(p, info), "_blank", isMobileDevice() ? undefined : "width=640,height=640");
      if (!w) return wtpBW.toast("Your browser blocked the share window — allow pop-ups and tap again.", true);
      try { w.opener = null; } catch (err) {}
      claim(p);
    });

    return overlay;
  }

  // ---- M7: Challenge a Friend -------------------------------------------------
  // Built to the prototype's Challenge modal (Your Link / Preview Their Invite,
  // Copy Link, Text It, WhatsApp) with the client's one correction: the friend
  // DOES need to log in or create an account, so every "no app or account
  // needed" line is gone. Link = /pages/play?vs=CODE (a Shopify theme can't serve
  // the prototype's /vs/CODE path). Challenges carry no money, so they use the
  // leaderboard's identity (the Shopify customer email), like submit_score.
  var wtpChallenge = (function () {
    var ICON = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round"><circle cx="9" cy="12" r="6"></circle><circle cx="15" cy="12" r="6"></circle></svg>';
    var active = null;        // the challenge this player arrived with, once loaded

    function normCode(c) {
      c = String(c || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      return /^[A-Z0-9]{6}$/.test(c) ? c : null;
    }
    function linkFor(code) { return location.origin + "/pages/play?vs=" + code; }
    function prettyLink(code) { return linkFor(code).replace(/^https?:\/\//, ""); }
    // The prototype's message, minus "No app or sign-up needed" (client correction).
    function message(title, code) {
      return "I challenge you to beat my time on \"" + title + "\" 🧩 Just tap: " + linkFor(code);
    }

    async function rpc(fn, args, retried) {
      if (!sb) await sbReady;
      try {
        var res = await sb.rpc(fn, args);
        if (res && res.error) {
          if (!retried && await healStaleAuth(res.error)) return rpc(fn, args, true);
          return { ok: false, error: res.error.message || "rpc_failed" };
        }
        return res && res.data ? res.data : { ok: false, error: "empty" };
      } catch (e) { return { ok: false, error: "threw" }; }
    }

    // A logged-out friend is sent to log in; this remembers the invite so they
    // land back on it even if the login flow drops return_to. Expires in an hour
    // so an abandoned invite can't hijack a later visit.
    function setPending(code) {
      try { localStorage.setItem("wtp_pending_vs", JSON.stringify({ code: code, at: Date.now() })); } catch (e) {}
    }
    function peekPending() {
      try {
        var p = JSON.parse(localStorage.getItem("wtp_pending_vs") || "null");
        if (p && normCode(p.code) && Date.now() - p.at < 3600000) return normCode(p.code);
        localStorage.removeItem("wtp_pending_vs");
      } catch (e) {}
      return null;
    }
    function clearPending() { try { localStorage.removeItem("wtp_pending_vs"); } catch (e) {} }

    function copyText(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).catch(function () { fallbackCopy(text); });
      }
      fallbackCopy(text);
      return Promise.resolve();
    }
    function fallbackCopy(text) {
      var t = document.createElement("textarea");
      t.value = text; t.style.position = "fixed"; t.style.opacity = "0";
      document.body.appendChild(t); t.focus(); t.select();
      try { document.execCommand("copy"); } catch (e) {}
      document.body.removeChild(t);
    }

    function timeLine(ttb, grid) {
      var d = DIFF_NAMES[grid] ? " · " + esc(DIFF_NAMES[grid]) : "";
      return ttb != null
        ? "Time to beat: <b>" + fmtTime(ttb) + "</b>" + d
        : "No time set yet — whoever finishes first wins!" + d;
    }
    // The friend's invite card (prototype "Preview Their Invite"), used both in
    // the preview tab and as the real landing page of the link.
    function inviteHtml(o) {
      return '<div class="wtp-vs__invite">' +
        '<div class="wtp-vs__ithumb"' + (o.imageUrl ? ' style="background-image:url(\'' + esc(o.imageUrl) + '\')"' : "") + "></div>" +
        '<div class="wtp-vs__ibody">' +
          '<div class="wtp-vs__ikicker">' + (o.kicker || "🎉 " + esc(o.name) + " challenged you!") + "</div>" +
          '<div class="wtp-vs__ititle">' + (o.titleHtml || "Beat their time on " + esc(o.title)) + "</div>" +
          '<div class="wtp-vs__itime">' + timeLine(o.ttb, o.grid) + "</div>" +
          o.button +
          (o.after || "") +
          '<div class="wtp-vs__ifoot">Powered by What The Puzzles</div>' +
        "</div></div>";
    }

    // ---- the challenger's modal ------------------------------------------------
    function open(ctx) {
      if (!customerEmail) return;
      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      var diff = DIFF_NAMES[ctx.grid] || "";
      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal wtp-modal--vs" role="dialog" aria-modal="true" aria-label="Challenge a Friend">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
            "<h2>Challenge a Friend</h2>" +
            '<p class="wtp-vs__sub">Send them this exact puzzle. Whoever finishes first wins bragging rights.</p>' +
            '<div class="wtp-vs__tabs" role="tablist">' +
              '<button type="button" class="wtp-vs__tab is-on" data-tab="link" role="tab">Your Link</button>' +
              '<button type="button" class="wtp-vs__tab" data-tab="preview" role="tab">Preview Their Invite</button>' +
            "</div>" +
            '<div class="wtp-vs__panel" data-panel="link">' +
              '<div class="wtp-vs__card">' +
                '<div class="wtp-vs__thumb"' + (ctx.imageUrl ? ' style="background-image:url(\'' + esc(ctx.imageUrl) + '\')"' : "") + "></div>" +
                '<div><div class="wtp-vs__cname">' + esc(ctx.title) + "</div>" +
                '<div class="wtp-vs__csub">' + esc(diff) + " · your time so far: " + fmtTime(ctx.elapsed || 0) + "</div></div>" +
              "</div>" +
              '<div class="wtp-vs__linkrow">' +
                '<div class="wtp-vs__link">Creating your link…</div>' +
                '<button type="button" class="wtp-vs__copy" disabled>Copy Link</button>' +
              "</div>" +
              '<div class="wtp-vs__share">' +
                '<a class="wtp-vs__out is-off" data-kind="sms">Text It</a>' +
                '<a class="wtp-vs__out is-off" data-kind="wa" target="_blank" rel="noopener">WhatsApp</a>' +
              "</div>" +
              '<p class="wtp-vs__note">No app download and no phone number collected by us — just a link. ' +
                "Your friend logs in (or creates a free account) and goes straight into the same puzzle, free.</p>" +
              '<div class="wtp-vs__results" hidden></div>' +
            "</div>" +
            '<div class="wtp-vs__panel" data-panel="preview" hidden>' +
              inviteHtml({ name: playerName(), title: ctx.title, imageUrl: ctx.imageUrl, ttb: null, grid: ctx.grid,
                button: '<span class="wtp-vs__accept wtp-vs__accept--demo">Accept Challenge — Play Free</span>' }) +
            "</div>" +
          "</div>" +
        "</div>");
      document.body.appendChild(overlay);
      function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });

      // Tabs.
      overlay.querySelector(".wtp-vs__tabs").addEventListener("click", function (e) {
        var t = e.target.closest("[data-tab]");
        if (!t) return;
        var tab = t.getAttribute("data-tab");
        overlay.querySelectorAll(".wtp-vs__tab").forEach(function (b) {
          b.classList.toggle("is-on", b.getAttribute("data-tab") === tab);
        });
        overlay.querySelectorAll(".wtp-vs__panel").forEach(function (p) {
          p.hidden = p.getAttribute("data-panel") !== tab;
        });
      });

      var linkBox = overlay.querySelector(".wtp-vs__link");
      var copyBtn = overlay.querySelector(".wtp-vs__copy");
      var code = null;

      copyBtn.addEventListener("click", function () {
        if (!code) return;
        copyText(linkFor(code)).then(function () {
          copyBtn.textContent = "Copied!";
          setTimeout(function () { copyBtn.textContent = "Copy Link"; }, 1500);
        });
      });

      rpc("create_challenge", {
        p_email: getPlayerKey(), p_name: playerName(), p_product: ctx.productId,
        p_handle: ctx.handle, p_title: ctx.title, p_difficulty: ctx.grid,
      }).then(function (res) {
        if (!res || !res.ok || !res.code) {
          linkBox.textContent = "Couldn’t create your link — please close and try again.";
          return;
        }
        code = res.code;
        linkBox.textContent = prettyLink(code);
        copyBtn.disabled = false;
        var msg = encodeURIComponent(message(ctx.title, code));
        var sms = overlay.querySelector('[data-kind="sms"]'), wa = overlay.querySelector('[data-kind="wa"]');
        sms.href = "sms:?&body=" + msg;              // the prototype's form (iOS + Android)
        wa.href = "https://wa.me/?text=" + msg;
        sms.classList.remove("is-off"); wa.classList.remove("is-off");

        // Preview: the real time to beat (their best on this puzzle + difficulty).
        rpc("get_challenge", { p_code: code, p_viewer: getPlayerKey() }).then(function (g) {
          var tl = overlay.querySelector('[data-panel="preview"] .wtp-vs__itime');
          if (g && g.ok && tl) tl.innerHTML = timeLine(g.time_to_beat, ctx.grid);
        });
        // How friends did on this link (shown only once someone has played).
        rpc("challenge_results", { p_email: getPlayerKey(), p_product: ctx.productId, p_difficulty: ctx.grid })
          .then(function (r) {
            var box = overlay.querySelector(".wtp-vs__results");
            if (!box || !r || !r.ok || !r.plays || !r.plays.length) return;
            box.innerHTML = '<div class="wtp-vs__rhead">How your friends did</div>' + r.plays.map(function (p) {
              var tag = r.time_to_beat == null ? "Finished first" : p.beat ? "Beat you" : "You’re ahead";
              return '<div class="wtp-vs__row"><span class="n">' + esc(p.name) + '</span><span class="tm">' +
                fmtTime(p.time) + '</span><span class="tag ' + (p.beat ? "is-beat" : "is-ahead") + '">' + tag + "</span></div>";
            }).join("");
            box.hidden = false;
          });
      });

      // Links without a code yet do nothing.
      overlay.querySelector(".wtp-vs__share").addEventListener("click", function (e) {
        var a = e.target.closest(".wtp-vs__out");
        if (a && a.classList.contains("is-off")) e.preventDefault();
      });
      return overlay;
    }

    // ---- the friend's landing page: /pages/play?vs=CODE ------------------------
    async function renderInvite(code) {
      stopTimer();
      root.innerHTML = '<div class="wtp-play__loading">Loading challenge…</div>';
      var got = await Promise.all([
        rpc("get_challenge", { p_code: code, p_viewer: customerEmail || null }),
        fetchAllProducts(), fetchInactiveIds(), fetchPuzzleMap(),
      ]).catch(function () { return [null, [], {}, {}]; });
      var ch = got[0], products = got[1] || [], hidden = got[2] || {}, map = got[3] || {};
      function msg(h, p) {
        root.innerHTML = "";
        root.appendChild(el('<div class="wtp-play__login"><h1>' + h + '</h1><p class="wtp-play__sub">' + p +
          '</p><a class="wtp-btn" href="/pages/play">Browse all puzzles</a></div>'));
      }
      if (!ch || !ch.ok) return msg("Challenge not found", "This challenge link isn’t valid — check it was copied in full.");
      var product = products.find(function (p) { return p.handle === ch.handle; });
      if (!product || hidden[String(product.id)]) {
        return msg("Puzzle unavailable", "The puzzle in this challenge isn’t available any more.");
      }
      var playHref = "/pages/play?puzzle=" + encodeURIComponent(ch.handle) + "&vs=" + ch.code;

      // Signed in after following this invite: carry straight on into the puzzle.
      if (customerEmail && !ch.is_mine && peekPending() === ch.code) {
        clearPending();
        location.replace(playHref);
        return;
      }

      var o = { name: ch.name, title: ch.title || product.title, imageUrl: playImage(map, product),
                ttb: ch.time_to_beat, grid: ch.difficulty };
      if (ch.is_mine) {
        o.kicker = "👋 This is your challenge link";
        o.titleHtml = "Send it to a friend to challenge them on " + esc(o.title);
        o.button = '<a class="wtp-vs__accept" href="/pages/play?puzzle=' + encodeURIComponent(ch.handle) + '">Play This Puzzle</a>';
      } else if (customerEmail) {
        o.button = '<a class="wtp-vs__accept" href="' + esc(playHref) + '">Accept Challenge — Play Free</a>';
      } else {
        o.button = '<a class="wtp-vs__accept wtp-vs__login" href="' + esc(buildLoginHref("/pages/play?vs=" + ch.code)) +
          '">Accept Challenge — Play Free</a>';
        o.after = '<div class="wtp-vs__need">Log in or create a free account to play.</div>';
      }
      root.innerHTML = "";
      root.appendChild(el('<div class="wtp-vs-page">' + inviteHtml(o) +
        '<p class="wtp-vs-page__back"><a class="wtp-link" href="/pages/play">← Browse all puzzles</a></p></div>'));
      // Only once they actually head to log in (v47: the bounce flag must never be
      // left set by a visitor who just looked, or it hijacks their next page).
      var login = root.querySelector(".wtp-vs__login");
      if (login) login.addEventListener("click", function () { setPending(ch.code); markWantsPlay(); });
    }

    // ---- in the puzzle, having accepted ----------------------------------------
    // Loads the challenge, sets its difficulty, and shows the banner. Anything
    // off (bad code, other puzzle, your own link) quietly plays as normal.
    function attach(code, product, controls, setDifficulty) {
      active = null;
      if (!code) return;
      var banner = el('<div class="wtp-vs-banner">' + ICON + "<span>Loading challenge…</span></div>");
      controls.parentNode.insertBefore(banner, controls.nextSibling);
      var select = controls.querySelector(".wtp-difficulty");
      function paint() {
        if (!active) return;
        var txt = banner.querySelector("span");
        var want = active.difficulty, on = select && parseInt(select.value, 10) === want;
        banner.classList.toggle("is-off", !on);
        txt.innerHTML = on
          ? "<strong>" + esc(active.name) + "</strong> challenged you — " +
            (active.time_to_beat != null
              ? "time to beat: <b>" + fmtTime(active.time_to_beat) + "</b> on " + esc(DIFF_NAMES[want] || "")
              : "no time set yet, finish first to win (" + esc(DIFF_NAMES[want] || "") + ")")
          : "Switch difficulty back to <b>" + esc(DIFF_NAMES[want] || "") + "</b> to play " +
            esc(active.name) + "’s challenge.";
      }
      if (select) select.addEventListener("change", paint);
      rpc("get_challenge", { p_code: code, p_viewer: customerEmail || null }).then(function (ch) {
        if (!ch || !ch.ok || String(ch.product) !== String(product.id)) {
          banner.parentNode && banner.parentNode.removeChild(banner);
          return;
        }
        if (ch.is_mine) {
          banner.querySelector("span").textContent = "This is your own challenge link — send it to a friend!";
          return;
        }
        active = ch;
        setDifficulty(ch.difficulty);
        paint();
      });
    }

    // On a genuine finish of the challenge puzzle at the challenge difficulty,
    // record the time and tell them how they did — on the completion screen.
    function afterSolve(overlay, genuine, pid, grid, secs) {
      var ch = active;
      if (!ch || !genuine || !overlay || String(ch.product) !== String(pid) || ch.difficulty !== grid) return;
      var box = el('<div class="wtp-win__vs"><span>Checking the challenge…</span></div>');
      var sub = overlay.querySelector(".wtp-win__sub");
      if (sub) sub.parentNode.insertBefore(box, sub.nextSibling);
      rpc("record_challenge_play", { p_code: ch.code, p_email: getPlayerKey(), p_name: playerName(), p_time: secs })
        .then(function (r) {
          if (!r || !r.ok) { if (box.parentNode) box.parentNode.removeChild(box); return; }
          var who = "<strong>" + esc(r.name) + "</strong>";
          var t = box.querySelector("span");
          if (r.time_to_beat == null) t.innerHTML = "🏁 You finished first — " + who + " hasn’t set a time yet!";
          else if (r.tie) t.innerHTML = "🤝 Dead heat — you matched " + who + "’s " + fmtTime(r.time_to_beat) + "!";
          else if (r.beat) t.innerHTML = "🏆 You beat " + who + " by " + fmtTime(r.margin) + "!";
          else t.innerHTML = "⏱ " + who + " is still ahead by " + fmtTime(r.margin) + " — hit Restart to try again.";
          box.classList.add(r.beat || r.tie ? "is-win" : "is-lose");
        });
    }

    return { ICON: ICON, open: open, renderInvite: renderInvite, attach: attach, afterSolve: afterSolve,
             normCode: normCode, peekPending: peekPending, setPending: setPending,
             active: function () { return active; } };
  })();

  // ---- M8: Custom Avatar ------------------------------------------------------
  // Client-confirmed: "Players can click their avatar and upload their own image.
  // It then appears on their profile and on the leaderboard. Images are validated
  // and squared automatically." The avatar is the prototype's round initial in the
  // site header (where the account icon sits); it also shows beside "Playing as"
  // (the only place on phones, where Dawn hides that header icon) and on every
  // leaderboard row. Moderation / reporting is phase 2 (client-confirmed).
  //
  // The browser checks the file, centre-crops it square and re-encodes it to a
  // 256px image (which also strips the photo's EXIF/GPS data). Uploading needs the
  // same one-time email verification as credits: a picture is shown to other
  // players, so only its verified owner may change it (enforced by storage RLS).
  var wtpAvatar = (function () {
    var SIZE = 256;                               // stored square, px
    var MIN = 64;                                 // smallest source side we accept
    var MAX_BYTES = 5 * 1024 * 1024;              // v78: "JPG, PNG or WebP images up to 5 MB" (Sep 30 Scope & Plan)
    var TYPES = { "image/jpeg": 1, "image/png": 1, "image/webp": 1 };
    var ACCEPT = "image/jpeg,image/png,image/webp";
    var mine = null;                              // { url } once loaded; null = initial
    var mineLoaded = null;

    function initialOf(name) {
      var m = String(name || "").match(/[A-Za-z0-9]/);
      return m ? m[0].toUpperCase() : "?";
    }
    function urlFor(rec) {
      if (!rec || !rec.path || !sb) return null;
      try {
        return sb.storage.from("avatars").getPublicUrl(rec.path).data.publicUrl + "?v=" + (rec.v || 0);
      } catch (e) { return null; }
    }
    function inner(url, name) {
      return url ? '<img src="' + esc(url) + '" alt="">' : esc(initialOf(name));
    }
    // One avatar bubble. me = the player's own (kept in sync when it changes);
    // edit = clicking it opens the uploader.
    function html(o) {
      var cls = "wtp-avatar" + (o.me ? " wtp-avatar--me" : "") + (o.cls ? " " + o.cls : "") +
        (o.url ? " has-img" : "");
      var attrs = o.edit ? ' role="button" tabindex="0" title="Change your avatar" aria-label="Change your avatar" data-avatar-edit' : "";
      if (o.lb != null) attrs += ' data-lb="' + o.lb + '"';
      return '<span class="' + cls + '"' + attrs + ">" + inner(o.url, o.name) + "</span>";
    }
    function paint(node, url, name) {
      node.innerHTML = inner(url, name);
      node.classList.toggle("has-img", !!url);
    }
    function myUrl() { return mine && mine.url; }
    function repaint() {
      document.querySelectorAll(".wtp-avatar--me").forEach(function (n) { paint(n, myUrl(), playerName()); });
    }

    // My own picture: public read, so it shows without verifying on this device.
    function loadMine(force) {
      if (!customerEmail) return Promise.resolve(null);
      if (mineLoaded && !force) return mineLoaded;
      mineLoaded = (async function () {
        if (!sb) await sbReady;
        var res = await sb.rpc("avatars_for", { p_emails: [customerEmail] });
        var rec = res && res.data ? res.data[customerEmail] : null;
        mine = rec ? { url: urlFor(rec) } : null;
        repaint();
        return mine;
      })().catch(function () { mineLoaded = null; return null; });
      return mineLoaded;
    }

    // The prototype's avatar: the header account icon becomes the player's
    // avatar on the play pages; clicking it opens the uploader, which links on to
    // "My account" so nothing the icon used to do is lost.
    function mountHeader() {
      if (!customerEmail) return;
      var link = document.querySelector("a.header__icon--account");
      if (!link || link.querySelector(".wtp-avatar")) return;
      var holder = link.querySelector("account-icon") || link.querySelector("svg") && link.querySelector("svg").parentNode || link;
      holder.innerHTML = html({ me: true, name: playerName(), url: myUrl(), cls: "wtp-avatar--nav" });
      link.setAttribute("title", "Change your avatar");
      link.addEventListener("click", function (e) { e.preventDefault(); open(); });
    }
    function boot() {
      if (!customerEmail) return;
      try { mountHeader(); } catch (e) {}
      loadMine();
    }

    // Validate -> decode -> centre square crop -> 256px -> re-encode.
    // Resolves a Blob (webp, or jpeg where the browser can't encode webp).
    function processFile(file) {
      return new Promise(function (resolve, reject) {
        if (!file) return reject("none");
        if (!TYPES[file.type]) return reject("type");
        if (file.size > MAX_BYTES) return reject("size");
        var src = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () {
          try {
            var w = img.naturalWidth, h = img.naturalHeight;
            if (w < MIN || h < MIN) { URL.revokeObjectURL(src); return reject("small"); }
            var s = Math.min(w, h), sx = (w - s) / 2, sy = (h - s) / 2;
            var c = document.createElement("canvas");
            c.width = c.height = SIZE;
            var g = c.getContext("2d");
            g.fillStyle = "#fff"; g.fillRect(0, 0, SIZE, SIZE);   // transparent PNGs -> white, not black
            g.imageSmoothingQuality = "high";
            g.drawImage(img, sx, sy, s, s, 0, 0, SIZE, SIZE);
            URL.revokeObjectURL(src);
            c.toBlob(function (b) {
              if (b && b.type === "image/webp") return resolve(b);
              c.toBlob(function (j) { return j ? resolve(j) : reject("decode"); }, "image/jpeg", 0.88);
            }, "image/webp", 0.86);
          } catch (e) { URL.revokeObjectURL(src); reject("decode"); }
        };
        img.onerror = function () { URL.revokeObjectURL(src); reject("decode"); };
        img.src = src;
      });
    }
    var REASONS = {
      type: "Please choose a JPG, PNG or WebP image.",
      size: "That image is over 5 MB — please pick a smaller one.",
      small: "That image is too small — please use one at least 64 × 64 pixels.",
      decode: "We couldn’t read that image — please try a different one.",
    };

    function open() {
      if (!customerEmail) return;
      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal wtp-modal--avatar" role="dialog" aria-modal="true" aria-label="Your avatar">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
            "<h2>Your avatar</h2>" +
            '<p class="wtp-av__sub">Shown on your profile and on the leaderboards. Pick any photo — ' +
              "we’ll crop it square for you.</p>" +
            '<div class="wtp-av__preview">' + html({ name: playerName(), url: myUrl(), cls: "wtp-avatar--big" }) + "</div>" +
            '<p class="wtp-av__msg" role="status"></p>' +
            '<div class="wtp-av__actions">' +
              '<label class="wtp-av__pick"><span>Choose a photo</span>' +
                '<input type="file" accept="' + ACCEPT + '" hidden></label>' +
              '<button type="button" class="wtp-av__save" hidden>Save</button>' +
              '<button type="button" class="wtp-av__remove"' + (myUrl() ? "" : " hidden") + ">Remove photo</button>" +
            "</div>" +
            '<p class="wtp-av__fine">JPG, PNG or WebP · up to 5 MB</p>' +
            '<a class="wtp-av__account" href="/account">My account →</a>' +
          "</div>" +
        "</div>");
      document.body.appendChild(overlay);
      var previewUrl = null;
      function close() {
        if (previewUrl) { URL.revokeObjectURL(previewUrl); previewUrl = null; }
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });

      var big = overlay.querySelector(".wtp-avatar--big");
      var msg = overlay.querySelector(".wtp-av__msg");
      var input = overlay.querySelector("input[type=file]");
      var pickLabel = overlay.querySelector(".wtp-av__pick span");
      var saveBtn = overlay.querySelector(".wtp-av__save");
      var removeBtn = overlay.querySelector(".wtp-av__remove");
      var ready = null;          // the processed square Blob awaiting Save
      var busy = false;

      function say(text, isErr) { msg.textContent = text || ""; msg.classList.toggle("is-err", !!isErr); }

      // M12: is my picture under review, or are uploads off for me? (Silent —
      // never prompts for verification just to show this.)
      wtpWallet.boundSession().then(function (sess) {
        return sess ? sb.rpc("avatar_my_status") : null;
      }).then(function (r) {
        var s = r && r.data;
        if (!s) return;
        if (s.blocked) {
          say("Picture uploads are turned off for your account.", true);
          overlay.querySelector(".wtp-av__pick").hidden = true;
        } else if (s.status === "hidden") {
          say("Your picture is hidden while our team reviews a report. Uploading a new one replaces it.", true);
        }
      }).catch(function () {});

      input.addEventListener("change", function () {
        var f = input.files && input.files[0];
        input.value = "";        // choosing the same file again still fires
        if (!f || busy) return;
        say("Preparing…");
        processFile(f).then(function (blob) {
          ready = blob;
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          previewUrl = URL.createObjectURL(blob);
          paint(big, previewUrl, playerName());
          say("Looking good? Save it to use it everywhere.");
          saveBtn.hidden = false;
          pickLabel.textContent = "Choose another";
        }, function (why) {
          ready = null; saveBtn.hidden = true;
          say(REASONS[why] || REASONS.decode, true);
        });
      });

      // Verification may swap in its own prompt; put this dialog back after.
      async function verified() {
        var sess = await wtpWallet.ensure();
        if (!overlay.parentNode) document.body.appendChild(overlay);
        if (!sess || !sess.user || !sess.user.id) {
          say("Verify your email to change your avatar — it’s a one-time check on this device.", true);
          return null;
        }
        return sess;
      }

      saveBtn.addEventListener("click", async function () {
        if (!ready || busy) return;
        busy = true; saveBtn.disabled = true; say("Saving…");
        try {
          var sess = await verified();
          if (!sess) return;
          var up = await sb.storage.from("avatars").upload(sess.user.id + "/avatar", ready,
            { upsert: true, contentType: ready.type, cacheControl: "3600" });
          if (up && up.error) return say("Upload failed — please try again.", true);
          var res = await sb.rpc("set_avatar");
          var d = res && res.data;
          if (d && d.error === "blocked") return say("Picture uploads are turned off for your account.", true);
          if (!d || !d.ok) return say("Couldn’t save your avatar — please try again.", true);
          mine = { url: urlFor(d) };
          mineLoaded = Promise.resolve(mine);
          repaint();
          close();
          wtpBW.toast("Avatar updated.");
        } catch (e) {
          say("Couldn’t save your avatar — please try again.", true);
        } finally { busy = false; saveBtn.disabled = false; }
      });

      removeBtn.addEventListener("click", async function () {
        if (busy) return;
        busy = true; removeBtn.disabled = true; say("Removing…");
        try {
          var sess = await verified();
          if (!sess) return;
          await sb.storage.from("avatars").remove([sess.user.id + "/avatar"]);
          var res = await sb.rpc("clear_avatar");
          if (!res || !res.data || !res.data.ok) return say("Couldn’t remove it — please try again.", true);
          mine = null;
          mineLoaded = Promise.resolve(null);
          repaint();
          close();
          wtpBW.toast("Avatar removed.");
        } catch (e) {
          say("Couldn’t remove it — please try again.", true);
        } finally { busy = false; removeBtn.disabled = false; }
      });
      return overlay;
    }

    // Leaderboard rows: initials render instantly; pictures fill in when known.
    function fillLeaderboard(boardEl, emails) {
      if (!emails || !emails.length) return;
      (async function () {
        if (!sb) await sbReady;
        var res = await sb.rpc("avatars_for", { p_emails: emails });
        var map = (res && res.data) || {};
        emails.forEach(function (email, i) {
          var rec = map[email];
          if (!rec) return;
          var node = boardEl.querySelector('.wtp-avatar[data-lb="' + i + '"]');
          if (node && !node.classList.contains("wtp-avatar--me")) {
            var name = node.textContent;
            paint(node, urlFor(rec), name);
            // M12: tap to view / report. The email stays a JS property — never
            // written into the page.
            node._avatarEmail = email;
            node.setAttribute("data-avatar-view", "");
            node.setAttribute("role", "button");
            node.setAttribute("tabindex", "0");
            node.setAttribute("title", "View picture");
          }
        });
      })().catch(function () {});
    }

    // ---- M12: view + report another player's picture ----------------------
    // Client: "Optional moderation or reporting can be added" (Phase 2). Any
    // verified player can report; 3 different reports hide it at once until an
    // admin reviews it on /pages/admin (report_avatar enforces all of that).
    var REPORT_REASONS = ["Inappropriate", "Offensive", "Spam or advertising", "Something else"];
    function view(node) {
      var email = node && node._avatarEmail;
      if (!email) return;
      var img = node.querySelector("img");
      var nameEl = node.closest(".wtp-lb-name");
      var name = nameEl ? nameEl.textContent.replace(/\s*·\s*you\s*$/, "").trim() : "";
      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal wtp-modal--avview" role="dialog" aria-modal="true" aria-label="Player picture">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">\u00d7</button>' +
            '<div class="wtp-av__preview">' + html({ name: name, url: img ? img.getAttribute("src") : null, cls: "wtp-avatar--big" }) + "</div>" +
            "<h2>" + esc(name || "Player") + "</h2>" +
            '<div class="wtp-avrep">' +
              '<p class="wtp-avrep__q">See something that shouldn\u2019t be here?</p>' +
              '<div class="wtp-avrep__chips">' + REPORT_REASONS.map(function (r) {
                return '<button type="button" class="wtp-avrep__chip" data-reason="' + esc(r) + '">' + esc(r) + "</button>";
              }).join("") + "</div>" +
              '<p class="wtp-avrep__note">Reporting is anonymous. Our team reviews every report, and a picture ' +
                "reported by several players is hidden straight away.</p>" +
            "</div>" +
          "</div>" +
        "</div>");
      document.body.appendChild(overlay);
      function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });
      var box = overlay.querySelector(".wtp-avrep"), sent = false;
      box.addEventListener("click", async function (e) {
        var b = e.target.closest("[data-reason]");
        if (!b || sent) return;
        sent = true;
        b.disabled = true;
        var sess = await wtpWallet.ensure();          // verified players only
        if (!overlay.parentNode) document.body.appendChild(overlay);
        if (!sess) { sent = false; b.disabled = false; return; }
        var res = null;
        try { res = await sb.rpc("report_avatar", { p_target: email, p_reason: b.getAttribute("data-reason") }); } catch (err) {}
        var d = res && res.data;
        if (d && d.ok) {
          if (d.hidden) paint(node, null, name);       // hidden now: back to the initial here too
          box.innerHTML = '<p class="wtp-avrep__done">\u2713 Thanks \u2014 ' +
            (d.hidden ? "this picture is now hidden while our team reviews it." : "our team will review it.") + "</p>";
        } else {
          sent = false; b.disabled = false;
          wtpBW.toast(d && d.error === "rate_limited"
            ? "You\u2019ve sent a lot of reports today \u2014 please try again tomorrow."
            : d && d.error === "own_picture" ? "That\u2019s your own picture."
            : "That didn\u2019t go through \u2014 please try again.", true);
        }
      });
      return overlay;
    }
    document.addEventListener("click", function (e) {
      var a = e.target && e.target.closest && e.target.closest("[data-avatar-view]");
      if (a) { e.preventDefault(); view(a); }
    });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Enter" && e.key !== " ") return;
      var a = e.target && e.target.closest && e.target.closest("[data-avatar-view]");
      if (a) { e.preventDefault(); view(a); }
    });

    // Any "me" bubble marked editable opens the uploader (survives innerHTML swaps).
    document.addEventListener("click", function (e) {
      var a = e.target && e.target.closest && e.target.closest("[data-avatar-edit]");
      if (!a) return;
      e.preventDefault();
      open();
    });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Enter" && e.key !== " ") return;
      var a = e.target && e.target.closest && e.target.closest("[data-avatar-edit]");
      if (!a) return;
      e.preventDefault();
      open();
    });

    return { html: html, myUrl: myUrl, repaint: repaint, boot: boot, open: open, loadMine: loadMine,
             processFile: processFile, fillLeaderboard: fillLeaderboard, initialOf: initialOf };
  })();

  // ---- M11: credit history + "credits added" notice ---------------------------
  // Client Phase 2 "physical-purchase credit user interface", as we defined it to
  // them: "a screen that surfaces credits earned from physical orders (e.g. 'you
  // earned 5 credits from order #1234') plus a history/ledger view. Today those
  // credits are granted silently and only show as a balance change."
  // No client mock exists ("we haven't built the user interface mock for this
  // yet"), so it follows the existing modal/chip language.
  var wtpHistory = (function () {
    var ITEM_NAMES = { edge_lord: "Edge Lord", cheat_peek: "Cheat Peek", auto_sort: "Auto-Sort Edge Pieces" };
    var CAT_NAMES = { confetti: "Confetti Cannon", sound: "Snap Pack", foil: "Gold Foil Finish", cursor: "Custom Cursor" };
    var ctxPromise = null;

    // Names for what the ledger stores as ids: puzzle titles, pack labels,
    // cosmetic labels, and the live earning rule. All best-effort.
    function context() {
      if (ctxPromise) return ctxPromise;
      ctxPromise = (async function () {
        if (!sb) await sbReady;
        var got = await Promise.all([
          fetchAllProducts().catch(function () { return []; }),
          sb.from("credit_packs").select("code, label").then(function (r) { return (r && r.data) || []; }, function () { return []; }),
          wtpCosmetics.catalogue().catch(function () { return []; }),
          sb.rpc("credit_settings_public").then(function (r) { return (r && r.data) || {}; }, function () { return {}; }),
        ]);
        var puzzles = {}, packs = {}, cos = {};
        got[0].forEach(function (p) { puzzles[String(p.id)] = String(p.title || "").replace(/^\s*digital\s*[|:\-]\s*/i, ""); });
        got[1].forEach(function (p) { packs[p.code] = p.label; });
        got[2].forEach(function (c) { cos[c.category + ":" + c.item_id] = c.label; });
        return { puzzles: puzzles, packs: packs, cos: cos,
                 block: Number(got[3].physical_spend_block || 24), per: Number(got[3].physical_block_credits || 5) };
      })().catch(function () { ctxPromise = null; return { puzzles: {}, packs: {}, cos: {}, block: 24, per: 5 }; });
      return ctxPromise;
    }
    function ruleText(ctx) {
      return "Buying puzzles earns credits: <b>" + ctx.per + " credit" + (ctx.per === 1 ? "" : "s") +
        " for every $" + ctx.block + "</b> you spend on puzzles — added automatically after your order.";
    }
    function num(n) { n = Math.round(Number(n) * 100) / 100; return String(n); }
    function orderRef(e) { return e.order_number ? "Order #" + e.order_number : "Your order"; }

    // One ledger row -> { ico, title, sub, amt, cls } in plain words, or null to hide.
    function describe(e, ctx) {
      var d = e.detail || {}, delta = Number(e.delta), pz = ctx.puzzles[String(d.product || "")];
      var included = d.unlimited === true;
      if (delta === 0 && !included) return null;                        // bookkeeping rows
      var amt = included ? "Included" : (delta > 0 ? "+" : "−") + num(Math.abs(delta));
      var cls = included ? "is-inc" : e.pool === "stash" ? "is-locked" : delta > 0 ? "is-plus" : "is-minus";
      switch (e.reason) {
        case "physical_purchase":
          return { ico: "🧩", title: "Earned from your puzzle order", cls: cls, amt: amt,
                   sub: orderRef(e) + (d.spend_usd ? " · $" + Number(d.spend_usd).toFixed(2) + " of puzzles" : "") };
        case "purchase":
          return { ico: "💳", title: "Bought " + (ctx.packs[d.pack] || "a credit pack") + (Number(d.qty) > 1 ? " ×" + d.qty : ""),
                   sub: orderRef(e), amt: amt, cls: cls };
        case "stash_unlock":
          if (e.pool === "stash") return null;                           // shown once, as the wallet side
          return { ico: "📦", title: "Stash Box unlocked", sub: "Your Locked Credits moved into your wallet", amt: amt, cls: cls };
        case "completion":
          return { ico: "🏁", title: "Finished " + (pz || "a puzzle"),
                   sub: (DIFF_NAMES[d.difficulty] || "") + " · Locked Credits for your Stash Box", amt: amt + " locked", cls: cls };
        case "consumable":
          return { ico: "⚡", title: ITEM_NAMES[d.item] || "Bells & Whistles",
                   sub: (pz ? pz : "This puzzle") + (included ? " · Unlimited" : ""), amt: amt, cls: cls };
        case "cosmetic": {
          var items = d.items || [];
          var one = items.length === 1 ? ctx.cos[d.category + ":" + items[0]] : null;
          return { ico: "✨", title: (CAT_NAMES[d.category] || "Cosmetic") + (one ? ": " + one : items.length > 1 ? ": " + items.length + " styles" : ""),
                   sub: "Yours forever" + (included ? " · Unlimited" : ""), amt: amt, cls: cls };
        }
        case "share":
          return { ico: "📣", title: "Shared a finished puzzle",
                   sub: (d.platform ? d.platform.charAt(0).toUpperCase() + d.platform.slice(1) + " · " : "") + (pz || "Thanks for sharing"),
                   amt: amt, cls: cls };
        case "refund":
          return { ico: "↩️", title: "Refund — " + (ctx.packs[d.pack] || "credit pack"), sub: orderRef(e), amt: amt, cls: cls };
        case "refund_earn":
          return { ico: "↩️", title: "Refund — puzzle order", sub: orderRef(e) + " · credits for the refunded items", amt: amt, cls: cls };
        case "refund_unlock":
          if (e.pool === "stash") return null;
          return { ico: "↩️", title: "Stash unlock refunded", sub: "Locked Credits returned to your Stash Box", amt: amt, cls: cls };
        case "admin_adjust":            // v78: admin credit tools (the admin's note stays private)
        case "test_grant":
          return { ico: "🎁", title: delta > 0 ? "Added by What The Puzzles" : "Removed by What The Puzzles",
                   sub: "Store adjustment", amt: amt, cls: cls };
        default:
          return { ico: "•", title: "Credit adjustment", sub: "", amt: amt, cls: cls };
      }
    }

    function dayLabel(d) {
      var today = new Date(); today.setHours(0, 0, 0, 0);
      var that = new Date(d); that.setHours(0, 0, 0, 0);
      var diff = Math.round((today - that) / 86400000);
      if (diff === 0) return "Today";
      if (diff === 1) return "Yesterday";
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    }
    function timeLabel(d) { return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }

    function rowsHtml(items, ctx, lastDay) {
      var html = "", day = lastDay;
      items.forEach(function (e) {
        var r = describe(e, ctx);
        if (!r) return;
        var at = new Date(e.at), dl = dayLabel(at);
        if (dl !== day) { html += '<div class="wtp-hist__day">' + esc(dl) + "</div>"; day = dl; }
        html += '<div class="wtp-hist__row"><span class="wtp-hist__ico" aria-hidden="true">' + r.ico + "</span>" +
          '<div class="wtp-hist__txt"><div class="t">' + esc(r.title) + '</div><div class="s">' +
          esc([r.sub, timeLabel(at)].filter(Boolean).join(" · ")) + "</div></div>" +
          '<span class="wtp-hist__amt ' + r.cls + '">' + esc(r.amt) + "</span></div>";
      });
      return { html: html, day: day };
    }

    async function open() {
      if (!customerEmail) return;
      if (!(await wtpWallet.ensure())) return;          // history is private: verify first
      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      var st = null;
      try { st = await wtpWallet.state(true); } catch (e) {}
      // v80: the top half is the mockup's "Your Credits" pop-up (opened from the credits
      // chip): the balance, what credits are for, the Stash Box, "See what Bells & Whistles
      // cost" and "Buy More Credits". The history (Sep 30 doc: "with a full history") follows.
      var stash = Number((st && st.stash) || 0), cap = Number((st && st.stash_cap) || 20);
      var bal = Number((st && st.wallet) || 0);
      var inGame = !!document.querySelector(".wtp-bw-open");     // the tray only exists in play
      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal wtp-modal--hist" role="dialog" aria-modal="true" aria-label="Your Credits">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
            "<h2>Your Credits</h2>" +
            '<div class="wtp-yc__bal"><span class="wtp-yc__coin">' + WTP_COIN_ICON + "</span>" +
              (st && st.unlimited
                ? '<div><div class="wtp-yc__n">∞ Unlimited</div><div class="wtp-yc__l">Every add-on is included</div></div>'
                : '<div><div class="wtp-yc__n">' + num(bal) + '</div><div class="wtp-yc__l">' +
                  (bal === 1 ? "credit" : "credits") + " available</div></div>") +
            "</div>" +
            '<p class="wtp-yc__p">Credits are what you spend on in-puzzle upsells — a completed border, hints, ' +
              "sound packs, gold foil, and more. Buy them directly, or earn them free as Locked Credits by " +
              "finishing puzzles.</p>" +
            '<button type="button" class="wtp-yc__stash">' + stashRingHtml(stash, cap) +
              '<span class="wtp-yc__stxt"><span class="t">' +
                (cap > 0 && stash >= cap ? "Box full — " + num(stash) + "/" + num(cap) + " Locked Credits"
                  : num(stash) + "/" + num(cap) + " Locked Credits waiting") + "</span>" +
                '<span class="s">In your Stash Box — tap to view</span></span>' +
              '<span class="wtp-yc__chev" aria-hidden="true">›</span></button>' +
            (inGame ? '<button type="button" class="wtp-yc__bw">' + WTP_BOLT_ICON + "See what Bells &amp; Whistles cost</button>" : "") +
            '<button type="button" class="wtp-yc__buy">Buy More Credits</button>' +
            '<h3 class="wtp-hist__head">History</h3>' +
            '<p class="wtp-hist__rule">…</p>' +
            '<div class="wtp-hist__list"><p class="wtp-hist__empty">Loading…</p></div>' +
            '<button type="button" class="wtp-hist__more" hidden>Show more</button>' +
          "</div>" +
        "</div>");
      document.body.appendChild(overlay);
      function close() { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });
      overlay.querySelector(".wtp-yc__stash").addEventListener("click", function () { close(); wtpWallet.stash(); });
      overlay.querySelector(".wtp-yc__buy").addEventListener("click", function () { close(); wtpWallet.buyCredits(); });
      var bwBtn = overlay.querySelector(".wtp-yc__bw");
      // Opened from the tray's "History ›" the tray is still underneath: closing reveals it
      // (open() toggles, so calling it then would shut the tray instead).
      if (bwBtn) bwBtn.addEventListener("click", function () {
        close();
        if (!document.querySelector(".wtp-bw")) wtpBW.open();
      });

      var list = overlay.querySelector(".wtp-hist__list"), more = overlay.querySelector(".wtp-hist__more");
      var ctx = await context();
      overlay.querySelector(".wtp-hist__rule").innerHTML = "🧩 " + ruleText(ctx);
      var before = null, lastDay = null, shown = 0;
      async function page() {
        more.disabled = true;
        var res = await sb.rpc("wallet_history", { p_limit: 40, p_before: before });
        var d = res && res.data;
        if (!d || !d.ok) { list.innerHTML = '<p class="wtp-hist__empty">Couldn’t load your history — please try again.</p>'; return; }
        var out = rowsHtml(d.items, ctx, lastDay);
        if (before === null) list.innerHTML = "";
        list.insertAdjacentHTML("beforeend", out.html);
        lastDay = out.day;
        shown += (out.html.match(/wtp-hist__row/g) || []).length;
        if (d.items.length) before = d.items[d.items.length - 1].at;
        more.hidden = !d.more; more.disabled = false;
        if (!shown && !d.more) {
          list.innerHTML = '<p class="wtp-hist__empty">No credit activity yet — buy puzzles, finish puzzles or share one to start earning.</p>';
        }
      }
      more.addEventListener("click", page);
      page();
      return overlay;
    }

    // Once per page: tell the player about order-driven credits they haven't
    // seen (physical-purchase earn, a bought pack, a Stash unlock). Never
    // prompts for verification; marked seen on the server so it shows once.
    var checked = false;
    async function checkNew() {
      if (checked || !customerEmail) return;
      checked = true;
      try {
        if (!sb) await sbReady;
        if (!(await wtpWallet.boundSession())) return;
        var res = await sb.rpc("wallet_new_rewards");
        var items = (res && res.data && res.data.items) || [];
        if (!items.length) return;
        await sb.rpc("wallet_mark_rewards_seen");
        var ctx = await context();
        var total = items.reduce(function (s, e) { return s + Number(e.delta); }, 0);
        var lines = items.map(function (e) {
          var d = e.detail || {};
          var what = e.reason === "physical_purchase"
            ? "from your puzzle order" + (e.order_number ? " #" + e.order_number : "") + (d.spend_usd ? " ($" + Number(d.spend_usd).toFixed(2) + ")" : "")
            : e.reason === "purchase" ? (ctx.packs[d.pack] || "credit pack") + (e.order_number ? " — order #" + e.order_number : "")
            : "Stash Box unlocked";
          return '<li><b>+' + num(e.delta) + "</b> " + esc(what) + "</li>";
        }).join("");
        var anyPhysical = items.some(function (e) { return e.reason === "physical_purchase"; });
        if (document.querySelector(".wtp-modal-overlay")) {         // don't stack on another dialog
          wtpBW.toast("🎉 " + num(total) + " credits added to your wallet");
        } else {
          var overlay = el(
            '<div class="wtp-modal-overlay">' +
              '<div class="wtp-modal wtp-modal--reward" role="dialog" aria-modal="true" aria-label="Credits added">' +
                '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
                '<div class="wtp-modal__emoji">🎉</div>' +
                "<h2>" + num(total) + " credit" + (total === 1 ? "" : "s") + " added!</h2>" +
                '<ul class="wtp-reward__list">' + lines + "</ul>" +
                (anyPhysical ? '<p class="wtp-hist__rule">' + ruleText(ctx) + "</p>" : "") +
                '<div class="wtp-reward__actions">' +
                  '<button type="button" class="wtp-reward__ok">Nice!</button>' +
                  '<button type="button" class="wtp-reward__hist">View credit history</button>' +
                "</div>" +
              "</div>" +
            "</div>");
          document.body.appendChild(overlay);
          var close = function () { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); };
          overlay.querySelector(".wtp-modal__close").addEventListener("click", close);
          overlay.querySelector(".wtp-reward__ok").addEventListener("click", close);
          overlay.querySelector(".wtp-reward__hist").addEventListener("click", function () { close(); open(); });
          overlay.addEventListener("click", function (e) { if (e.target === overlay) close(); });
        }
        try { await wtpWallet.state(true); wtpWallet.repaint(); } catch (e) {}
      } catch (e) { /* a notice is a nicety — never break the page */ }
    }

    // Any "View credit history" link, anywhere.
    document.addEventListener("click", function (e) {
      var b = e.target && e.target.closest && e.target.closest("[data-open-history]");
      if (!b) return;
      e.preventDefault();
      var ov = b.closest(".wtp-modal-overlay");
      if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
      open();
    });

    return { open: open, checkNew: checkNew, describe: describe, rowsHtml: rowsHtml };
  })();

  // Mark that this visitor came to PLAY, so after they log in (even via Shopify's
  // OTP flow, which ignores return_url) the storefront bounces them to /pages/play.
  // The bounce lives in layout/theme.liquid and fires on the next signed-in page load.
  function markWantsPlay() { try { localStorage.setItem("wtp_wants_play", "1"); } catch (e) {} }

  // Land on the play dashboard after sign-in. NEW customer accounts (this store)
  // IGNORE ?return_url on the classic /account/login route — the working path is the
  // storefront-auth route /customer_authentication/login with a ?return_to=<relative,
  // url-encoded path>. (Shopify docs: storefronts/themes/sign-in.)
  function buildLoginHref(returnTo) {
    return "/customer_authentication/login?return_to=" + encodeURIComponent(returnTo || "/pages/play");
  }

  // ---- login required (only to PLAY a specific puzzle) ----------------------
  function renderLoginPrompt(puzzle) {
    stopTimer();
    markWantsPlay(); // logged-out visitor on the play flow → send them back here after login
    // Land on the dashboard after login (client's request) with a clean return_url.
    var href = buildLoginHref();
    root.innerHTML = "";
    root.appendChild(el(
      '<div class="wtp-play__login">' +
        "<h1>Log in to play</h1>" +
        '<p class="wtp-play__sub">This puzzle is free — just log in to your account to play' +
        (puzzle ? " it" : " it") + '.</p>' +
        '<a class="wtp-btn" href="' + esc(href) + '">Log in</a>' +
        '<p class="wtp-play__sub" style="margin-top:1rem">' +
        '<a class="wtp-link" href="/pages/play">← Back to all puzzles</a></p>' +
        "</div>",
    ));
  }

  // ---- dashboard (My Puzzles) ----------------------------------------------
  async function renderDashboard() {
    stopTimer();
    root.innerHTML = '<div class="wtp-play__loading">Loading puzzles…</div>';
    var products, hidden;
    try {
      // Both run in PARALLEL (products already prefetched at boot, Supabase is modulepreloaded
      // by the section). The hidden list must land BEFORE first paint, or a deactivated puzzle
      // would flash on screen before being removed.
      var loaded = await Promise.all([fetchAllProducts(), fetchInactiveIds()]);
      products = loaded[0];
      hidden = loaded[1] || {};
    } catch (e) {
      loadFailed(renderDashboard);
      return;
    }
    // Free-to-play: every digital puzzle is available to everyone — except ones the admin
    // has switched to Inactive, which are hidden from the list entirely.
    var digital = products.filter(function (p) { return isDigital(p) && !hidden[String(p.id)]; });

    // Card badges (leaderboard record + your best) are filled in AFTER first paint by
    // fetchBestTimes() below — the list itself must never wait on Supabase.

    function loggedInMeta() {
      return 'Playing as ' + wtpAvatar.html({ me: true, edit: true, name: playerName(), url: wtpAvatar.myUrl(), cls: "wtp-avatar--meta" }) + ' <strong class="wtp-playername">' + esc(playerName()) + "</strong>" +
        ' <button type="button" class="wtp-edit-name" title="Change your name">✎ edit</button>' +
        '<span class="wtp-wallet-slot"></span>';
    }
    if (!customerEmail) markWantsPlay(); // logged-out on the play page → bounce back here after login
    var metaHtml = customerEmail
      ? loggedInMeta()
      : '<a class="wtp-link" href="' + esc(buildLoginHref()) + '">Log in</a> to save your times &amp; join the leaderboard';

    root.innerHTML = "";
    var header = el(
      '<div class="wtp-play__header">' +
        "<h1>Play Digital Puzzles</h1>" +
        '<div class="wtp-play__meta">' + metaHtml + versionTag() + "</div>" +
        "</div>",
    );
    root.appendChild(header);
    function wireDashEdit() {
      var b = header.querySelector(".wtp-edit-name");
      if (b) b.addEventListener("click", editUsernameFlow);
    }
    wireDashEdit();
    try { wtpWallet.mount(header.querySelector(".wtp-wallet-slot")); } catch (e) {}
    // Don't block the (public, fast) list on Supabase — refresh the chosen name in
    // the background and update the "Playing as" text once it arrives.
    if (customerEmail) {
      fetchUsername().then(function () {
        var m = header.querySelector(".wtp-play__meta");
        if (m) {
          m.innerHTML = loggedInMeta() + versionTag(); wireDashEdit(); wtpAvatar.repaint();
          // This innerHTML swap destroys the mounted wallet chips - remount them.
          try { wtpWallet.mount(header.querySelector(".wtp-wallet-slot")); } catch (e) {}
        }
      }).catch(function () {});
    }

    if (!digital.length) {
      root.appendChild(el(
        '<div class="wtp-play__empty">' +
          "<p>No digital puzzles are available yet.</p>" +
          '<a class="wtp-btn" href="/collections/digital-puzzles">Browse the store</a>' +
          "</div>",
      ));
      return;
    }

    var grid = el('<div class="wtp-play__grid"></div>');
    digital.forEach(function (p) {
      var img = sizedImg(productImage(p), 600);
      grid.appendChild(el(
        '<a class="wtp-pcard" data-pid="' + esc(String(p.id)) + '" href="/pages/play?puzzle=' + encodeURIComponent(p.handle) + '">' +
          '<div class="wtp-pcard__media">' +
          (img ? '<img src="' + esc(img) + '" loading="lazy" alt="' + esc(p.title) + '">' : "") +
          "</div>" +
          '<div class="wtp-pcard__title">' + esc(p.title) + "</div>" +
          '<div class="wtp-pcard__badge"></div>' +
          '<div class="wtp-pcard__play">▶ Play</div>' +
          "</a>",
      ));
    });
    root.appendChild(grid);

    // Fill in the leaderboard record + personal best once Supabase arrives. BACKGROUND —
    // the (public, fast) card list never blocks on it. Same DOM-patch pattern as the
    // custom-image swap below.
    fetchBestTimes().then(function (best) {
      digital.forEach(function (p) {
        var card = grid.querySelector('.wtp-pcard[data-pid="' + String(p.id) + '"]');
        var slot = card && card.querySelector(".wtp-pcard__badge");
        if (slot) slot.innerHTML = badgeHtml(String(p.id), best);
      });
    }).catch(function () {});

    // Swap in admin-uploaded custom images in the background (never blocks first paint).
    fetchPuzzleMap().then(function (map) {
      digital.forEach(function (p) {
        var row = puzzleRowFor(map, p);
        if (!row || !row.custom_image_url) return;
        var card = grid.querySelector('.wtp-pcard[data-pid="' + String(p.id) + '"]');
        if (!card) return;
        var media = card.querySelector(".wtp-pcard__media");
        var im = media && media.querySelector("img");
        if (im) im.src = row.custom_image_url;
        else if (media) media.innerHTML = '<img src="' + esc(row.custom_image_url) + '" loading="lazy" alt="' + esc(p.title) + '">';
      });
    }).catch(function () {});
  }

  // M13: the store's product feed failed even after retries — say so honestly
  // and let the player retry, rather than "no puzzles" / "not found".
  function loadFailed(retry) {
    root.innerHTML = "";
    var box = el('<div class="wtp-play__msg wtp-loadfail">' +
      "<p>We couldn\u2019t load the puzzles just now \u2014 the store is busy for a moment.</p>" +
      '<button type="button" class="wtp-btn wtp-loadfail__retry">Try again</button></div>');
    box.querySelector(".wtp-loadfail__retry").addEventListener("click", retry);
    root.appendChild(box);
  }

  // ---- play view ------------------------------------------------------------
  async function renderPlay(handle, vs) {
    stopTimer();
    root.innerHTML = '<div class="wtp-play__loading">Loading puzzle…</div>';
    var loaded;
    try {
      loaded = await Promise.all([fetchAllProducts(), fetchInactiveIds()]);
    } catch (e) {
      loadFailed(function () { renderPlay(handle, vs); });   // not "Puzzle not found"
      return;
    }
    var products = loaded[0], hidden = loaded[1] || {};
    var product = products.find(function (p) { return p.handle === handle; });
    // An INACTIVE puzzle is treated exactly like one that doesn't exist. This is what blocks
    // direct-URL access, and it gives away nothing about which puzzles are hidden.
    if (product && hidden[String(product.id)]) product = null;
    if (!product) {
      root.innerHTML = '<div class="wtp-play__msg">Puzzle not found. <a href="/pages/play">Back</a></div>';
      return;
    }

    // Admin overrides (custom image + default difficulty) are fetched in the BACKGROUND
    // below so the play view never blocks on Supabase; applied to imageUrl/difficulty
    // when they arrive (before the player clicks Start).
    var pRow = null;

    // Look up the physical product URL for the "Buy this puzzle" button. Handles vary
    // and predictive search is fuzzy, so resolution matches on the puzzle NAME against
    // the physical catalogue — see resolvePhysicalUrl. Runs in the background so the
    // completion popup has it ready by the time the player solves the puzzle.
    physicalUrl = null;
    resolvePhysicalUrl(handle, product.title).then(function (u) { physicalUrl = u; });

    // NOTE: the display-name prompt used to sit HERE and block the whole view behind
    // Supabase (await sbReady + fetchUsername + showUsernameModal). It now runs in the
    // BACKGROUND at the end of renderPlay so the Start button never waits on Supabase.

    var imageUrl = productImage(product); // may be replaced by the admin custom image below
    root.innerHTML = "";
    // "Playing as <name> ✎ edit ·  ← All puzzles · vN" — one helper so the initial paint
    // and the background username refresh (end of renderPlay) stay identical.
    function playMetaHtml() {
      var playerLabel = customerEmail
        ? 'Playing as ' + wtpAvatar.html({ me: true, edit: true, name: playerName(), url: wtpAvatar.myUrl(), cls: "wtp-avatar--meta" }) + ' <strong class="wtp-playername">' + esc(playerName()) + "</strong>" +
          ' <button type="button" class="wtp-edit-name" title="Change your name">✎ edit</button> · '
        : "";
      return playerLabel + '<a class="wtp-link" href="/pages/play">← All puzzles</a>' + versionTag() +
        '<span class="wtp-wallet-slot"></span>';
    }
    var playHeader = el(
      '<div class="wtp-play__header">' +
        '<h1>' + esc(product.title) + "</h1>" +
        '<div class="wtp-play__meta">' + playMetaHtml() + "</div>" +
        "</div>",
    );
    root.appendChild(playHeader);
    function wirePlayEdit() {
      var b = playHeader.querySelector(".wtp-edit-name");
      if (b) b.addEventListener("click", editUsernameFlow);
    }
    wirePlayEdit();
    // Credits + Stash chips. mount() paints instantly from cache and refreshes in
    // the BACKGROUND, so the wallet never delays the board (the v42-v44 rule).
    try { wtpWallet.mount(playHeader.querySelector(".wtp-wallet-slot")); } catch (e) {}

    var controls = el(
      '<div class="wtp-game__controls">' +
        '<div class="wtp-tools wtp-tools--left">' +
          '<label class="wtp-tool-field">Difficulty <select class="wtp-difficulty">' +
          '<option value="2">Very Easy</option>' +
          '<option value="3" selected>Easy</option>' +
          '<option value="4">Casual</option>' +
          '<option value="5">Medium</option>' +
          '<option value="6">Tricky</option>' +
          '<option value="7">Hard</option>' +
          '<option value="8">Expert</option>' +
          "</select></label>" +
          '<button type="button" class="wtp-btn wtp-start">↻ Restart</button>' +
          '<button type="button" class="wtp-btn wtp-btn--primary wtp-submit">✓ Submit</button>' +
          '<button type="button" class="wtp-btn wtp-pause">⏸ Pause</button>' +
          '<button type="button" class="wtp-btn wtp-bw-open">⚡ Bells &amp; Whistles</button>' +
          '<button type="button" class="wtp-btn wtp-vs-open">' + wtpChallenge.ICON + ' Challenge a Friend</button>' +
        "</div>" +
        '<div class="wtp-tools wtp-tools--right">' +
          '<div class="wtp-zoom">' +
            '<button type="button" class="wtp-btn wtp-icon wtp-zoom-out" title="Zoom out" aria-label="Zoom out">−</button>' +
            '<button type="button" class="wtp-btn wtp-icon wtp-zoom-in" title="Zoom in" aria-label="Zoom in">+</button>' +
          "</div>" +
          '<button type="button" class="wtp-btn wtp-icon wtp-ref-toggle" title="Show/hide reference" aria-pressed="true">🖼</button>' +
          '<button type="button" class="wtp-btn wtp-icon wtp-mute-toggle" aria-pressed="false"></button>' +
          '<span class="wtp-timer" title="Time">00:00</span>' +
          '<div class="wtp-progress-wrap" title="Progress">' +
            '<span class="wtp-progress-bar"><span class="wtp-progress-fill"></span></span>' +
            '<span class="wtp-progress">0%</span>' +
          "</div>" +
        "</div>" +
        "</div>",
    );
    // v77: two columns on wide screens. LEFT = toolbar + board; RIGHT = the leaderboard,
    // which starts level with the toolbar (not under it). Narrower screens stack them,
    // leaderboard last. The board sizes itself from #wtp-canvas's width at Start.
    var layout = el('<div class="wtp-game__layout"><div class="wtp-game__main"></div></div>');
    var mainCol = layout.firstChild;
    root.appendChild(layout);
    mainCol.appendChild(controls);

    // Difficulty options show the REAL piece count for THIS photo (e.g. "Easy
    // (15 pieces)") — the board is aspect-aware, so the count varies per photo.
    // Plain names until the image dimensions are known; re-run when the admin
    // custom image replaces imageUrl (it may have a different aspect).
    var diffSelect = controls.querySelector(".wtp-difficulty");
    function updateDifficultyLabels(url) {
      if (!url || !diffSelect) return;
      var pre = new Image();
      pre.onload = function () {
        var aspect = pre.naturalWidth / Math.max(1, pre.naturalHeight);
        Array.prototype.forEach.call(diffSelect.options, function (opt) {
          var base = parseInt(opt.value, 10);
          if (!(base >= 2 && base <= 8)) return;
          var rc = gridFor(base, aspect);
          opt.textContent = DIFF_NAMES[base] + " (" + (rc.rows * rc.cols) + " pieces)";
        });
      };
      pre.src = url;
    }
    updateDifficultyLabels(imageUrl);
    var progressEl = controls.querySelector(".wtp-progress");
    var progressFill = controls.querySelector(".wtp-progress-fill");
    function setProgress(done, total) {
      var pct = total ? Math.round((done / total) * 100) : 0;
      if (progressFill) progressFill.style.width = pct + "%";
      if (progressEl) progressEl.textContent = pct + "%";
    }
    function updateProgress() {
      var p = progress();
      if (p[1]) setProgress(p[0], p[1]);
    }
    controls.querySelector(".wtp-submit").addEventListener("click", function () {
      manualSubmit(this);
    });
    var bwBtn = controls.querySelector(".wtp-bw-open");
    if (bwBtn) bwBtn.addEventListener("click", function () { wtpBW.open(); });
    var vsBtn = controls.querySelector(".wtp-vs-open");
    if (vsBtn) vsBtn.addEventListener("click", function () {
      var inGame = current && current.productId === String(product.id);
      wtpChallenge.open({
        productId: String(product.id), handle: product.handle, title: product.title, imageUrl: imageUrl,
        grid: inGame ? current.grid : parseInt(controls.querySelector(".wtp-difficulty").value, 10),
        elapsed: inGame ? Math.floor(elapsedMs() / 1000) : 0,
      });
    });
    // M7: arrived via a challenge — set its difficulty (never mid-game) + banner.
    wtpChallenge.attach(vs, product, controls, function (d) {
      if (started) return;
      controls.querySelector(".wtp-difficulty").value = String(d);
      showReady(null);
    });

    var stage = el(
      '<div class="wtp-game__stage">' +
        '<div id="wtp-canvas" class="wtp-canvas"></div>' +
        '<div class="wtp-preview"><span class="wtp-preview-label">Reference</span>' +
        '<img class="wtp-preview-img" alt="reference image"></div>' +
        '<div class="wtp-pause-overlay" style="display:none"><div>⏸ Paused' +
        '<br><small>Click Resume to continue</small></div></div>' +
        '<div class="wtp-win" style="display:none">🎉 Solved! <span class="wtp-win-time"></span></div>' +
        '<span class="wtp-hint">Tip: double-click / double-tap a piece to separate it from a group</span>' +
        '<span class="wtp-buildtag">' + WTP_PLAY_VERSION + "</span>" +
        "</div>",
    );
    mainCol.appendChild(stage);              // under the toolbar, in the left column (v77)
    var pauseBtn = controls.querySelector(".wtp-pause");
    var pauseOverlay = stage.querySelector(".wtp-pause-overlay");
    pauseBtn.addEventListener("click", function () {
      if (!timer.start || current && current.recorded) return; // nothing to pause
      togglePause();
      pauseBtn.textContent = timer.paused ? "▶ Resume" : "⏸ Pause";
      pauseOverlay.style.display = timer.paused ? "" : "none";
      if (timer.paused) saveProgressNow(); // persist when stepping away
    });

    var board = el('<div class="wtp-leaderboard"></div>');
    layout.appendChild(board);

    var previewBox = stage.querySelector(".wtp-preview");
    var refBtn = controls.querySelector(".wtp-ref-toggle");
    refBtn.addEventListener("click", function () {
      var show = previewBox.style.display === "none";
      previewBox.style.display = show ? "" : "none";
      refBtn.setAttribute("aria-pressed", show ? "true" : "false");
      refBtn.classList.toggle("wtp-icon--off", !show);
    });
    // v78: sound on/off — remembered on this device (wtpAudio.setMuted).
    var muteBtn = controls.querySelector(".wtp-mute-toggle");
    function paintMute() {
      var m = wtpAudio.isMuted();
      muteBtn.textContent = m ? "🔇" : "🔊";
      muteBtn.title = m ? "Sound is off — tap to turn it on" : "Sound is on — tap to mute";
      muteBtn.setAttribute("aria-label", m ? "Unmute sounds" : "Mute sounds");
      muteBtn.setAttribute("aria-pressed", m ? "true" : "false");
      muteBtn.classList.toggle("wtp-icon--off", m);
    }
    paintMute();
    muteBtn.addEventListener("click", function () { wtpAudio.setMuted(!wtpAudio.isMuted()); paintMute(); });
    // Zoom the whole board (engine keeps snapping; the drag clamp is zoom-aware).
    function applyZoom() {
      if (!current || !current.canvas) return;
      try { current.canvas.scale(playZoom); current.canvas.redraw(); } catch (e) {}
      try { if (current.reapplyPhoto) current.reapplyPhoto(); } catch (e) {}
    }
    controls.querySelector(".wtp-zoom-in").addEventListener("click", function () {
      playZoom = Math.min(2.2, Math.round(playZoom * 1.2 * 100) / 100); applyZoom();
    });
    controls.querySelector(".wtp-zoom-out").addEventListener("click", function () {
      playZoom = Math.max(0.6, Math.round((playZoom / 1.2) * 100) / 100); applyZoom();
    });
    // Mouse-wheel over the board zooms in (scroll up) / out (scroll down).
    stage.addEventListener("wheel", function (e) {
      if (!current || !current.canvas) return;
      e.preventDefault();
      if (e.deltaY < 0) playZoom = Math.min(2.2, Math.round(playZoom * 1.1 * 100) / 100);
      else playZoom = Math.max(0.6, Math.round((playZoom / 1.1) * 100) / 100);
      applyZoom();
    }, { passive: false });

    // Update the piece counter + auto-detect completion on each piece release.
    stage.addEventListener("pointerup", function () {
      setTimeout(function () {
        if (!current || current.recorded) return;
        // A piece detached by a double-click keeps its exact position, which is still
        // INSIDE the engine's connect proximity — a plain autoconnect() here would
        // silently re-lock it (the "double-click snaps the piece back" bug). Skip the
        // flush-meshing pass briefly after any successful detach; re-snapping by real
        // dragging is unaffected (the engine's own dragend → drop() still autoconnects).
        if (Date.now() < suppressAutoconnectUntil) {
          try { if (current.reapplyPhoto) current.reapplyPhoto(); } catch (e) {}
          updateProgress();
          if (isArrangedCorrectly()) recordSolve();
          else scheduleSave();
          return;
        }
        // Snap pieces that were dropped near a matching neighbour so they mesh
        // FLUSH (tab into blank, no gaps) — like a real jigsaw.
        try { current.canvas.autoconnect(); current.canvas.redraw(); } catch (e) {}
        // Re-assert the photo mapping (idempotent) in case anything re-filled it.
        try { if (current.reapplyPhoto) current.reapplyPhoto(); } catch (e) {}
        updateProgress();
        if (isArrangedCorrectly()) recordSolve();
        else scheduleSave();
      }, 60);
    });

    // Load the leaderboard on demand, de-duped by difficulty so opening then starting at
    // the same level never fires the query twice (force=true bypasses — e.g. after a new
    // score is submitted). The leaderboard is ALWAYS independent of the Start button.
    var lastLbGrid = null;
    function refreshLeaderboard(g, force) {
      if (!force && g === lastLbGrid) return;
      lastLbGrid = g;
      loadLeaderboard(board, String(product.id), g);
    }
    function startGame(resume) {
      var grid = resume && resume.difficulty
        ? resume.difficulty
        : parseInt(controls.querySelector(".wtp-difficulty").value, 10);
      controls.querySelector(".wtp-difficulty").value = String(grid);
      buildPuzzle(product, imageUrl, grid, controls.querySelector(".wtp-timer"),
        stage.querySelector(".wtp-win"), board, resume);
      refreshLeaderboard(grid);
      pauseBtn.textContent = "⏸ Pause";
      pauseOverlay.style.display = "none";
      setProgress(0, 1); // 0% until the board is built
    }
    // The timer must NOT auto-start. Show a "Ready" screen with a Start/Resume
    // button; building the board + starting the timer only happens on click.
    var host = document.getElementById("wtp-canvas");
    var started = false;
    var pendingResume = null;
    // Once the player picks a difficulty themselves, background arrivals (the admin
    // default level, a saved game) must not switch it back. Found by the M12 live
    // test: picking a level in the first second was overwritten when they landed.
    var userPicked = false;

    // Background: apply the admin custom image + default difficulty when they arrive,
    // so opening a puzzle is instant (no Supabase wait) and Start still uses them.
    fetchPuzzleMap().then(function (map) {
      pRow = puzzleRowFor(map, product);
      if (!pRow) return;
      if (pRow.custom_image_url) {
        imageUrl = pRow.custom_image_url;
        try { var pv = stage.querySelector(".wtp-preview-img"); if (pv) pv.src = imageUrl; } catch (e) {}
        updateDifficultyLabels(imageUrl);
      }
      if (pRow.difficulty && !started && !vs && !userPicked) {
        var ad = parseInt(pRow.difficulty, 10);
        if (ad >= 2 && ad <= 8) {
          try { var ds = controls.querySelector(".wtp-difficulty"); if (ds) ds.value = String(ad); } catch (e) {}
          refreshLeaderboard(ad); // keep the board in sync with the admin default level
        }
      }
    }).catch(function () {});

    function showReady(resume) {
      current = null;               // no active game until the player starts
      var g = resume && resume.difficulty
        ? resume.difficulty
        : parseInt(controls.querySelector(".wtp-difficulty").value, 10);
      controls.querySelector(".wtp-difficulty").value = String(g);
      refreshLeaderboard(g); // leaderboard loads/refreshes for this level, in the background
      controls.querySelector(".wtp-timer").textContent =
        resume && resume.elapsed ? fmtTime(resume.elapsed) : "00:00";
      setProgress(0, 1);
      var pv = stage.querySelector(".wtp-preview-img"); // show the reference up front
      if (pv && imageUrl) pv.src = imageUrl;
      host.innerHTML =
        '<div class="wtp-ready">' +
        "<p>" + (resume ? "You have a saved game in progress." : "Ready when you are!") + "</p>" +
        '<button type="button" class="wtp-btn wtp-ready-start">▶ ' +
        (resume ? "Resume" : "Start") + "</button>" +
        "</div>";
      host.querySelector(".wtp-ready-start").addEventListener("click", beginGame);
    }

    function beginGame() {
      started = true;
      if (pendingResume) {
        var r = pendingResume; pendingResume = null;
        startGame(r);               // resume the saved board (timer continues)
      } else {
        startGame(null);            // fresh (timer from 0)
      }
    }

    // Toolbar "Start / Restart" always begins a fresh game.
    controls.querySelector(".wtp-start").addEventListener("click", function () {
      pendingResume = null;
      started = true;
      clearProgress(String(product.id));
      startGame(null);
    });
    // Changing difficulty resets to a fresh, not-yet-started board.
    controls.querySelector(".wtp-difficulty").addEventListener("change", function () {
      userPicked = true;
      clearProgress(String(product.id));
      pendingResume = null;
      if (started) startGame(null); // mid-game change restarts immediately
      else showReady(null);         // still on the ready screen
    });

    // On open: prime the Ready screen IMMEDIATELY so the Start button never waits on
    // Supabase. Saved-progress, the leaderboard, and the display-name prompt all load in
    // the BACKGROUND (mirrors renderDashboard). This is the core of the fix.
    showReady(null);

    // Background: if a saved game exists (and the player hasn't already started a fresh
    // one), upgrade the Ready button to "Resume". loadProgress waits for Supabase itself.
    (async function () {
      try {
        var saved = await loadProgress(String(product.id));
        if (started || vs || userPicked || !(saved && saved.offsets)) return;
        pendingResume = { difficulty: saved.difficulty, offsets: saved.offsets, elapsed: saved.elapsed_seconds };
        showReady(pendingResume);
      } catch (e) {}
    })();

    // Background: first-time display-name prompt — dismissible, over the Ready screen,
    // never delaying Start and never interrupting a game already in progress (!started).
    if (customerEmail && !usernameSkipped) {
      (async function () {
        try {
          if (!sb) await sbReady;
          if (!(await fetchUsername()) && !started) await showUsernameModal(customerName);
        } catch (e) {}
        try {
          var meta = playHeader.querySelector(".wtp-play__meta");
          if (meta) {
            meta.innerHTML = playMetaHtml(); wirePlayEdit(); wtpAvatar.repaint();
            // Same as the dashboard: re-mount after the innerHTML swap.
            try { wtpWallet.mount(playHeader.querySelector(".wtp-wallet-slot")); } catch (e) {}
          }
        } catch (e) {}
      })();
    }
  }

  // ---- classic jigsaw outline v2 (drawn by us, point by point) ----------------
  // Seeded 0..1 hash — deterministic per key, so both neighbours of a seam derive
  // identical knob parameters (position, size, side) and stay complementary.
  function seamRand(n) {
    var x = ((n + 7) * 2654435761) >>> 0;
    x = ((x ^ (x >>> 13)) * 1274126177) >>> 0;
    return ((x ^ (x >>> 16)) >>> 0) / 4294967295;
  }
  function seamParams(key) {
    return {
      tab: seamRand(key) < 0.5,                    // which side gets the tab
      shift: 0,                                     // classic tabs sit centred on the edge
      scale: 0.92 + seamRand(key + 202) * 0.16,    // tab size 92%..108% (subtle natural variation)
      bow: 0,                                       // straight edges between tabs (traditional jigsaw)
    };
  }

  // One edge as a dense polyline shaped like a TRUE classic jigsaw tab: a flat run,
  // then a knob built from the canonical 3-cubic-Bézier profile (Draradech) — the
  // neck first UNDERCUTS to −t so the round ball OVERHANGS it (the hallmark of a real
  // jigsaw piece), then a flat run. Mirror-symmetric about the centre, so the neighbour
  // tracing the same seam in reverse lands on the identical world points (zero-gap fit).
  // (nx,ny) = outward normal; ins = +1 tab out / −1 tab in (socket) / 0 straight;
  // shift moves the tab centre (world-axis, negated by the caller for reverse edges);
  // scale sizes the tab; bow is accepted but unused (edges are straight in the classic look).
  function wtpEdge(ax, ay, bx, by, nx, ny, ins, out, shift, scale, bow) {
    var ex = bx - ax, ey = by - ay;
    var L = Math.sqrt(ex * ex + ey * ey);
    var ux = ex / L, uy = ey / L;
    if (!ins) { out.push(bx, by); return; }
    function emit(al, pe) { out.push(ax + al * ux + pe * nx, ay + al * uy + pe * ny); }
    var t = 0.1 * (scale || 1);        // tab size (Draradech classic default ≈ 20%)
    var m = 0.5 + (shift || 0);        // tab centre along the edge (fraction of L)
    // Control points in NORMALIZED edge units [alongFraction, perpFraction]:
    // P0 C1 C2 | P3 C4 C5 | P6 C7 C8 P9. The −t at C2/C7 is the neck undercut.
    var P = [
      [0.0, 0.0], [0.2, 0.0], [m, -t], [m - t, t],
      [m - 2 * t, 3 * t], [m + 2 * t, 3 * t], [m + t, t],
      [m, -t], [0.8, 0.0], [1.0, 0.0],
    ];
    function bez(p0, c1, c2, p3, steps, startI) {
      for (var i = startI; i <= steps; i++) {
        var uu = i / steps, vv = 1 - uu;
        var al = vv*vv*vv*p0[0] + 3*vv*vv*uu*c1[0] + 3*vv*uu*uu*c2[0] + uu*uu*uu*p3[0];
        var pe = vv*vv*vv*p0[1] + 3*vv*vv*uu*c1[1] + 3*vv*uu*uu*c2[1] + uu*uu*uu*p3[1];
        emit(al * L, ins * pe * L);
      }
    }
    var N = 10;
    bez(P[0], P[1], P[2], P[3], N, 0); // flat run → neck (incl. corner A)
    bez(P[3], P[4], P[5], P[6], N, 1); // the round ball
    bez(P[6], P[7], P[8], P[9], N, 1); // neck → flat run (incl. corner B)
  }

  // Outline object for headbreaker: plain polyline (isBezier false, use with
  // lineSoftness 0) so every Konva version renders it EXACTLY as computed.
  // Internal seams: complementary classic tab pairs (same seam key both sides).
  // Border edges (whole-puzzle perimeter): FLAT straight edges with square corners —
  // a classic rectangular jigsaw (client request); knobs only on internal seams.
  function WtpClassicOutline(dims) { this.rows = dims.rows; this.cols = dims.cols; }
  WtpClassicOutline.prototype.isBezier = function () { return false; };
  WtpClassicOutline.prototype.draw = function (piece, size, _borderFill) {
    var s = (size && typeof size === "object") ? size.x : size;
    var rows = this.rows, cols = this.cols;
    // Derive row/col from the piece's ACTUAL solved position on the board (pieces
    // are sketched while laid out solved). The image region is assigned from the
    // same position, so shape and image can NEVER disagree — regardless of how
    // the engine orders pieces internally (ids proved unreliable in the browser).
    var row = 0, col = 0;
    try {
      var pcs = piece.puzzle.pieces;
      var minX = Infinity, minY = Infinity;
      for (var i = 0; i < pcs.length; i++) {
        var a = pcs[i].centralAnchor;
        if (a.x < minX) minX = a.x;
        if (a.y < minY) minY = a.y;
      }
      col = Math.max(0, Math.min(cols - 1, Math.round((piece.centralAnchor.x - minX) / s)));
      row = Math.max(0, Math.min(rows - 1, Math.round((piece.centralAnchor.y - minY) / s)));
    } catch (e) {
      var idx = (parseInt(piece.metadata && piece.metadata.id, 10) || 1) - 1;
      row = Math.floor(idx / cols); col = idx % cols;
    }
    // Keys: internal seams (shared by both neighbours) + border edges (one owner).
    function vKey(r0, c0) { return r0 * 4096 + c0 * 2; }             // seam col c0 | c0+1
    function hKey(r0, c0) { return 1048576 + r0 * 4096 + c0 * 2; }   // seam row r0 | r0+1
    function bKey(side, i0) { return 4194304 + side * 65536 + i0; }  // border edge

    // Per edge: {ins, shift, scale}. Internal seams: p.tab means the LEFT (for
    // vertical) / UPPER (for horizontal) piece owns the tab; the other side is the
    // complementary blank. `shift` is defined on the WORLD axis of the seam, so
    // edges drawn in the reverse direction (down: BR→BL, left: BL→TL) negate it.
    // OUTER edges of the whole puzzle are FLAT (ins = 0) → straight borders and square
    // corners, a classic rectangular jigsaw (client request); knobs only on inner seams.
    var pu = row === 0 ? null : seamParams(hKey(row - 1, col));
    var upIns = row === 0 ? 0 : (pu.tab ? -1 : 1);
    var pr = col === cols - 1 ? null : seamParams(vKey(row, col));
    var rightIns = col === cols - 1 ? 0 : (pr.tab ? 1 : -1);
    var pd = row === rows - 1 ? null : seamParams(hKey(row, col));
    var downIns = row === rows - 1 ? 0 : (pd.tab ? 1 : -1);
    var pl = col === 0 ? null : seamParams(vKey(row, col - 1));
    var leftIns = col === 0 ? 0 : (pl.tab ? -1 : 1);

    // Border edges pass a null param; wtpEdge ignores shift/scale/bow when ins === 0.
    var pts = [0, 0];
    wtpEdge(0, 0, s, 0, 0, -1, upIns, pts, pu ? pu.shift : 0, pu ? pu.scale : 1, pu ? pu.bow : 0);       // top (dir +x)
    wtpEdge(s, 0, s, s, 1, 0, rightIns, pts, pr ? pr.shift : 0, pr ? pr.scale : 1, pr ? pr.bow : 0);     // right (dir +y)
    wtpEdge(s, s, 0, s, 0, 1, downIns, pts, pd ? -pd.shift : 0, pd ? pd.scale : 1, pd ? pd.bow : 0);     // bottom (dir −x)
    wtpEdge(0, s, 0, 0, -1, 0, leftIns, pts, pl ? -pl.shift : 0, pl ? pl.scale : 1, pl ? pl.bow : 0);    // left (dir −y)
    return pts;
  };

  // Difficulty level names keyed by the difficulty BASE (2..8), shared by the
  // toolbar dropdown, the ready screen and the leaderboard chip.
  var DIFF_NAMES = { 2: "Very Easy", 3: "Easy", 4: "Casual", 5: "Medium", 6: "Tricky", 7: "Hard", 8: "Expert" };

  // RECTANGULAR grid fitted to the photo (client: minimize cropping): the difficulty
  // BASE (2..8) goes to the photo's SHORTER side and the longer side gets
  // proportionally more, so the board keeps (almost) the photo's aspect ratio.
  // Shared by buildPuzzle (deals the board) and the toolbar (shows the REAL piece
  // count per difficulty option — the count varies per photo).
  function gridFor(base, aspect) {
    var a = Math.max(0.4, Math.min(2.6, aspect)); // relaxed so the board can match wide/tall photos
    var cap = Math.max(base, Math.round(base * 2.6));
    var rows, cols;
    if (Math.abs(a - 1) < 0.10) { rows = base; cols = base; }
    else if (a > 1) { rows = base; cols = Math.min(cap, Math.max(base, Math.round(base * a))); }
    else { cols = base; rows = Math.min(cap, Math.max(base, Math.round(base / a))); }
    return { rows: rows, cols: cols };
  }

  async function buildPuzzle(product, imageUrl, grid, timerEl, winEl, boardEl, resume) {
    stopTimer();
    winEl.style.display = "none";
    try { wtpFoil.clearBoard(); } catch (e) {}   // v88: a new board starts without the last finish
    var host = document.getElementById("wtp-canvas");
    host.innerHTML = '<div class="wtp-play__msg">Loading game engine…</div>';

    var headbreaker;
    try {
      headbreaker = await ensureHeadbreaker();
    } catch (e) {
      host.innerHTML = '<div class="wtp-play__msg">Could not load the game engine. Check your connection and click “Start / Restart”.</div>';
      return;
    }
    host.innerHTML = "";

    var img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = function () {
      // --- RECTANGULAR grid fitted to the photo (client: minimize cropping) ----
      // `grid` (2..8) is the difficulty BASE — see gridFor() above.
      var aspect = img.naturalWidth / Math.max(1, img.naturalHeight);
      var rc = gridFor(grid, aspect);
      var rows = rc.rows, cols = rc.cols;

      // The Konva stage must EXACTLY fill its container, else the extra container
      // width becomes dead (non-draggable) space on the sides. Use the live width
      // (the .wtp-game__stage card is capped in CSS so this stays a sane size).
      var hostW = host.clientWidth || host.parentElement && host.parentElement.clientWidth || 900;
      var stageW = Math.max(320, Math.min(hostW, 1600));
      // Piece size (proven from the scatter layout — reads clearly, not too big).
      var assembledW = Math.min(stageW * 0.5, 430);
      var pieceSize = Math.max(38, Math.floor(assembledW / cols));
      var boardW = pieceSize * cols, boardH = pieceSize * rows;
      // Pieces sit in ONE neat grid that fills the width; the canvas height fits that
      // grid COMPACTLY. Cell = 1.55·P → 0.55·P gap so adjacent tray pieces never
      // auto-snap (gap > proximity 0.5·P). The top-right corner is RESERVED for the
      // Reference card so pieces are never laid hidden behind it.
      var trayPad = Math.round(pieceSize * 0.5);
      var trayCell = Math.round(pieceSize * 1.55);
      var trayCols = Math.max(1, Math.floor((stageW - trayPad * 2) / trayCell));
      var refW = Math.min(240, Math.round(stageW * 0.32)); // Reference card footprint
      var refH = 250, refLeft = stageW - refW;
      var reservedCells = Math.ceil(refW / trayCell) * Math.ceil(refH / trayCell);
      var trayTop = trayPad;
      var trayRows = Math.ceil((rows * cols + reservedCells) / trayCols);
      var stageH = Math.max(440, trayTop + trayRows * trayCell + trayPad);

      // Flat borders → pieces never poke past the board, so NO bleed ring is needed.
      // COVER-fit the photo to fill the board EDGE-TO-EDGE (the board's aspect already
      // ≈ the photo's via the grid, so the crop is tiny) — this removes the contain-fit
      // side margins that showed up as an "extra border / line on both sides".
      var bleed = 0;
      var offW = boardW, offH = boardH;
      var off = document.createElement("canvas");
      off.width = offW; off.height = offH;
      var octx = off.getContext("2d");
      var scale = Math.max(offW / img.naturalWidth, offH / img.naturalHeight);
      var dw = img.naturalWidth * scale, dh = img.naturalHeight * scale;
      octx.drawImage(img, (offW - dw) / 2, (offH - dh) / 2, dw, dh);

      // Preview thumbnail shows the BOARD region the pieces reconstruct (no bleed).
      try {
        var pv = document.querySelector(".wtp-preview-img");
        if (pv) {
          var pvc = document.createElement("canvas");
          pvc.width = boardW; pvc.height = boardH;
          pvc.getContext("2d").drawImage(off, bleed, bleed, boardW, boardH, 0, 0, boardW, boardH);
          pv.src = pvc.toDataURL();
        }
      } catch (e) { /* cross-origin: leave preview empty */ }

      try {
        // Our own classic jigsaw outline v2 (WtpClassicOutline): classic tabs on
        // internal seams (complementary per seam) + FLAT straight outer borders.
        // Drawn as an exact polyline → lineSoftness MUST be 0 (no tension warping)
        // and borderFill MUST be 0 (no piece enlargement/overlap painting over the
        // neighbours' knob outlines).
        var canvasOpts = {
          width: stageW, height: stageH, pieceSize: pieceSize,
          proximity: Math.max(18, Math.floor(pieceSize * 0.5)),
          borderFill: 0,
          strokeWidth: 1.5, lineSoftness: 0,
          image: off, painter: new headbreaker.painters.Konva(),
          preventOffstageDrag: true, fixed: false,
          outline: new WtpClassicOutline({ rows: rows, cols: cols }),
        };
        var canvas = new headbreaker.Canvas("wtp-canvas", canvasOpts);
        canvas.autogenerate({ horizontalPiecesCount: cols, verticalPiecesCount: rows });
        // DRAG MODEL (hybrid): connected pieces move together as ONE cluster for the WHOLE
        // drag — they never come apart from mere direction changes. Separation is instead
        // a DELIBERATE gesture (double-click / double-tap a piece → piece.disconnect(),
        // wired below in the per-piece loop) so a player can pull a piece back out of a
        // group whenever they want, while an ordinary drag keeps the group intact.
        // NOTE: forceConnectionWhileDragging lives on the PUZZLE, not the Canvas. Calling
        // it on the canvas silently no-ops (method is undefined there), which left the
        // engine in its default TryDisconnection mode — that mode disconnects the grabbed
        // piece the moment a move "opens" its connectors (i.e. on a direction change),
        // breaking the group. Set the mode on canvas.puzzle so it actually takes effect.
        try {
          var dragOwner = canvas.puzzle || canvas;
          if (dragOwner && typeof dragOwner.forceConnectionWhileDragging === "function") {
            dragOwner.forceConnectionWhileDragging();
          } else if (typeof canvas.forceConnectionWhileDragging === "function") {
            canvas.forceConnectionWhileDragging();
          }
        } catch (e) {}
        canvas.draw();                      // first draw lays pieces in SOLVED positions

        // CRITICAL photo-mapping fix: headbreaker + Konva 6 assigns each piece's
        // photo region wrapped to the OPPOSITE board corner (Konva translates the
        // fill pattern by −offset; headbreaker's computed offsets assume the other
        // sign). Result: players had to assemble the picture in mirrored positions,
        // which made the piece shapes look inverted and blocked snapping all along.
        // Fix: while pieces still sit in the solved layout, point each piece's fill
        // pattern at the photo region under it — photo pixel (anchor − radius) must
        // sit at the piece's top-left corner (local 0,0).
        // The manufacturer lays the puzzle out one whole piece from the origin
        // (first piece's centre at (pieceSize, pieceSize)). A piece at grid
        // (row,col) must show photo region (col*pieceSize, row*pieceSize) at its
        // top-left corner — which is exactly (anchor − pieceSize) on both axes.
        // Compute ONCE from the SOLVED layout, store per piece, and re-assert on
        // every interaction so nothing in the engine can silently undo it.
        var photoOffsets = {};
        try {
          canvas.puzzle.pieces.forEach(function (pc) {
            try {
              var pid = pc.metadata && pc.metadata.id;
              var fg = canvas.figures[pid];
              if (!pid || !fg || !fg.shape || typeof fg.shape.fillPatternOffset !== "function") return;
              var sc = 1;
              try { var scv = fg.shape.fillPatternScale(); if (scv && scv.x) sc = scv.x; } catch (e2) {}
              // Piece (r,c)'s region top-left sits at (bleed + c·P, bleed + r·P)
              // inside the bled off-canvas → offset = bleed + (anchor − P).
              photoOffsets[pid] = {
                x: (bleed + pc.centralAnchor.x - pieceSize) / sc,
                y: (bleed + pc.centralAnchor.y - pieceSize) / sc,
              };
            } catch (e3) { /* skip this piece */ }
          });
        } catch (e) { /* leave engine defaults if the internals differ */ }
        function applyPhotoOffsets() {
          try {
            Object.keys(photoOffsets).forEach(function (pid) {
              try {
                var fg = canvas.figures[pid];
                if (fg && fg.shape && typeof fg.shape.fillPatternOffset === "function") {
                  fg.shape.fillPatternOffset(photoOffsets[pid]);
                }
              } catch (e1) {}
            });
            canvas.redraw();
          } catch (e) {}
        }
        applyPhotoOffsets();

        var solvedPts = readPoints(canvas); // capture those solved positions

        // PROFESSIONAL JIGSAW RULE: only the CORRECT neighbour may connect.
        // By default headbreaker connects ANY close tab↔slot pair, letting pieces
        // snap together in the wrong arrangement (scrambled picture). We gate every
        // connection: the two pieces' CURRENT relative offset must match their
        // SOLVED relative offset (right piece, right side) or they won't click.
        try {
          var solvedByIdx = {};
          canvas.puzzle.pieces.forEach(function (pc, i) {
            if (solvedPts && solvedPts[i]) solvedByIdx[pc.metadata.id] = solvedPts[i];
          });
          var reqTol = Math.max(24, Math.floor(pieceSize * 0.75));
          canvas.attachConnectionRequirement(function (one, other) {
            try {
              var a = solvedByIdx[one.metadata.id], b = solvedByIdx[other.metadata.id];
              if (!a || !b) return true;
              var sdx = a[0] - b[0], sdy = a[1] - b[1];           // solved offset
              var cdx = one.centralAnchor.x - other.centralAnchor.x; // current offset
              var cdy = one.centralAnchor.y - other.centralAnchor.y;
              return Math.abs(sdx - cdx) <= reqTol && Math.abs(sdy - cdy) <= reqTol;
            } catch (e) { return true; }
          });
        } catch (e) { /* engine without requirement API: keep default */ }

        var resumedMs = 0, didResume = false;
        if (resume && resume.offsets && resume.offsets.length === rows * cols) {
          try {
            var pcs = canvas.puzzle.pieces;
            resume.offsets.forEach(function (o, i) {
              if (pcs[i] && o) pcs[i].drag(o[0] * pieceSize, o[1] * pieceSize, true);
            });
            canvas.redraw();
            resumedMs = (resume.elapsed || 0) * 1000;
            didResume = true;
          } catch (e) { didResume = false; }
        }
        if (!didResume) {
          // Lay unsolved pieces in a NEAT GRID tray (organized, not random scatter).
          // Shuffle the ORDER so the picture isn't pre-solved, but positions stay tidy.
          try {
            var pcs2 = canvas.puzzle.pieces;
            var order = pcs2.map(function (_, i) { return i; });
            for (var oi = order.length - 1; oi > 0; oi--) {
              var oj = Math.floor(Math.random() * (oi + 1));
              var ot = order[oi]; order[oi] = order[oj]; order[oj] = ot;
            }
            canvas.puzzle.disconnect();
            // Build grid slots row-by-row, SKIPPING any cell that would sit under the
            // top-right Reference card, so every piece stays visible and grabbable.
            var slots = [];
            for (var rr = 0; slots.length < pcs2.length; rr++) {
              for (var cc = 0; cc < trayCols && slots.length < pcs2.length; cc++) {
                var sx = trayPad + cc * trayCell + trayCell / 2;
                var sy = trayTop + rr * trayCell + trayCell / 2;
                if (sx + trayCell / 2 > refLeft && sy - trayCell / 2 < refH) continue; // under Reference
                slots.push([sx, sy]);
              }
            }
            order.forEach(function (pi, i) {
              var s = slots[i];
              if (s) pcs2[pi].relocateTo(s[0], s[1]);
            });
          } catch (e) { canvas.shuffle(0.85); } // fallback to scatter if the grid fails
          canvas.redraw();
        }
        playZoom = 1; // fresh board renders at 1× (reset any prior zoom)
        applyPhotoOffsets(); // re-assert after resume/grid redraws
        activeCanvas = canvas;
        current = {
          canvas: canvas, grid: grid, rows: rows, cols: cols, timerEl: timerEl,
          winEl: winEl, boardEl: boardEl, productId: String(product.id), recorded: false,
          solved: solvedPts, tol: Math.max(30, Math.floor(pieceSize * 0.5)), pieceSize: pieceSize,
          reapplyPhoto: applyPhotoOffsets,
          title: product.title, handle: product.handle, imageUrl: imageUrl, hints: 0,
        };
        current.hints = hintsStart(current.productId, grid, didResume); // M6: hints used
        // Reset the progress bar to 0% now that the real grid exists.
        try {
          var pe = document.querySelector(".wtp-progress");
          if (pe) pe.textContent = "0%";
          var pf = document.querySelector(".wtp-progress-fill");
          if (pf) pf.style.width = "0%";
        } catch (e) {}

        startTimer(timerEl, resumedMs);
        // Auto-detect uses our own STRICT cluster check (isArrangedCorrectly) on each
        // piece release. headbreaker's onValid fired unreliably (false "solved"), so we
        // no longer record from it — only a real full assembly (or the ✓ Submit button).
        canvas.autoconnect(); // pieces snap together when dragged near a correct neighbour
        applyPhotoOffsets(); // and once more after initial autoconnect

        // Keep every piece FULLY inside the play area while dragging. The engine's
        // built-in offstage guard only stops the piece CENTRE at the edge, so the outer
        // half + knobs still slide off-screen (client report). Override each piece's
        // Konva dragBoundFunc with a margin = half-piece (0.5·P) + knob reach (~0.3·P).
        try {
          var dragMgn = Math.round(pieceSize * 0.85); // half-piece + knob reach
          // Extents of the connected cluster around the grabbed piece (from its centre),
          // recomputed at each drag start. The clamp bounds the GRABBED piece so the WHOLE
          // cluster stays on-screen — so a connected group moves freely in every direction
          // instead of being pinned when one of its pieces is near an edge.
          var dragExt = { l: dragMgn, r: dragMgn, t: dragMgn, b: dragMgn };
          function clusterExtents(pc0) {
            var half = dragMgn, seen = {}, stack = [pc0], a0 = pc0.centralAnchor, guard = 0;
            var minX = a0.x, maxX = a0.x, minY = a0.y, maxY = a0.y;
            while (stack.length && guard++ < 5000) {
              var p = stack.pop();
              var id = p.metadata && p.metadata.id;
              if (id == null || seen[id]) continue;
              seen[id] = true;
              var a = p.centralAnchor;
              if (a.x - half < minX) minX = a.x - half;
              if (a.x + half > maxX) maxX = a.x + half;
              if (a.y - half < minY) minY = a.y - half;
              if (a.y + half > maxY) maxY = a.y + half;
              (p.presentConnections || []).forEach(function (c) { if (c) stack.push(c); });
            }
            return { l: a0.x - minX, r: maxX - a0.x, t: a0.y - minY, b: maxY - a0.y };
          }
          // DELIBERATE SEPARATE: pop a single piece OUT of its connected group. Only this
          // piece's own links are severed (piece.disconnect() clears its up/down/left/right
          // + the neighbours' back-refs), so every OTHER correct connection in the group
          // stays intact. The piece does NOT move — it stays exactly where it is and is
          // immediately grabbable on its own (any small nudge would sit inside the engine's
          // autoconnect proximity and get silently re-snapped by the pointerup pass below).
          // Solve-detection is position-based (isArrangedCorrectly), so disconnecting never
          // affects whether the puzzle counts as solved.
          function detachPiece(pc0) {
            try {
              if (!pc0 || !pc0.connected) return false;
              pc0.disconnect();
              suppressAutoconnectUntil = Date.now() + 600; // pointerup pass must not re-lock it
              canvas.redraw();
              applyPhotoOffsets();
              wtpAudio.play("drop"); // soft feedback that it detached
              return true;
            } catch (e) { return false; }
          }
          canvas.puzzle.pieces.forEach(function (pc) {
            var pid = pc.metadata && pc.metadata.id;
            var fg = pid != null && canvas.figures[pid];
            var g = fg && fg.group;
            if (g && typeof g.dragBoundFunc === "function") {
              g.dragBoundFunc(function (pos) {
                // Konva passes ABSOLUTE (scaled) coords → scale by zoom. Bound the GRABBED
                // group so a group/piece can move freely in EVERY direction (even mostly
                // off the visible area) while keeping ~1 piece on screen so it's always
                // grabbable back. Extents are cluster-relative (measured on grab).
                var z = playZoom || 1, keep = pieceSize * 1.4 * z;
                var loX = keep - dragExt.r * z, hiX = stageW - keep + dragExt.l * z;
                var loY = keep - dragExt.b * z, hiY = stageH - keep + dragExt.t * z;
                if (loX > hiX) { loX = hiX = (loX + hiX) / 2; }
                if (loY > hiY) { loY = hiY = (loY + hiY) / 2; }
                return { x: Math.max(loX, Math.min(hiX, pos.x)), y: Math.max(loY, Math.min(hiY, pos.y)) };
              });
            }
            if (g && typeof g.on === "function") {
              // Measure the grabbed cluster so the clamp above bounds the whole group.
              g.on("dragstart", function () {
                try { dragExt = clusterExtents(pc); } catch (e) { dragExt = { l: dragMgn, r: dragMgn, t: dragMgn, b: dragMgn }; }
              });
              // RELIABLE DOUBLE-CLICK DETECTION. Konva fires dblclick/dbltap ONLY when
              // BOTH presses were perfectly stationary (any press that moved >3px is a
              // "drag" and swallows click/dblclick entirely — Konva Stage._mouseup logic).
              // Real-world double-clicks jitter, so the gesture failed often. Track presses
              // ourselves: a clean tap = press released within ~0.2·P of where it started;
              // two taps on the SAME piece within 400ms = detach (idempotent with the
              // engine's own dblclick/dbltap — detachPiece no-ops when already detached).
              function ptrOf(e) {
                try {
                  var ev = e && e.evt;
                  if (!ev) return null;
                  var t = (ev.changedTouches && ev.changedTouches[0]) || (ev.touches && ev.touches[0]) || null;
                  if (t) return { x: t.clientX, y: t.clientY };
                  if (typeof ev.clientX === "number") return { x: ev.clientX, y: ev.clientY };
                } catch (err) {}
                return null;
              }
              var tapTol = Math.max(8, Math.round(pieceSize * 0.22 * (playZoom || 1)));
              var lastTapAt = 0, pressAt = 0, pressX = 0, pressY = 0, pressDist = 0, pressFollowup = false;
              // SFX: pick on grab (press), drop on release. Mouse + touch; util debounces.
              g.on("mousedown touchstart", function (e) {
                var p = ptrOf(e) || { x: 0, y: 0 };
                pressAt = Date.now(); pressX = p.x; pressY = p.y; pressDist = 0;
                pressFollowup = !!lastTapAt && pressAt - lastTapAt <= 400; // 2nd press of a double-click
                wtpAudio.play("pick");
              });
              g.on("mouseup touchend", function (e) {
                var p = ptrOf(e);
                var d = p ? Math.abs(p.x - pressX) + Math.abs(p.y - pressY) : 0;
                if (d <= tapTol) {
                  if (pressFollowup) { detachPiece(pc); lastTapAt = 0; }
                  else lastTapAt = Date.now();
                } else {
                  lastTapAt = 0;
                }
                pressDist = d;
              });
              g.on("dragend", function () {
                wtpAudio.play("drop");
                // Jittery second press: Konva treated it as a (micro) drag, so NO
                // dblclick fired and the engine's drop() already re-connected the piece.
                // If this press quickly followed a clean tap on THIS piece and barely
                // moved, it was meant as a double-click → detach (deferred so it runs
                // AFTER the engine's dragend drop() re-lock).
                if (pressFollowup && pressDist <= tapTol) {
                  setTimeout(function () { detachPiece(pc); }, 0);
                }
              });
              // Native gesture for genuinely stationary double-click / double-tap:
              // separate this piece from its group (our tap tracker handles the rest).
              g.on("dblclick dbltap", function () { detachPiece(pc); });
            }
          });
        } catch (e) { /* engine without figure groups: keep the built-in guard */ }

        // SFX: correct-snap. The engine fires onConnect per join, and only CORRECT
        // neighbours are allowed to connect (attachConnectionRequirement), so every
        // firing is a valid snap. Registered AFTER the initial/resume autoconnect so
        // pre-existing connections don't play a burst on load.
        try {
          if (typeof canvas.onConnect === "function") {
            canvas.onConnect(function () { wtpAudio.play("correct"); });
          }
        } catch (e) { /* no onConnect API: correct-snap sound simply won't fire */ }
        wtpLooks.apply();   // M5b: equipped Snap Pack sound + Custom Cursor skin
      } catch (err) {
        host.innerHTML = '<div class="wtp-play__msg">Could not start the puzzle: ' +
          esc(err.message || err) + "</div>";
      }
    };
    img.onerror = function () {
      host.innerHTML = '<div class="wtp-play__msg">Could not load the puzzle image.</div>';
    };
    img.src = imageUrl;
  }

  // ---- timer (with pause/resume) --------------------------------------------
  function elapsedMs() {
    if (!timer.start) return 0;
    return timer.paused ? timer.elapsedMs : (Date.now() - timer.start);
  }
  function renderTimer() {
    if (timer.el) timer.el.textContent = fmtTime(elapsedMs() / 1000);
  }
  function startTimer(elm, initialMs) {
    stopTimer();
    timer.el = elm;
    timer.start = Date.now() - (initialMs || 0);
    timer.elapsedMs = initialMs || 0;
    timer.paused = false;
    timer.id = setInterval(renderTimer, 250);
    renderTimer();
  }
  function stopTimer() { if (timer.id) { clearInterval(timer.id); timer.id = null; } }
  function pauseTimer() {
    if (timer.paused || !timer.start) return;
    timer.elapsedMs = Date.now() - timer.start;
    if (timer.id) { clearInterval(timer.id); timer.id = null; }
    timer.paused = true;
  }
  function resumeTimer() {
    if (!timer.paused) return;
    timer.start = Date.now() - timer.elapsedMs;
    timer.paused = false;
    timer.id = setInterval(renderTimer, 250);
  }
  function togglePause() { if (timer.paused) resumeTimer(); else pauseTimer(); }

  // ---- completion (auto + manual submit) ------------------------------------
  // Read every piece's current [x,y] centre. ALWAYS read from puzzle.pieces in
  // piece-index order, so the "solved" snapshot and the live snapshot stay aligned
  // to the same pieces (mixing puzzle.points with puzzle.pieces scrambled the order
  // and broke the offset comparison — the cause of the false "4/9 placed" count).
  function readPoints(canvas) {
    try {
      return canvas.puzzle.pieces.map(function (pc) {
        var a = pc.centralAnchor;
        return [a.x, a.y];
      });
    } catch (e) { return null; }
  }
  // Progress = size of the largest group of pieces sharing the same offset from
  // their solved spot (i.e. correctly assembled together), and the total count.
  // Robust to gaps and board position; updates as pieces are dragged.
  function progress() {
    if (!current || !current.solved) return [0, 0];
    var pts = readPoints(current.canvas);
    if (!pts || pts.length !== current.solved.length) {
      return [0, (current.rows || current.grid) * (current.cols || current.grid)];
    }
    var n = pts.length, tol = current.tol || 30;
    var dx = [], dy = [];
    for (var i = 0; i < n; i++) {
      dx.push(pts[i][0] - current.solved[i][0]);
      dy.push(pts[i][1] - current.solved[i][1]);
    }
    var best = n ? 1 : 0;
    for (var i = 0; i < n; i++) {
      var c = 0;
      for (var j = 0; j < n; j++) {
        if (Math.abs(dx[j] - dx[i]) <= tol && Math.abs(dy[j] - dy[i]) <= tol) c++;
      }
      if (c > best) best = c;
    }
    return [best, n];
  }
  function isArrangedCorrectly() {
    var p = progress();
    return p[1] > 0 && p[0] === p[1];
  }

  function recordSolve() {
    if (!current || current.recorded) return;
    // Read the board BEFORE the snap-to-solved below. Credits (Locked Credits and
    // the M6 share credit) are for a genuinely finished board only: ✓ Submit still
    // records a time for the leaderboard exactly as before, but pressing it on an
    // unfinished board no longer earns credits. Same position-based check as the
    // auto-detect (isArrangedCorrectly), so a real solve always qualifies.
    var genuine = false, pct = 0;
    try {
      var pr = progress();
      genuine = pr[1] > 0 && pr[0] === pr[1];
      pct = pr[1] ? Math.floor(pr[0] * 100 / pr[1]) : 0;
    } catch (e) {}
    current.recorded = true;
    current.genuine = genuine;
    var pid = current.productId, grid = current.grid;
    wtpAudio.play("complete"); // once per solve — the recorded guard prevents repeats
    var secs = Math.floor(elapsedMs() / 1000);
    stopTimer();
    // Snap every piece to its exact solved position so the finished image is
    // seamless — no gaps/half-joined joints, however roughly they were dropped.
    try {
      if (current.solved && current.canvas && current.canvas.puzzle) {
        current.canvas.puzzle.relocateTo(current.solved);
        current.canvas.redraw();
      }
      if (current.reapplyPhoto) current.reapplyPhoto(); // final image must be exact
    } catch (e) { /* best effort — cosmetic only */ }
    clearProgress(pid); // solved → no saved game to resume
    hintsClear(pid, grid);
    var t = current.winEl.querySelector(".wtp-win-time");
    if (t) t.textContent = "Time: " + fmtTime(secs);
    current.winEl.style.display = "";
    submitScore(pid, grid, secs, current.boardEl);
    // Locked Credits for a FIRST completion (server enforces first-only + the cap).
    if (genuine) {
      try {
        wtpWallet.boundSession().then(function (sess) {
          if (!sess) return;   // not verified on this device - no prompt mid-celebration
          return wtpWallet.call("wallet_record_completion", { p_product: pid, p_difficulty: grid })
            .then(function () { try { wtpWallet.repaint(); } catch (e) {} });
        }).catch(function () {});
      } catch (e) {}
    }
    var winOverlay = showCompletionPopup(secs, genuine, pct);
    try { wtpChallenge.afterSolve(winOverlay, genuine, pid, grid, secs); } catch (e) {}   // M7
    if (genuine) fireWinConfetti();   // M5: the player's equipped Confetti Cannon style
    if (genuine) { try { fireWinFoil(winOverlay, fmtTime(secs)); } catch (e) {} }   // v88: Gold Foil
  }

  var usernameSkipped = false; // set if the player closes the name popup this session

  // Popup to set (or edit) a display name. Names are UNIQUE — a taken name is
  // rejected inline. Closable via ✕ (or backdrop); if closed without saving we skip
  // it for the rest of this session. opts.edit=true switches to "change name" wording.
  // Resolves with the saved name on success, or null if closed without saving.
  function showUsernameModal(prefill, opts) {
    opts = opts || {};
    var isEdit = !!opts.edit;
    var heading = isEdit ? "Edit your name" : "What's your name?";
    var sub = isEdit ? "Change how you appear on the leaderboard." : "Enter a name to appear on the leaderboard.";
    var saveLabel = isEdit ? "Save" : "Save & Continue";
    return new Promise(function (resolve) {
      var existing = document.querySelector(".wtp-modal-overlay");
      if (existing) existing.parentNode.removeChild(existing);
      var overlay = el(
        '<div class="wtp-modal-overlay">' +
          '<div class="wtp-modal" role="dialog" aria-modal="true" aria-label="Enter your name">' +
            '<button type="button" class="wtp-modal__close" aria-label="Close">×</button>' +
            '<div class="wtp-modal__emoji">🧩</div>' +
            "<h2>" + esc(heading) + "</h2>" +
            '<p class="wtp-modal__time">' + esc(sub) + "</p>" +
            '<form class="wtp-uname-form">' +
              '<input class="wtp-uname-input" type="text" maxlength="24" autocomplete="nickname" ' +
              'placeholder="Your name" value="' + esc((prefill || "").trim()) + '">' +
              '<p class="wtp-uname-err" style="display:none;color:#c0392b;margin:.4rem 0 0;font-size:.85rem"></p>' +
              '<div class="wtp-modal__actions">' +
                '<button type="submit" class="wtp-btn wtp-uname-save">' + esc(saveLabel) + "</button>" +
              "</div>" +
            "</form>" +
          "</div>" +
        "</div>",
      );
      document.body.appendChild(overlay);
      var input = overlay.querySelector(".wtp-uname-input");
      var errEl = overlay.querySelector(".wtp-uname-err");
      var saveBtn = overlay.querySelector(".wtp-uname-save");
      try { input.focus(); input.select(); } catch (e) {}
      function showErr(msg) { errEl.textContent = msg; errEl.style.display = ""; }
      function clearErr() { errEl.style.display = "none"; }
      input.addEventListener("input", clearErr);
      function skip() {
        usernameSkipped = true; // don't nag again this session
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        resolve(null);
      }
      overlay.querySelector(".wtp-modal__close").addEventListener("click", skip);
      overlay.addEventListener("click", function (e) { if (e.target === overlay) skip(); });
      overlay.querySelector(".wtp-uname-form").addEventListener("submit", async function (e) {
        e.preventDefault();
        var name = (input.value || "").trim();
        if (name.length < 2) { showErr("Please enter at least 2 characters."); input.focus(); return; }
        if (name.length > 24) name = name.slice(0, 24);
        saveBtn.disabled = true; saveBtn.textContent = "Saving…";
        try {
          var out = await saveUsername(name);
          if (out && out.ok) {
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
            resolve(out.username);
            return;
          }
          saveBtn.disabled = false; saveBtn.textContent = saveLabel;
          if (out && out.error === "taken") {
            showErr("“" + name + "” is already taken — please choose another.");
            input.focus(); input.select();
          } else if (out && out.error === "empty") {
            showErr("Please enter a valid name.");
          } else {
            showErr("Could not save — please try again.");
          }
        } catch (err) {
          saveBtn.disabled = false; saveBtn.textContent = saveLabel;
          showErr("Could not save — please try again.");
        }
      });
    });
  }

  // Open the "edit name" popup, then refresh every place the name is shown.
  async function editUsernameFlow() {
    if (!customerEmail) return;
    var newName = await showUsernameModal(playerName(), { edit: true });
    if (!newName) return; // closed without saving
    document.querySelectorAll(".wtp-playername").forEach(function (n) { n.textContent = newName; });
    if (current) loadLeaderboard(current.boardEl, current.productId, current.grid);
  }

  // Popup shown after a puzzle is completed: Play again + Buy this puzzle.
  function manualSubmit() {
    if (!current || current.recorded) return;
    // headbreaker's internal "solved" state is unreliable, so a deliberate Submit
    // records the time directly — the player verifies against the Goal thumbnail.
    recordSolve();
  }

  // ---- save & resume --------------------------------------------------------
  // Each piece's offset from its solved spot, normalized by piece size so it
  // restores correctly on any screen size.
  function currentOffsets() {
    if (!current || !current.solved) return null;
    var pts = readPoints(current.canvas);
    if (!pts || pts.length !== current.solved.length) return null;
    var ps = current.pieceSize || 1;
    return pts.map(function (p, i) {
      return [(p[0] - current.solved[i][0]) / ps, (p[1] - current.solved[i][1]) / ps];
    });
  }
  function saveProgressNow() {
    if (!current || current.recorded) return;
    var offs = currentOffsets();
    if (!offs) return;
    try {
      sb.rpc("save_progress", {
        p_email: getPlayerKey(), p_product: current.productId,
        p_difficulty: current.grid, p_offsets: offs,
        p_elapsed: Math.floor(elapsedMs() / 1000),
      });
    } catch (e) { /* best effort */ }
  }
  var saveTimer = null;
  function scheduleSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(saveProgressNow, 800);
  }
  async function loadProgress(productId) {
    if (!sb) await sbReady; // Supabase may still be arriving — don't lose a saved game
    try {
      var res = await sb.rpc("load_progress", { p_email: getPlayerKey(), p_product: productId });
      if (res.error || !res.data || !res.data.length) return null;
      return res.data[0]; // { difficulty, offsets, elapsed_seconds }
    } catch (e) { return null; }
  }
  function clearProgress(productId) {
    try { sb.rpc("clear_progress", { p_email: getPlayerKey(), p_product: productId }); } catch (e) {}
  }

  // ---- scores / leaderboard -------------------------------------------------
  var lbNote = null; // transient message shown atop the leaderboard (errors)

  // A stale Supabase Auth session in this browser (e.g. an old ADMIN login whose
  // token expired) gets attached to every request and breaks even public reads
  // with "JWT expired". Clearing it makes the client fall back to the anon key,
  // which is all the play page needs. Returns true if it cleared a stale session.
  async function healStaleAuth(err) {
    var msg = ((err && (err.message || err)) || "") + "";
    if (!/jwt|token|expired/i.test(msg)) return false;
    try { await sb.auth.signOut(); } catch (e) {}
    return true;
  }

  async function submitScore(productId, grid, secs, boardEl, retried) {
    if (!sb) await sbReady; // Supabase still arriving — wait, don't lose the score
    lbNote = null;
    try {
      var res = await sb.rpc("submit_score", {
        p_email: getPlayerKey(), p_product: productId, p_difficulty: grid, p_time: secs,
        p_name: playerName() || "Guest",
      });
      if (res && res.error) {
        if (!retried && await healStaleAuth(res.error)) {
          return submitScore(productId, grid, secs, boardEl, true);
        }
        console.error("submit_score failed:", res.error);
        lbNote = "⚠ Could not save your time: " + (res.error.message || res.error);
      }
    } catch (e) {
      console.error("submit_score threw:", e);
      lbNote = "⚠ Could not save your time (connection error): " + (e.message || e);
    }
    loadLeaderboard(boardEl, productId, grid);
  }

  async function loadLeaderboard(boardEl, productId, grid, retried) {
    if (!sb) {
      // Supabase CDN still loading — show a placeholder, fill in when it arrives.
      boardEl.innerHTML = '<div class="wtp-lb-head"><h2>🏆 Leaderboard</h2></div>' +
        '<p class="wtp-lb-empty">Loading…</p>';
      await sbReady;
    }
    var res = await sb.from("puzzle_scores")
      .select("email, time_seconds, player_name")
      .eq("shopify_product_id", productId).eq("difficulty", grid)
      .order("time_seconds", { ascending: true }).limit(200);
    if (res.error) {
      if (!retried && await healStaleAuth(res.error)) {
        return loadLeaderboard(boardEl, productId, grid, true);
      }
      boardEl.innerHTML = '<div class="wtp-lb-head"><h2>🏆 Leaderboard</h2></div>' +
        '<p class="wtp-lb-empty" style="color:#c0392b">⚠ Could not load leaderboard: ' +
        esc(res.error.message || res.error) + "</p>";
      return;
    }
    var rows = res.data || [];

    // One row per player = their BEST time (rows are already sorted fastest-first).
    var best = {}, names = {}, order = [];
    rows.forEach(function (r) {
      var key = (r.email || "").toLowerCase();
      if (!key) return;
      if (!(key in best)) {
        best[key] = r.time_seconds;
        names[key] = r.player_name || maskEmail(key);
        order.push(key);
      }
    });
    var top = order.slice(0, 10);
    var medals = ["🥇", "🥈", "🥉"];

    // Leaderboards are keyed by the difficulty BASE (2..8); with rectangular
    // boards the exact piece count varies per photo, so show the level name.
    var html =
      '<div class="wtp-lb-head"><h2>🏆 Leaderboard</h2>' +
      '<span class="wtp-lb-diff">' + esc(DIFF_NAMES[grid] || ("Level " + grid)) + "</span></div>";
    if (lbNote) {
      html += '<p class="wtp-lb-empty" style="color:#c0392b">' + esc(lbNote) + "</p>";
    }
    if (!top.length) {
      html += '<p class="wtp-lb-empty">No times yet — be the first! 🏁</p>';
    } else {
      html += '<ol class="wtp-lb">';
      top.forEach(function (key, i) {
        var me = key === getPlayerKey();
        var rank = medals[i] || (i + 1);
        html += '<li class="wtp-lb-row' + (me ? " wtp-lb--me" : "") + '">' +
          '<span class="wtp-lb-rank">' + rank + "</span>" +
          '<span class="wtp-lb-name">' + wtpAvatar.html({ me: me, edit: me, name: names[key],
            url: me ? wtpAvatar.myUrl() : null, cls: "wtp-avatar--lb", lb: i }) +
            esc(names[key]) + (me ? " · you" : "") + "</span>" +
          '<span class="wtp-lb-time">' + fmtTime(best[key]) + "</span></li>";
      });
      html += "</ol>";
      html += '<p class="wtp-lb-foot">' + order.length + " player" + (order.length === 1 ? "" : "s") + " · best times shown</p>";
    }
    boardEl.innerHTML = html;
    try { wtpAvatar.fillLeaderboard(boardEl, top); } catch (e) {}   // M8
  }

  // ---- routing --------------------------------------------------------------
  function route() {
    // M13: on a puzzle page (phones), the store's fixed free-shipping bar sits
    // over the lower edge of the board — hide it there only (CSS keys off this).
    try { document.body.classList.toggle("wtp-on-puzzle", !!(qparam("puzzle") && customerEmail)); } catch (e) {}
    try { wtpAvatar.boot(); } catch (e) {}   // M8: header avatar (logged-in only)
    try { wtpHistory.checkNew(); } catch (e) {}   // M11: "N credits added from your order"
    // The LIST is public; logging in is only required to PLAY a specific puzzle.
    var puzzle = qparam("puzzle");
    var vs = wtpChallenge.normCode(qparam("vs"));
    // M7: a challenge link lands on its invite; a logged-out friend logs in from there.
    if (vs && (!puzzle || !customerEmail)) return wtpChallenge.renderInvite(vs);
    if (puzzle) {
      if (!customerEmail) return renderLoginPrompt(puzzle);
      return renderPlay(puzzle, vs);
    }
    // Back from logging in via a challenge invite (in case the login flow dropped
    // return_to): carry on to that challenge instead of the list.
    var pendingVs = customerEmail ? wtpChallenge.peekPending() : null;
    if (pendingVs) { location.replace("/pages/play?vs=" + pendingVs); return; }
    return renderDashboard(); // public list of all digital puzzles
  }

  // ---- boot -----------------------------------------------------------------
  // Render IMMEDIATELY: the puzzle list only needs /products.json (already
  // prefetching above). Supabase (leaderboard, badges, save/resume) hooks in
  // whenever its CDN script arrives — it must never block first paint.
  var sbReadyResolve;
  var sbReady = new Promise(function (resolve) { sbReadyResolve = resolve; });

  window.addEventListener("pagehide", saveProgressNow);   // save when leaving
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") saveProgressNow();
  });
  route();

  whenReady(function () {
    if (!window.supabase) return;
    sb = window.supabase;
    // Proactively clear a stale/expired Supabase Auth session (e.g. an old admin
    // login in this browser) so requests use the anon key instead of a dead JWT.
    try {
      sb.auth.getSession().then(function (res) {
        var s = res && res.data && res.data.session;
        if (s && s.expires_at && s.expires_at * 1000 < Date.now() + 30000) {
          sb.auth.signOut().catch(function () {});
        }
      }).catch(function () { try { sb.auth.signOut(); } catch (e) {} });
    } catch (e) { /* no auth API: nothing to heal */ }
    sbReadyResolve(sb);
  });
})();
